export const languages = {
    'en': {
        name: 'English',
        nativeName: 'English',
        flag: '🇺🇸'
    },
    'zh-CN': {
        name: 'Simplified Chinese',
        nativeName: '简体中文',
        flag: '🇨🇳'
    },
    'zh-Hant': {
        name: 'Traditional Chinese',
        nativeName: '繁體中文',
        flag: '🇹🇼'
    },
    'ja': {
        name: 'Japanese',
        nativeName: '日本語',
        flag: '🇯🇵'
    }
};

export const translations = {
    'en': {
        // Login
        username: 'Username',
        password: 'Password',
        enterUsername: 'Enter your username',
        enterPassword: 'Enter your password',
        rememberMe: 'Remember me for 30 days',
        logIn: 'Log In',
        loginFailed: 'Login failed.',
        connectionError: 'Could not connect to the server.',
        signingIn: 'Signing in...',
        or: 'or',
        googleLoginFailed: 'Google login failed.',
        loginTitle: 'Daily Learning Hub',
        loginSubtitle: 'Start your learning journey',
        secureConnection: 'Your connection is secure and encrypted',
        
        // Registration
        createAccount: 'Create Account',
        registerDescription: 'Sign up to start your learning journey',
        email: 'Email',
        confirmPassword: 'Confirm Password',
        enterEmail: 'Enter your email',
        confirmPasswordPlaceholder: 'Confirm your password',
        passwordRequirement: 'At least 6 characters',
        passwordsDoNotMatch: 'Passwords do not match',
        passwordTooShort: 'Password must be at least 6 characters long',
        invalidEmail: 'Please enter a valid email address',
        emailAlreadyRegistered: 'This email is already registered',
        emailAlreadyRegisteredVerified: 'Email already registered and verified. Please log in.',
        accountExistsUnverified: 'An account with this email already exists. We\'ve sent a verification email. Please check your inbox.',
        registrationFailed: 'Registration failed',
        notWhitelisted: 'Sorry, only invited test users can create an account at this time.',
        creatingAccount: 'Creating Account...',
        alreadyHaveAccount: 'Already have an account?',
        signIn: 'Sign in',
        noAccount: "Don't have an account?",
        allFieldsRequired: 'All fields are required',
        
        // Password Reset
        forgotPassword: 'Forgot password?',
        resetPassword: 'Reset Password',
        resetPasswordDescription: "Enter your email address and we'll send you a link to reset your password.",
        sendResetLink: 'Send Reset Link',
        sending: 'Sending...',
        checkYourEmail: 'Check Your Email',
        resetLinkSent: 'If an account exists with this email, a password reset link has been sent.',
        backToLogin: 'Back to Login',
        setNewPassword: 'Set New Password',
        enterNewPassword: 'Enter your new password below',
        newPassword: 'New Password',
        confirmNewPassword: 'Confirm New Password',
        resettingPassword: 'Resetting Password...',
        passwordResetSuccess: 'Password Reset Successful!',
        passwordResetSuccessMessage: 'Your password has been reset successfully. You can now log in with your new password.',
        redirectingToLogin: 'Redirecting to login...',
        resetPasswordFailed: 'Failed to reset password',
        
        // Email Verification
        verificationEmailSent: 'Check Your Email!',
        verificationEmailDescription: "We've sent a verification email to:",
        nextSteps: 'Next Steps:',
        checkInbox: 'Check your email inbox',
        clickVerifyLink: 'Click the verification link in the email',
        loginAfterVerification: 'Log in with your credentials',
        cantFindEmail: "Can't find the email?",
        checkSpamFolder: 'Check your spam folder or request a new verification email from the login page.',
        goToLogin: 'Go to Login',
        emailNotVerified: 'Email Not Verified',
        verifyEmailToLogin: 'Please verify your email address to log in. Check your inbox for the verification email.',
        resendVerificationEmail: 'Resend Verification Email',
        sendingEmail: 'Sending...',
        emailSent: 'Email Sent!',
        checkYourInbox: 'Verification email sent! Check your inbox.',
        
        // User onboarding
        welcomeToApp: 'Welcome to {appTitle}',
        selectPreferredLanguage: 'Please select your preferred language to continue.',
        uiLanguage: 'UI Language',
        youCanChangeLater: 'You can change this later in settings.',
        next: 'Next',
        back: 'Back',
        previous: 'Previous',
        step: 'Step',
        quickStartGuide: 'Quick Start Guide',
        userProfileSetup: 'Tell Us About Yourself',
        profileSetup: 'This optional information helps us tailor the learning experience to you.',
        
        // Guided Tutorial
        tutorialWelcomeTitle: "Let's Get Started!",
        tutorialButtonSpotlightTitle: "Interactive Tutorial",
        tutorialButtonSpotlightMessage: "Want a comprehensive walkthrough? Click the Tutorial button to explore all features with our interactive guide.",
        tutorialStepIndicator: 'Step 1 of 1',
        tutorialWelcomeMessage: 'Ready to start your learning journey? Click the blue "+" button above to add your first topic and begin learning something new today!',
        tutorialActionHint: 'Click here to add a topic',
        tutorialActionDescription: "Choose any subject you'd like to learn",
        tutorialSkip: 'Skip tutorial',
        tutorialGetStarted: 'Got it!',
        
        // Comprehensive Tutorial Mode
        tutorial: 'Tutorial',
        startTutorial: 'Start Tutorial',
        tutorialNext: 'Next',
        tutorialPrevious: 'Previous',
        tutorialComplete: 'Complete',
        tutorialWelcomeContent: 'Welcome to your personalized learning companion! This tutorial will guide you through all the features to help you along the learning journey.',
        tutorialWelcomeTip: '• This tutorial will take about 1-2 minutes to complete.',
        tutorialNavigationTip: '• Use ← and → arrow keys on your keyboard for easier navigation.',

        tutorialAddTopicContent: "Click the '+' button above to add your first learning topic. You can add any subject you are interested in!",
        tutorialTip: 'Tip:',
        tutorialAddTopicTip: 'Use general topics like Machine Learning, Cooking, Animals, or Gardening. You can also add learning goals and details when creating a topic to get more personalized content.',
        tutorialTopicCardContent: 'Topic cards organize your learning subjects. Notice the status indicators:',
        tutorialUnreadIndicator: 'Blue dot - Unread content',
        tutorialUnreadDesc: 'Shows when you have new tips to read',
        tutorialReviewIndicator: 'Orange dot - Review due',
        tutorialReviewDesc: 'Time to review concepts for better retention',
        tutorialFavoriteIndicator: 'Heart - Favorite topic',
        tutorialFavoriteDesc: 'Your starred topics for quick access',
        tutorialAddTipContent: 'Click the "+" button in the tips panel to generate a new AI-powered learning concept. This may take up to ~1 minute as the AI creates personalized content for you.',
        tutorialManualTipBenefit: 'Pro Tip:',
        tutorialManualTipDesc: "Don't want to wait? You can schedule concept generation in the topic settings, so concepts are prepared for you ahead of time automatically!",
        tutorialTipCardContent: 'These are your learning concepts. Click any tip to view its full content, start an AI conversation, or mark it for review.',
        tutorialInteractionHint: 'Quick Actions:',
        tutorialInteractionDesc: 'Right-click on any tip for more options like marking as favorite, editing, or deleting.',
        tutorialTopicInteractionDesc: 'Right-click on any topic for more options like marking as favorite, editing, or deleting.',
        tutorialTopicGroupingContent: 'Keep your topics organized with custom groups! Group related topics together for better organization and workflow.',
        tutorialCreateGroup: 'Create Topic Groups',
        tutorialCreateGroupDesc: 'Click "Add Group" to create custom categories like "Work Projects", "Personal Learning", or "Hobbies"',
        tutorialDragTopics: 'Drag Topics into Groups',
        tutorialDragTopicsDesc: 'Simply drag and drop topic cards into any group to organize them',
        tutorialReorderWithinGroups: 'Reorder Topics Within Groups',
        tutorialReorderWithinGroupsDesc: 'Drag topics up and down within a group to arrange them in your preferred order',
        tutorialReorderGroups: 'Reorder Entire Groups',
        tutorialReorderGroupsDesc: 'Drag the group header to reorder entire groups and prioritize your learning areas',
        tutorialGroupingTip: 'Organization Tip:',
        tutorialGroupingTipDesc: 'Groups help you focus on specific areas during your learning sessions and keep related topics together for better context.',
        tutorialGroupingNote: 'Important Note:',
        tutorialGroupingNoteDesc: "Groups are purely for visual organization and won't affect the AI-generated content in any way.",
        tutorialAITutorContent: 'Your AI tutor is always ready to help! Ask questions, get explanations, and dive deeper into any topic.',
        tutorialReviewContent: 'Regular reviews using spaced repetition help you retain knowledge long-term. Answer questions to reinforce your learning.',
        tutorialCongrats: 'Congratulations!',
        tutorialCompleteContent: "You've completed the tutorial! You're now ready to start your learning journey.",
        tutorialNextSteps: 'Next Steps:',
        tutorialStep1: 'Add your first learning topic',
        addYourFirstTopic: 'Add Your First Topic',
        tutorialStartLearningHint: 'Start by adding a topic you\'re passionate about learning!',
        tutorialStep2: 'Review daily tips and concepts',
        tutorialStep3: 'Chat with your AI tutor when you have questions',
        tutorialStep4: 'Complete reviews to strengthen retention',
        tutorialConcepts: 'concepts learned',
        tutorialExplanation: 'Explanation:',
        
        // Tutorial Dummy Content
        tutorialDummyTopicTitle: 'Learn JavaScript',
        tutorialDummyTopicDesc: 'Master the fundamentals of JavaScript programming',
        tutorialDummyNextReview: 'Tomorrow at 9:00 AM',
        tutorialDummyTipTitle: 'Understanding Variables',
        tutorialDummyTipContent: 'Variables are containers for storing data values. In JavaScript, you can declare variables using let, const, or var.',

        tutorialDummyReviewQuestion: 'What is the difference between let and const?',
        tutorialDummyOption1: 'let can be reassigned, const cannot',
        tutorialDummyOption2: 'const can be reassigned, let cannot',
        tutorialDummyOption3: 'Both can be reassigned',
        tutorialDummyOption4: 'Neither can be reassigned',
        tutorialDummyExplanation: 'Variables declared with let can be reassigned, while const creates a constant reference.',
        
        // Tutorial Knowledge Level Modal
        tutorialNewConceptModalIntro: 'When you select an unread learning concept, you\'ll see this knowledge level selection screen. This is a crucial step in personalizing your learning experience!',
        tutorialTooEasy: 'Too Easy',
        tutorialTooEasyDesc: 'This concept feels basic and doesn\'t add much to your current knowledge',
        tutorialAlreadyKnown: 'Already Known',
        tutorialAlreadyKnownDesc: 'You understand this concept well and feel confident about it',
        tutorialPartiallyKnow: 'Partially Know',
        tutorialPartiallyKnowDesc: 'You have some understanding but need to strengthen your knowledge',
        tutorialNewToMe: 'New to Me',
        tutorialNewToMeDesc: 'This is completely new information you\'re learning for the first time',
        tutorialTooHard: 'Too Hard',
        tutorialTooHardDesc: 'This concept is too challenging for you to understand at the moment',
        tutorialKnowledgeTip: 'Why This Matters:',
        tutorialKnowledgeTipDesc: 'Your selection helps the AI tutor tailor explanations and recommend content to your knowledge level.',
        tutorialKnowledgeLevelPanelIntro: 'You can always change your knowledge level later.',
        tutorialProgressTracking: 'Track Your Progress',
        tutorialProgressTrackingDesc: 'Update from "New to Me" to "Partially Know" as you learn, helping the AI adapt to your progress',
        tutorialFlexibleLearning: 'Flexible Learning',
        tutorialFlexibleLearningDesc: 'Made a mistake? Found it easier than expected? Just click to update your knowledge level anytime!',
        tutorialTipContentIntro: 'This is where your learning content appears. Each concept contains bite-sized, AI-generated knowledge designed to help you master your chosen topics. All content is search-grounded for accuracy and relevance.',
        tutorialQuickActionsIntro: 'These quick action buttons help you send conversations with pre-written prompts, saving you time from typing common questions.',
        tutorialViewModesIntro: 'Switch between different view modes to customize your learning experience:',
        tutorialTipOnlyMode: 'Concept Only',
        tutorialTipOnlyModeDesc: 'Focus solely on reading and learning the concept content without any distractions. Perfect for initial reading and understanding.',
        tutorialFullViewMode: 'Full View',
        tutorialFullViewModeDesc: 'See both the tip content and AI conversation side by side. Ideal for active learning where you read and ask questions simultaneously.',
        tutorialChatOnlyMode: 'Chat Only',
        tutorialChatOnlyModeDesc: 'Maximize the conversation space for in-depth discussions with the AI tutor. Great for extended Q&A sessions and detailed explanations.',
        tutorialViewModesProTip: 'Pro Tip:',
        tutorialViewModesProTipDesc: 'The view automatically switches to Full View when you start a conversation. You can always change it to match your learning preference!',
        tutorialTipActionsIntro: 'Manage your learning concepts with these powerful actions:',
        tutorialHideAction: 'Hide',
        tutorialHideActionDesc: 'Temporarily hide concepts you\'ve mastered or want to skip for now',
        tutorialFavoriteAction: 'Favorite',
        tutorialFavoriteActionDesc: 'Mark important concepts for quick access and prioritized review',
        tutorialScheduleReview: 'Schedule Review',
        tutorialScheduleReviewDesc: 'Set reminders to review concepts using spaced repetition for better retention',
        tutorialProTip: 'Pro Tip:',
        tutorialTipActionsProTip: 'Use these actions to customize your learning flow. Hide concepts you know well, favorite challenging ones, and schedule reviews for optimal retention.',
        tutorialReviewTrackingIntro: 'Based on the Ebbinghaus curve, review questions are generated to help you retain knowledge over time:',
        tutorialDateLearned: 'Date Learned',
        tutorialDateLearnedDesc: 'This is the date you first chose a knowledge level for a concept.',
        tutorialReviewButton: 'Review Questions Button',
        tutorialReviewButtonDesc: 'Click to test your knowledge with multiple choice questions. Glows orange when there is a new review question available.',
        tutorialDaysTillReview: 'Days Till Next Review',
        tutorialDaysTillReviewDesc: 'Shows countdown to your next review.',
        tutorialReviewModalIntro: 'Test your knowledge with review questions! This powerful learning tool helps reinforce concepts through active recall:',
        tutorialAnswerQuestions: 'Answer Review Questions',
        tutorialAnswerQuestionsDesc: 'Multiple choice questions test your understanding of key concepts. Select your answer and confidence level.',
        tutorialDeepDiveChat: 'Deep Dive Chat',
        tutorialDeepDiveChatDesc: 'After answering, start a conversation with the AI tutor to explore the topic deeper and clarify any confusion.',
        tutorialActiveRecall: 'Active Recall Tip:',
        tutorialActiveRecallDesc: 'Try to recall the answer before looking at the options. This strengthens memory pathways and improves long-term retention!',
        tutorialSpacedRepetition: 'Best Practice:',
        tutorialSpacedRepetitionDesc: 'For the most effective learning outcome, review a concept as soon as it becomes due for review. This timing optimizes memory retention and long-term recall.',
        tutorialScrollTip: 'Tip:',
        tutorialScrollTipDesc: 'The content area is scrollable. You can scroll down to read the entire concept if it contains more information.',
        tutorialDummyTopic1Name: 'Health and Wellness',
        tutorialDummyTopic1Desc: 'Essential knowledge for a healthier, more energetic life',
        tutorialDummyTopic2Name: 'Cooking',
        tutorialDummyTopic2Desc: 'Master basic cooking techniques and recipes',
        tutorialDummyTopic3Name: 'Gardening',
        tutorialDummyTopic3Desc: 'Learn how to grow your own plants and vegetables',
        tutorialDummyTip1Title: 'The Power of Hydration',
        tutorialDummyTip1Content: 'Proper hydration is fundamental to every system in your body. While the "8 glasses a day" rule is popular, your actual needs depend on activity level, climate, and body size.\n\nKey facts:\n• Your body is 60% water\n• Even mild dehydration (2%) affects focus and energy\n• Best indicator: pale yellow urine means good hydration\n• Drink before you feel thirsty - thirst means you\'re already mildly dehydrated\n\nOptimal timing: Start with water upon waking, drink throughout the day, and reduce intake 2 hours before bed.',
        tutorialDummyTip2Title: 'Understanding Your Energy Levels',
        tutorialDummyTip2Content: 'Your energy naturally fluctuates throughout the day following your circadian rhythm. Understanding these patterns helps you optimize productivity and well-being.\n\nNatural energy peaks:\n• Morning (9-11 AM): Best for complex tasks\n• Early afternoon (1-3 PM): Natural dip, ideal for routine work\n• Late afternoon (3-6 PM): Second peak, good for meetings\n\nEnergy boosters:\n• Consistent sleep schedule (7-9 hours)\n• Regular meals with balanced nutrients\n• Short walks every 90 minutes\n• Staying hydrated throughout the day',
        tutorialDummyTip3Title: 'Knife Skills: The Foundation',
        tutorialDummyTip3Content: 'Proper knife skills are essential for efficient and safe cooking. Master these basic cuts:\n\n• Julienne: thin matchstick cuts\n• Dice: uniform cube shapes\n• Chiffonade: rolling and slicing herbs\n• Brunoise: very fine dice\n\nAlways keep your knife sharp and use a stable cutting board.',
        
        // Tutorial Conversations
        tutorialDummyBotInit1: 'Great to see you\'re exploring hydration! It\'s truly fundamental to how our bodies function.\n\nWhat aspect would you like to dive deeper into - daily intake guidelines, timing, or recognizing dehydration signs?',
        tutorialDummyQuestion1: 'How can I tell if I\'m actually dehydrated vs just thirsty?',
        tutorialDummyAnswer1: 'Good distinction! Thirst is actually a late indicator - by the time you feel thirsty, you\'re already mildly dehydrated.\n\nBetter indicators include:\n• **Urine color**: Pale yellow means well-hydrated; dark amber indicates dehydration\n• **Energy dips**: Unexplained fatigue, especially mid-afternoon\n• **Skin elasticity**: Pinch the skin on your hand - it should snap back immediately\n\nThe key is preventing dehydration by drinking water consistently throughout the day, not waiting for thirst.',
        
        tutorialDummyBotInit2: 'Nice work diving into energy levels! Understanding your natural rhythms is key to productivity.\n\nWhat would you like to explore - managing energy dips, optimizing peak hours, or improving overall stamina?',
        tutorialDummyQuestion2: 'Why do I always crash around 2-3 PM even after a good night\'s sleep?',
        tutorialDummyAnswer2: 'That afternoon dip is actually natural - it\'s part of your circadian rhythm, not necessarily a sign something\'s wrong.\n\nYour body temperature naturally drops between 1-3 PM, triggering sleepiness. To minimize the crash:\n• **Eat a lighter lunch** - heavy meals worsen the slump\n• **Take a brief walk** - even 5-10 minutes helps\n• **Stay hydrated** - dehydration amplifies fatigue\n• **Consider a 20-minute power nap** if possible\n\nWork with this natural dip by scheduling routine tasks during this time and saving complex work for your morning or late afternoon peaks.',
        
        // Tutorial Review Questions
        tutorialDummyReviewQ2: 'When is the best time to drink a large glass of water for metabolism?',
        tutorialDummyReviewChoice2A: 'Right before bed',
        tutorialDummyReviewChoice2B: 'During meals',
        tutorialDummyReviewChoice2C: 'First thing in the morning',
        tutorialDummyReviewChoice2D: 'Only when exercising',
        tutorialDummyReviewExp2A: 'Drinking large amounts before bed can disrupt sleep with bathroom trips.',
        tutorialDummyReviewExp2B: 'Large amounts during meals can dilute digestive acids. Small sips are better.',
        tutorialDummyReviewExp2C: 'Correct! Morning hydration jumpstarts metabolism and helps flush overnight toxins.',
        tutorialDummyReviewExp2D: 'While important during exercise, this isn\'t the optimal time for metabolism boost.',
        tutorialChatStarter2A: 'Bedtime hydration is tricky! Let me explain the balance between hydration and sleep...',
        tutorialChatStarter2B: 'Mealtime hydration is important but timing matters. Let me explain why...',
        tutorialChatStarter2C: 'Perfect! Want to learn about the morning hydration routine that maximizes benefits?',
        tutorialChatStarter2D: 'Exercise hydration is crucial but different. Let me explain the timing...',
        
        // Tutorial Scheduled Generation Modal
        tutorialScheduledGenerationIntro: 'Automate your learning with scheduled concept generation! Set it and forget it - new concepts will be generated automatically at your preferred time.',
        tutorialSetSchedule: 'Set Your Schedule',
        tutorialSetScheduleDesc: 'Choose when and how often new concepts should be generated - daily, weekly, or monthly at your preferred time.',
        tutorialTimezoneAware: 'Timezone Aware',
        tutorialTimezoneAwareDesc: 'The system automatically adjusts to your timezone, ensuring concepts are ready when you expect them.',
        tutorialSmartSkipping: 'Smart Skipping',
        tutorialSmartSkippingDesc: 'Enable "Skip if unread concepts exist" to prevent overwhelming yourself with too many unread concepts.',
        
        // Tutorial Filter Functions
        tutorialFilterFunctionsIntro: 'Stay organized with smart filtering! Quickly find the topics and concepts that matter most to you with powerful filter options.',
        tutorialFavoriteFilter: 'Favorites Filter',
        tutorialFavoriteFilterDesc: 'Click the heart icon to show only your favorite topics or concepts. Perfect for focusing on content you care about most.',
        tutorialReviewDueFilter: 'Review Due Filter',
        tutorialReviewDueFilterDesc: 'Click the clock icon to show only items due for review. Stay on top of your spaced repetition schedule.',
        tutorialAdvancedFilter: 'Advanced Filters',
        tutorialAdvancedFilterDesc: 'Click the funnel icon to access advanced filtering options like status (unread, marked as known, etc.) and scheduled generation settings.',
        tutorialFilterProTip: 'Pro Tip',
        tutorialCombineFiltersTip: 'You can combine multiple filters to create powerful views. For example, show only favorite topics that are due for review!',
        
        // Tutorial AI Learning Summary Modal
        tutorialAILearningSummaryIntro: 'Get personalized insights into your learning journey! The AI analyzes your interactions to understand your knowledge profile, learning patterns, and optimization opportunities.',
        tutorialKnowledgeProfile: 'Knowledge Profile',
        tutorialKnowledgeProfileDesc: 'Understand your strengths and knowledge gaps based on your engagement with concepts and conversations.',
        tutorialLearningPatterns: 'Learning Patterns',
        tutorialLearningPatternsDesc: 'Discover how you learn best - whether through examples, theory, or practice-based approaches.',
        tutorialOptimizationInsights: 'Optimization Insights',
        tutorialOptimizationInsightsDesc: 'Get actionable recommendations to enhance your learning effectiveness and retention.',
        tutorialRequirementNote: 'Note:',
        tutorialRequirementNoteDesc: 'You need to read at least 5 concepts before generating your first AI learning summary. This ensures enough data for meaningful insights.',
        tutorialRightClickTopicTip: 'You can access these settings anytime by right-clicking on any topic card and selecting "Edit".',
        
        // Tutorial Group Names
        tutorialDummyGroup1Name: 'Health & Lifestyle',
        tutorialDummyGroup2Name: 'Hobbies',
        
        // Header
        appTitle: 'Daily Learning Hub',
        welcome: 'Welcome',
        logout: 'Logout',
        
        // Profile
        name: 'Name',
        enterYourName: 'Enter your name',
        nameHelp: 'Your name will be used for personalized interactions',
        
        // Topics
        topics: 'Topics',
        addTopicTooltip: 'Add a new topic to learn',
        filterFavoriteTopicsTooltip: 'Show only favorite topics',
        filterReviewDueTopicsTooltip: 'Show topics with concepts due for review',
        filterTopicsTooltip: 'Filter topics',
        noTopicsToFilter: 'Create a topic first to use filters',
        editTopic: 'Edit Topic',
        topicName: 'Topic Name',
        startNewLearningTopic: 'Start a New Learning Topic',
        learningContext: 'Learning Context & Goals (Optional)',
        learningContextHelp: 'Describe your current level, what you\'ve already learned, specific goals, and any context that would help generate more relevant concepts.',
        learningContextExample: 'Example: "I\'m a beginner in Japanese and want to learn casual slang for my trip to Tokyo. I\'ve already learned basic greetings and can read hiragana. I want to focus on phrases used by young people in everyday conversations. I\'m particularly interested in food-related vocabulary and asking for directions."',
        purpose: 'Purpose of Learning',
        hobby: 'For Interest / Hobby',
        professional: 'For Technical / Professional Growth',
        topicLanguage: 'Topic Language',
        saveChanges: 'Save Changes',
        editTopicChangesNote: 'Note: Changes to topic settings will only affect future generated tips. Existing tips will remain unchanged.',
        learningDetails: 'Learning Details',
        knowledgeContext: 'Knowledge & Context',
        learningSummary: 'AI Learning Summary',
        summaryTooltip: 'AI-generated analysis of your learning patterns and interests based on your activity',
        generateSummary: 'Generate Summary',
        generating: 'Generating...',
        summaryPlaceholder: 'Click "Generate Summary" to analyze your learning patterns and interests...',
        includeSummaryInPrompt: 'Include in AI context for personalized content',
        summaryHelp: 'This AI-generated summary analyzes your learning patterns to personalize future content.',
        insufficientReadTips: 'You need at least 5 read tips to generate an AI learning summary. You currently have {count} read tip(s). Continue reading and marking tips to unlock this feature.',
        noTipsForSummary: 'No learning content available yet. Please add some tips first to generate a summary.',
        summaryCachedMessage: 'No changes detected in your learning activity since the last summary. Try again after reading more tips or engaging in conversations.',
        summaryNewMessage: 'New learning summary generated based on your latest activity and interactions.',
        summaryGenerationError: 'Failed to generate learning summary. Please try again.',
        knowledgeProfile: 'Knowledge Profile',
        learningPatterns: 'Learning Patterns',
        optimizationInsights: 'Optimization Insights',
        edit: 'Edit',
        view: 'View',
        learningContextGoals: 'Learning Context & Goals (Optional)',
        learningContextPlaceholder: 'Describe your specific learning goals, interests, or what you want to focus on...',
        cancel: 'Cancel',
        startLearning: 'Start Learning',
        settings: 'Settings',
        feedbackBugReport: 'Feedback / Bug Report',
        currentProfile: 'Current Profile',
        age: 'Age',
        education: 'Education',
        notProvided: 'Not provided',
        yearsOld: 'years old',
        saving: 'Saving...',
        profile: 'Profile',
        aiModel: 'AI Model',
        activeSessions: 'Active Sessions',
        usage: 'Usage',
        thankYouTestUser: 'Thank You for Being a Test User!',
        thankYouMessage: 'We appreciate your help in testing {appTitle}. Your feedback is invaluable.',
        testUserBenefits: 'Your Test User Benefits',
        conversations: 'Conversations',
        unlimited: 'Unlimited',
        conceptGeneration: 'Concept Generation',
        aiLearningSummary: 'AI Learning Summary',
        reviewScheduling: 'Review Scheduling',
        loadingSessions: 'Loading sessions...',
        activeSessionsInfo: 'Manage Your Active Sessions',
        activeSessionsDescription: 'View and manage all devices where you are currently logged in. You can log out from specific devices or all devices at once.',
        noActiveSessions: 'No active sessions found',
        unknownDevice: 'Unknown Device',
        currentSession: 'Current Session',
        ipAddress: 'IP Address',
        unknown: 'Unknown',
        lastActive: 'Last Active',
        logoutThisDevice: 'Log out from this device',
        logoutAllDevices: 'Log Out from All Devices',
        confirmLogoutSession: 'Are you sure you want to log out from this device?',
        confirmLogoutAllSessions: 'Are you sure you want to log out from all devices? You will need to log in again.',
        close: 'Close',
        
        // AI Model Settings
        conversationModel: 'Conversation Model',
        selectConversationModel: 'Choose AI model for conversations',
        selectAIProvider: 'Select AI Provider',
        modelQualityVsSpeed: 'Quality vs Speed Trade-off',
        modelPro: 'Premium',
        modelProDescription: 'Best quality responses with deep reasoning',
        modelFlash: 'Balanced',
        modelFlashDescription: 'Good quality with faster responses',
        modelLite: 'Speed',
        modelLiteDescription: 'Quick responses for simple queries',
        recommended: 'Recommended',
        bestQuality: 'Best Quality',
        balanced: 'Balanced',
        fastest: 'Fastest',
        quality: 'Quality',
        speed: 'Speed',
        
        // Slow Chat Notification
        slowChatNotification: 'Waiting too long for the response? Try switching to a faster model for quicker replies',
        changeModel: 'Change Model',
        dontShowAgain: "Don't show again",
        
        sessionInvalidatedByAnotherLogin: 'You have been logged out because you logged in from another device.',
        favoriteTopic: 'Favorite Topic',
        unfavoriteTopic: 'Unfavorite Topic',
        favoritedTopic: 'Favorited Topic',
        showFavoriteTopicsOnly: 'Show Favorite Topics Only',
        showReviewDueTopicsOnly: 'Show Review Due Topics Only',
        showScheduledTopicsOnly: 'Show Auto-Generated Topics Only',
        showUnscheduledTopicsOnly: 'Show Manual Topics Only',
        showAllTopics: 'Show All Topics',
        daily: 'Daily',
        weekly: 'Weekly',
        monthly: 'Monthly',
        everyNDays: 'Every {count} days',
        everyNWeeks: 'Every {count} weeks',
        everyNMonths: 'Every {count} months',
        topicSingular: 'topic',
        topicPlural: 'topics',
        ungrouped: 'Ungrouped',
        createGroup: 'Create Group',
        groupName: 'Group name',
        rename: 'Rename',
        deleteGroupTitle: 'Delete Group',
        deleteGroupMessage: 'Are you sure you want to delete this group? Topics will be moved to Ungrouped.',
        
        // Add Topic Modal specific translations
        learningGoalQuestion: 'What\'s your goal for learning this?',
        contentLanguageQuestion: 'In which language should the content be explained?',
        rateYourKnowledge: 'Rate Your Current Knowledge',
        example: 'Example',
        
        // Scheduling translations
        enableAutomaticTipGeneration: 'Enable automatic tip generation',
        generateTipsAt: 'Generate tips at:',
        generateNewTipsEvery: 'Generate new tips every:',
        hour: 'hour',
        hours: 'hours',
        days: 'days',
        weeks: 'weeks',
        months: 'months',
        timezone: 'Timezone',
        schedulingNote: 'Your selected timezone will be used for scheduling',
        scheduledSkipIfUnread: 'If there is any unread tip in this topic, do NOT request a new tip for this topic',
        
        // Placeholders
        topicNamePlaceholder: 'e.g., Quantum Computing',
        selectKnowledgeLevel: 'Move the slider to rate your current knowledge level',
        selectLearningGoal: 'Please select your learning goal',
        completeAllFieldsToStart: 'Please complete the topic name, learning goal, and knowledge level to start learning',
        
        // Topic Examples
        topicExamples: [
            {
                name: 'Quantum Computing',
                description: 'I\'m a physics student with a strong background in linear algebra and classical mechanics. I want to understand quantum algorithms for my research project. I\'ve already studied quantum mechanics basics and can work with complex numbers. I\'m particularly interested in quantum cryptography and quantum machine learning applications.'
            },
            {
                name: 'Japanese Conversation',
                description: 'I\'m a beginner in Japanese and want to learn casual slang for my trip to Tokyo. I\'ve already learned basic greetings and can read hiragana. I want to focus on phrases used by young people in everyday conversations. I\'m particularly interested in food-related vocabulary and asking for directions.'
            },
            {
                name: 'Machine Learning',
                description: 'I\'m a software developer with experience in Python and statistics. I want to build recommendation systems for my company\'s e-commerce platform. I\'ve already taken an online course on supervised learning and understand basic algorithms. I\'m particularly interested in collaborative filtering and deep learning approaches.'
            },
            {
                name: 'Spanish for Travel',
                description: 'I\'m planning a backpacking trip through South America and need practical Spanish skills. I know some basic vocabulary from high school but haven\'t practiced in years. I want to focus on conversational skills for ordering food, booking accommodations, and making friends. I\'m particularly interested in regional variations and cultural context.'
            },
            {
                name: 'Blockchain Development',
                description: 'I\'m a web developer with experience in JavaScript and smart contracts. I want to build decentralized applications for a startup project. I\'ve already learned Solidity basics and understand how Ethereum works. I\'m particularly interested in DeFi protocols and NFT marketplaces.'
            },
            {
                name: 'Cooking Techniques',
                description: 'I\'m a home cook who wants to improve my culinary skills for dinner parties. I can follow recipes but want to understand the science behind cooking methods. I\'ve already mastered basic knife skills and can make simple sauces. I\'m particularly interested in French techniques and molecular gastronomy.'
            },
            {
                name: 'Data Science',
                description: 'I\'m a business analyst who wants to transition into data science. I have strong Excel skills and basic SQL knowledge. I want to learn Python for data analysis and visualization. I\'m particularly interested in predictive modeling and business intelligence applications.'
            },
            {
                name: 'Guitar Theory',
                description: 'I\'m an intermediate guitarist who can play chords and simple songs. I want to understand music theory to write my own songs and improvise. I\'ve already learned basic scales and can read tablature. I\'m particularly interested in jazz theory and fingerpicking techniques.'
            },
            {
                name: 'Photography',
                description: 'I\'m a hobbyist photographer with a DSLR camera. I want to improve my composition and lighting skills for landscape photography. I understand basic camera settings but want to master manual mode. I\'m particularly interested in long exposure techniques and post-processing.'
            },
            {
                name: 'Web Development',
                description: 'I\'m a beginner programmer who wants to build modern web applications. I have basic HTML and CSS knowledge from online tutorials. I want to learn JavaScript frameworks and backend development. I\'m particularly interested in React and Node.js for full-stack development.'
            },
            {
                name: 'Financial Planning',
                description: 'I\'m a recent graduate who wants to learn personal finance and investment strategies. I have a basic understanding of budgeting but want to learn about stocks, bonds, and retirement planning. I\'m particularly interested in index funds and tax-efficient investing.'
            },
            {
                name: 'Public Speaking',
                description: 'I\'m a professional who needs to improve presentation skills for work meetings and conferences. I can speak in small groups but get nervous with larger audiences. I want to learn techniques for engaging delivery and handling questions. I\'m particularly interested in storytelling and persuasive speaking.'
            },
            {
                name: 'Yoga Practice',
                description: 'I\'m a beginner who wants to develop a consistent yoga practice for stress relief and flexibility. I\'ve tried a few classes but want to understand proper alignment and breathing techniques. I\'m particularly interested in vinyasa flow and meditation practices.'
            },
            {
                name: 'Game Development',
                description: 'I\'m a programmer who wants to create 2D games as a hobby project. I have experience in C++ and basic graphics programming. I want to learn game engines and design principles. I\'m particularly interested in Unity and mobile game development.'
            },
            {
                name: 'Creative Writing',
                description: 'I\'m an aspiring writer who wants to improve my storytelling skills for short fiction. I enjoy reading but struggle with character development and plot structure. I want to learn techniques for creating compelling narratives. I\'m particularly interested in science fiction and fantasy genres.'
            },
            {
                name: 'Digital Marketing',
                description: 'I\'m a small business owner who wants to improve online presence and customer acquisition. I have basic social media knowledge but want to learn SEO and paid advertising. I\'m particularly interested in content marketing and email campaigns.'
            },
            {
                name: 'Meditation',
                description: 'I\'m a busy professional who wants to develop a meditation practice for mental clarity and stress management. I\'ve tried mindfulness apps but want to understand different techniques and traditions. I\'m particularly interested in transcendental meditation and guided practices.'
            },
            {
                name: 'Home Brewing',
                description: 'I\'m a beer enthusiast who wants to start brewing my own craft beer at home. I understand the basic brewing process but want to learn about different styles and techniques. I\'m particularly interested in IPAs and sour beers.'
            },
            {
                name: 'Astronomy',
                description: 'I\'m a science enthusiast who wants to learn about the universe and celestial objects. I have a basic telescope and can identify major constellations. I want to understand astrophysics concepts and observational techniques. I\'m particularly interested in exoplanets and deep space objects.'
            },
            {
                name: 'Chess Strategy',
                description: 'I\'m an intermediate chess player who wants to improve my strategic thinking and endgame skills. I know basic openings and can calculate simple tactics. I want to learn positional play and advanced concepts. I\'m particularly interested in middle game planning and pawn structures.'
            }
        ],
        
        // Concepts
        concepts: 'Concepts',
        addConceptTooltip: 'Generate a new concept for this topic',
        filterFavoriteConceptsTooltip: 'Show only favorite concepts',
        filterReviewDueConceptsTooltip: 'Show concepts due for review',
        filterConceptsTooltip: 'Filter concepts',
        selectTopicToFilterConcepts: 'Select a topic first to filter concepts',
        filterByStatus: 'Filter by Status',
        showFavoritesOnly: 'Show Favorites Only',
        showAllConcepts: 'Show All Concepts',
        noConceptsMatchFilter: 'No concepts match your current filter settings.',
        tooEasy: 'Too Easy',
        alreadyKnown: 'Already Known',
        partiallyKnow: 'Partially Know',
        newToMe: 'New to Me',
        tooHard: 'Too Hard',
        notWhatLookingFor: 'Not What I\'m Looking For',
        showHiddenTips: 'Show Hidden Tips',
        hideThisTip: 'Hide',
        hiddenTip: 'Hidden Tip',
        showThisTipAgain: 'Show This Tip Again',
        favoriteThisTip: 'Favorite',
        unfavoriteThisTip: 'Unfavorite',
        favoritedTip: 'Favorited Tip',
        scheduleReview: 'Schedule Review',
        unscheduleReview: 'Unschedule Review',
        enableReviewSchedule: 'Enable review schedule for this concept',
        resetReviewProgress: 'Reset Review Progress',
        nextReviewIn: 'Next review in',
        noReviewQuestionsYet: 'No review questions available yet',
        reviewDays: 'days',
        day: 'day',
        today: 'Today',
        yesterday: 'Yesterday',
        daysAgo: '{count} days ago',
        weeksAgo: '{count} {count, plural, one {week} other {weeks}} ago',
        monthsAgo: '{count} {count, plural, one {month} other {months}} ago',
        yearsAgo: '{count} {count, plural, one {year} other {years}} ago',
        reviewDue: 'Review due',
        reviewOverdue: 'Review overdue',
        reviewSequenceCompleted: 'Review sequence completed',
        reviewProgressReset: 'Review Progress Reset',
        reviewProgressResetMessage: 'This will:\n• Reset your review progress to the beginning\n• Turn off review scheduling\n• Delete all review questions and answers\n• Keep your learning date unchanged\n\nYou can re-enable reviews later.\n\nAre you sure?',
        
        // Main Content
        currentLearningConcept: 'Current Learning Concept',
        selectTopicConcept: 'Select a topic and concept to begin.',
        notLookingFor: 'Not what you\'re looking for?',
        tryEditingTopic: 'Try editing the topic to add more description about your goals.',
        selectConcept: 'Select a concept to get started.',
        askQuestion: 'Ask a question...',
        
        // Three-State Feature
        tipOnly: 'Concept Only',
        fullView: 'Full View',
        chatOnly: 'Chat Only',
        clickStartDeepDive: 'Click "Start Deep Dive" to begin',
        startConversationFirst: 'Start a conversation first to use this view',
        botGeneratingMessage: 'The bot is generating a response, please wait...',
        
        // Quick Response Buttons
        quickActions: 'Quick Actions',
        stillDontUnderstand: 'Still don\'t understand',
        testMyKnowledge: 'Test my knowledge',
        giveAnExample: 'Give an example',
        explainDifferently: 'Explain differently',
        
        // Quick Response Messages (actual messages sent to conversation)
        stillDontUnderstandMessage: 'I still don\'t understand this concept. Could you explain it in a simpler way?',
        testMyKnowledgeMessage: 'Can you test my understanding of this concept? Ask me a question to see if I really get it.',
        giveAnExampleMessage: 'Can you give me a concrete example of this concept in action?',
        explainDifferentlyMessage: 'Could you explain this concept in a different way? Maybe use a different analogy or approach?',
        
        // Concept Details
        conceptDetails: 'Concept Details',
        myKnowledgeLevel: 'My Knowledge Level',
        dateIntroduced: 'Date Introduced',
        dateLearned: 'Date Learned',
        notLearnedYet: 'Not learned yet',
        unknownStatus: 'Unknown',
        conversation: 'Conversation',
        startNewDeepDive: 'Start New Deep Dive',
        reviewQuestions: 'Review Questions',
        tipActions: 'Actions',
        deleteConcept: 'Delete Concept',
        selectConceptDetails: 'Select a concept to see details.',
        
        // Review Questions Modal
        reviewQuestion: 'Review Question',
        deepDiveChat: 'Deep Dive Chat',
        howConfident: 'How confident are you?',
        totallyGuessing: 'Totally Guessing',
        aBitUnsure: 'A Bit Unsure',
        confident: 'Confident',
        submitAnswer: 'Submit Answer',
        tryAgain: 'Try Again',
        correctWellDone: 'Correct! Well done!',
        notQuiteRight: 'Not quite right',
        reviewLocked: 'Review Locked',
        reviewWillUnlock: 'This review will unlock on Day {day}',
        startConversation: 'Start Conversation',
        startNewConversation: 'Start New Conversation',
        answerQuestionFirst: 'Answer the review question first to start a conversation.',
        clearChatHistory: 'Clear Chat History?',
        clearChatMessage: 'This will clear all messages in the current review\'s chat. This action cannot be undone.',
        clearAndStartNew: 'Clear & Start New',
        askQuestionsToDeepen: 'Ask questions about this review to deepen your understanding!',
        reviewQuestionForDay: 'Review Question for Day {day}',
        dayNumber: 'Day {day}',
        reviewDay: 'Review {day}',
        
        // Modals
        confirm: 'Confirm',
        delete: 'Delete',
        overwriteConversation: 'Overwrite Conversation?',
        overwriteMessage: 'Starting a new deep dive will erase the current conversation for this concept. Do you want to proceed?',
        eraseAndStartNew: 'Erase and Start New',
        continueLearning: 'Continue Learning?',
        knownMessage: 'You know this concept! Would you like to request a new one?',
        easyMessage: 'This concept was too easy. Would you like to request a more advanced one?',
        requestNewConcept: 'Request New Concept',
        notNow: 'Not Now',
        hideForNow: 'Hide',
        skipConversation: 'Skip Conversation',
        continueExploring: 'Continue Exploring',
        conceptTooHard: 'Concept Too Hard',
        hardMessage: 'This concept seems challenging. Would you like to request an easier one, or skip it for now?',
        requestEasier: 'Request Easier One',
        skipForNow: 'Skip For Now',
        letsLearnMore: 'Let\'s Learn More!',
        learnMoreMessage: 'Do you want to start a deep dive conversation about this concept?',
        deleteConfirmTitle: 'Delete Topic',
        deleteConfirmMessage: 'Are you sure you want to delete "{title}"? This action cannot be undone.',
        notLookingForTitle: 'Not What You\'re Looking For?',
        notLookingForMessage: 'This concept doesn\'t match what you\'re trying to learn. Would you like to request a different concept?',
        requestDifferentConcept: 'Request Different Concept',
        selectKnowledgeLevelTitle: 'Select Knowledge Level',
        selectKnowledgeLevelMessage: 'Please select your knowledge level for this concept before starting a conversation.',
        selectLevelToContinue: 'Select a level to continue',
        
        // User Profile
        ageRange: 'Age Range',
        selectAgeRange: 'Select your age range',
        educationLevel: 'Highest Education Level',
        selectEducation: 'Select your highest education level',
        selectUILanguage: 'Select your preferred UI language',
        saveProfile: 'Save Profile',
        optional: 'Optional',
        preferNotToSay: 'I prefer not to say',
        ageRangeHelp: 'This helps us tailor content to your age group. You can skip this if you prefer.',
        educationLevelHelp: 'This helps us adjust the complexity of explanations. Include your current level if you\'re still studying.',
        
        // Age ranges
        ageRanges: {
            'under20': 'Under 20',
            '20-30': '20-30',
            '31-40': '31-40',
            '41-50': '41-50',
            '51-60': '51-60',
            'over60': 'Over 60'
        },
        
        // Education levels
        educationLevels: {
            'high_school': 'High School (or Attending)',
            'some_college': 'Some College (or Attending)',
            'associates': "Associate's Degree (or Attending)",
            'bachelors': "Bachelor's Degree (or Attending)",
            'masters': "Master's Degree (or Attending)",
            'doctorate': "Doctorate (or Attending)",
            'professional': 'Professional Degree (or Attending)',
            'other': 'Other'
        },
        
        // Loading
        loadingData: 'Loading Data...',
        loadingJourney: 'Loading Your Learning Journey...',
        generatingConcept: 'Generating Your First Concept...',
        updatingTopic: 'Updating topic...',
        deletingTopic: 'Deleting topic...',
        requestsPending: 'Requests Pending',
        
        // Errors
        errorAddingTopic: 'Error adding topic:',
        errorUpdatingTopic: 'Error updating topic:',
        errorDeletingTopic: 'Error deleting topic:',
        errorGeneratingTip: 'Error generating new tip:',
        errorDeletingTip: 'Error deleting tip:',
        failedToUpdateStatus: 'Failed to update status.',
        failedToStartConversation: 'Failed to start conversation',
        failedToSendMessage: 'Failed to send message:',
        rateLimitError: 'Too many requests. Please wait a moment before trying again.',
        rateLimitExceeded: 'Rate limit exceeded. Please try again later.',
        retryingRequest: 'Retrying request in {seconds} seconds...',
        networkError: 'Sorry, I had trouble connecting to the AI.',
        
        // Feedback Modal
        feedbackModalTitle: 'Feedback / Bug Report',
        feedbackDescription: 'Your feedback helps us improve Daily Learning Hub. Please share your thoughts!',
        feedbackInstructions: 'How to describe a bug or feedback effectively:',
        feedbackTip1: '• Be specific about what you were trying to do',
        feedbackTip2: '• Describe what happened vs what you expected',
        feedbackTip3: '• Include any error messages you saw',
        feedbackTip4: '• Mention the steps to reproduce the issue',
        feedbackTip5: '• Feel free to suggest improvements!',
        feedbackPlaceholder: 'Type your feedback or bug report here...',
        submitFeedback: 'Submit Feedback',
        submittingFeedback: 'Submitting...',
        feedbackSuccess: 'Thank you for your feedback! We\'ll review it soon.',
        feedbackError: 'Failed to submit feedback. Please try again.',
        requestTimeout: 'The request is taking longer than expected. The AI service might be busy. Please try again.',
        serverError: 'Server error occurred. Please try again in a moment.',
        pleaseSelectKnowledgeLevel: 'Please select a knowledge level for this concept before starting a conversation.',
        retry: 'Retry',
        retrying: 'Retrying...',
        previousMessageFailed: 'Failed to get response for your previous message. You can retry or send a new message.',
        
        // Session Takeover Modal
        activeSessionDetected: 'Active Session Detected',
        sessionTakeoverWarning: 'You already have this application open in another tab or window. Opening multiple sessions can cause data conflicts.',
        sessionTakeoverContinueWarning: 'If you continue, the other session will be logged out automatically.',
        continueAnyway: 'Continue Anyway',
        
        // Connection status
        connected: 'Connected to server',
        connectionLost: 'Connection lost. Attempting to reconnect...',
        reconnecting: 'Reconnecting... ({attempt}/{maxAttempts})',
        serverUpdating: 'Server is updating. Please wait...',
        connectionTimeout: 'Connection timeout. Reconnecting...',
        unableToConnect: 'Unable to connect to server. Please refresh the page.',
        serverUpdateMessage: 'Server is updating. Your session will be restored shortly.',
        
        // Group management
        dropTopicHere: 'Drop topic here',
        noTopicsInGroup: 'No topics in this group',
        failedToCreateGroup: 'Failed to create group',
        failedToUpdateGroup: 'Failed to update group',
        failedToDeleteGroup: 'Failed to delete group',
        reorderingWhileFilteringNotSupported: 'Reordering while filtering is not supported yet',
        
        // Review and testing
        noTopicsHaveReviewDue: 'No topics have review due',
        testMe: 'Test me',
        different: 'Different',
        confused: 'Confused',
        devGenerateReview: 'Dev only: Generate review question for a specific day',
        
        // Responsive warning modal
        responsiveWarningTitle: 'Best Viewed on Desktop',
        responsiveWarningMessage: 'This application is designed to be used in maximized browser windows on a computer. It may not be optimally displayed on phones or tablets.',
        understood: 'Understood',
        dontShowResponsiveWarning: "Don't show this again",
        
        // Rate concept modal
        pleaseRateThisConcept: 'Please Rate This Concept',
        beforeStartingDeepDive: 'Before starting a deep dive, please rate your knowledge of "{conceptTitle}".',
        howDoesConceptFeel: 'How does this concept feel to you?',
        
        loading: 'Loading...',
        
        // Error messages
        userNotAuthenticated: 'User is not authenticated.',
        failedToDeleteTip: 'Failed to delete tip',
        failedToDeleteTopic: 'Failed to delete topic',
        networkResponseNotOk: 'Network response was not ok',
        noTopicSelected: 'No topic selected.',
        authenticationRequired: 'Authentication required to generate summary.',
        failedToLoadReviewQuestions: 'Failed to load review questions',
        failedToGenerateAIResponse: 'Failed to generate AI response',
        tokenCountsInvalid: 'Token counts must be numbers',
        tokenCountsNegative: 'Token counts cannot be negative',
        tokenCountsNotFinite: 'Token counts must be finite numbers',
        tokenCountsExceedLimit: 'Token counts exceed reasonable limits (100M tokens)',
        noJsonInAIResponse: 'No JSON block found in AI response.',
        geminiApiKeyNotSet: 'GEMINI_API_KEY is not set',
        useTimezoneError: 'useTimezone must be used within a TimezoneProvider',
        
        // Placeholders
        askAboutReviewQuestion: 'Ask about this review question...',

        // Knowledge levels
        knowledgeLevels: {
            '0': { 
                label: 'Absolute Beginner', 
                description: 'I\'ve heard the name, but that\'s it.',
                hobbyDescription: 'I\'ve heard the name, but that\'s it.',
                professionalDescription: 'I\'ve heard the name, but that\'s it.'
            },
            '1': { 
                label: 'Novice', 
                description: 'I can define it, but have no practical experience.',
                hobbyDescription: 'I can describe it simply, but have no practical experience.',
                professionalDescription: 'I can define it, but have no practical experience.'
            },
            '2': { 
                label: 'Novice', 
                description: 'I can follow a tutorial to perform a simple task.',
                hobbyDescription: 'I can follow tutorials to learn basic concepts.',
                professionalDescription: 'I can follow tutorials for basic tasks.'
            },
            '3': { 
                label: 'Advanced Beginner', 
                description: 'I can perform simple tasks, but need to look things up often.',
                hobbyDescription: 'I can do basic things but often need to look up instructions.',
                professionalDescription: 'I can do basic work but still need to reference materials frequently.'
            },
            '4': { 
                label: 'Advanced Beginner', 
                description: 'I can perform fundamental tasks reliably without guidance.',
                hobbyDescription: 'I can handle most hobby-related tasks on my own.',
                professionalDescription: 'I can reliably perform routine tasks at work.'
            },
            '5': { 
                label: 'Competent', 
                description: 'I can troubleshoot minor, common problems on my own.',
                hobbyDescription: 'I can solve common problems and help others.',
                professionalDescription: 'I can independently troubleshoot and resolve common issues in a professional setting.'
            },
            '6': { 
                label: 'Competent', 
                description: 'I can perform most tasks independently and efficiently.',
                hobbyDescription: 'I can efficiently complete complex activities and sometimes mentor others.',
                professionalDescription: 'I can efficiently handle complex work tasks and can mentor or lead colleagues.'
            },
            '7': { 
                label: 'Proficient', 
                description: 'I can handle complex or unfamiliar problems effectively.',
                hobbyDescription: 'I can tackle challenging concepts and experiment and innovate in my area of interest.',
                professionalDescription: 'I can solve complex or novel problems at work and help drive team progress.'
            },
            '8': { 
                label: 'Proficient', 
                description: 'I have a deep understanding and can teach others.',
                hobbyDescription: 'I am influential in the hobby community and can share my passion with others.',
                professionalDescription: 'I have deep expertise, can train colleagues, and provide expert advice to my team or organization.'
            },
            '9': { 
                label: 'Expert', 
                description: 'I am a recognized authority or pioneer in this field.',
                hobbyDescription: 'I am a recognized leader in this hobby and actively contribute to the community.',
                professionalDescription: 'I am an industry leader or innovator, setting standards and driving the field forward.'
            },
            '10': { 
                label: 'Senior-Professor-Level', 
                description: 'I am a world-renowned expert and have made significant contributions to this field.',
                hobbyDescription: 'I am a world-renowned expert in this hobby and have made significant contributions to the community.',
                professionalDescription: 'I am a world-renowned expert and have made significant contributions to this industry.'
            }
        }
    },
    
    'zh-CN': {
        // Login
        username: '用户名',
        password: '密码',
        enterUsername: '请输入用户名',
        enterPassword: '请输入密码',
        rememberMe: '记住我 30 天',
        logIn: '登录',
        loginFailed: '登录失败。',
        connectionError: '无法连接到服务器。',
        signingIn: '正在登录...',
        or: '或',
        googleLoginFailed: 'Google登录失败。',
        loginTitle: '知旅驿站',
        loginSubtitle: '积跬步而行千里',
        secureConnection: '您的连接是安全且加密的',
        
        // Registration
        createAccount: '创建账户',
        registerDescription: '注册以开始您的学习之旅',
        email: '邮箱',
        confirmPassword: '确认密码',
        enterEmail: '请输入您的邮箱',
        confirmPasswordPlaceholder: '请再次输入密码',
        passwordRequirement: '至少6个字符',
        passwordsDoNotMatch: '两次密码不一致',
        passwordTooShort: '密码至少需要6个字符',
        invalidEmail: '请输入有效的邮箱地址',
        emailAlreadyRegistered: '该邮箱已被注册',
        emailAlreadyRegisteredVerified: '邮箱已注册并验证。请登录。',
        accountExistsUnverified: '该邮箱的账户已存在。我们已发送验证邮件，请查看您的收件箱。',
        registrationFailed: '注册失败',
        notWhitelisted: '抱歉，目前只有受邀的测试用户可以创建账户。',
        creatingAccount: '正在创建账户...',
        alreadyHaveAccount: '已有账户？',
        signIn: '登录',
        noAccount: '还没有账户？',
        allFieldsRequired: '所有字段都是必填的',
        
        // Password Reset
        forgotPassword: '忘记密码？',
        resetPassword: '重置密码',
        resetPasswordDescription: '输入您的电子邮箱地址，我们将向您发送重置密码的链接。',
        sendResetLink: '发送重置链接',
        sending: '发送中...',
        checkYourEmail: '查看您的邮箱',
        resetLinkSent: '如果该邮箱存在账户，重置密码链接已发送。',
        backToLogin: '返回登录',
        setNewPassword: '设置新密码',
        enterNewPassword: '请在下方输入您的新密码',
        newPassword: '新密码',
        confirmNewPassword: '确认新密码',
        resettingPassword: '正在重置密码...',
        passwordResetSuccess: '密码重置成功！',
        passwordResetSuccessMessage: '您的密码已成功重置。您现在可以使用新密码登录。',
        redirectingToLogin: '正在重定向到登录页面...',
        resetPasswordFailed: '重置密码失败',
        
        // Email Verification
        verificationEmailSent: '请查收邮件！',
        verificationEmailDescription: '我们已向以下邮箱发送了验证邮件：',
        nextSteps: '后续步骤：',
        checkInbox: '查看您的邮箱收件箱',
        clickVerifyLink: '点击邮件中的验证链接',
        loginAfterVerification: '使用您的凭据登录',
        cantFindEmail: '找不到邮件？',
        checkSpamFolder: '请检查垃圾邮件文件夹，或从登录页面请求新的验证邮件。',
        goToLogin: '前往登录',
        emailNotVerified: '邮箱未验证',
        verifyEmailToLogin: '请验证您的邮箱地址以登录。请查看收件箱中的验证邮件。',
        resendVerificationEmail: '重新发送验证邮件',
        sendingEmail: '发送中...',
        emailSent: '邮件已发送！',
        checkYourInbox: '验证邮件已发送！请查看您的收件箱。',
        
        // User onboarding
        welcomeToApp: '欢迎来到 {appTitle}',
        selectPreferredLanguage: '请选择您的语言以继续。',
        uiLanguage: '界面语言',
        youCanChangeLater: '您稍后可以在设置中更改。',
        next: '下一步',
        back: '返回',
        previous: '上一步',
        step: '步骤',
        quickStartGuide: '快速入门指南',
        userProfileSetup: '请介绍一下您自己',
        profileSetup: '此可选信息有助于我们为您量身定制学习体验。',
        
        // Guided Tutorial
        tutorialWelcomeTitle: '让我们开始吧！',
        tutorialButtonSpotlightTitle: '互动教程',
        tutorialButtonSpotlightMessage: '想要完整的功能演示吗？点击教程按钮，通过互动指南探索所有功能。如果您想直接开始，点击下一步添加您的第一个主题！',
        tutorialStepIndicator: '第 1 步，共 1 步',
        tutorialWelcomeMessage: '准备好开始您的学习之旅了吗？点击上方蓝色的"+"按钮，添加您的第一个主题，今天就开始学习新知识！',
        tutorialActionHint: '点击这里添加主题',
        tutorialActionDescription: '选择您想学习的任何科目',
        tutorialSkip: '跳过教程',
        tutorialGetStarted: '知道了！',
        
        // Comprehensive Tutorial Mode
        tutorial: '教程',
        startTutorial: '开始教程',
        tutorialNext: '下一步',
        tutorialPrevious: '上一步',
        tutorialComplete: '完成',
        tutorialWelcomeContent: '欢迎来到您的个性化学习助手！本教程将引导您了解所有功能，帮助您充分利用系统的学习优势。',
        tutorialWelcomeTip: '• 本教程大约需要1-2分钟完成。',
        tutorialNavigationTip: '• 使用键盘上的 ← 和 → 箭头键可以更方便地导航。',
        
        tutorialAddTopicContent: '点击上方的"+"按钮添加您的第一个学习主题。您可以添加任何感兴趣的科目！',
        tutorialTip: '提示：',
        tutorialAddTopicTip: '使用通用主题，如机器学习、烹饪、动物或园艺。创建主题时还可以添加学习目标和详细信息，以获得更个性化的内容。',
        tutorialTopicCardContent: '主题卡片整理您的学习科目。注意状态指示器：',
        tutorialUnreadIndicator: '蓝点 - 未读内容',
        tutorialUnreadDesc: '显示您有新的知识点需要阅读',
        tutorialReviewIndicator: '橙点 - 待复习',
        tutorialReviewDesc: '是时候复习概念以加强记忆了',
        tutorialFavoriteIndicator: '红心 - 收藏主题',
        tutorialFavoriteDesc: '您收藏的主题，方便快速访问',
        tutorialAddTipContent: '点击知识点面板中的"+"按钮，生成新的AI驱动学习概念。AI创建个性化内容可能需要约1分钟时间。',
        tutorialManualTipBenefit: '专业提示：',
        tutorialManualTipDesc: '不想等待？您可以在主题设置中安排概念生成，这样系统会自动提前为您准备好内容！',
        tutorialTipCardContent: '这些是您的学习概念。点击任何知识点查看完整内容、开始AI对话或标记为复习。',
        tutorialInteractionHint: '快捷操作：',
        tutorialInteractionDesc: '右键点击任何知识点，可以收藏、编辑或删除。',
        tutorialTopicInteractionDesc: '右键点击任何主题，可以收藏、编辑或删除。',
        tutorialTopicGroupingContent: '使用自定义分组保持主题井然有序！将相关主题归类，优化学习流程。',
        tutorialCreateGroup: '创建主题分组',
        tutorialCreateGroupDesc: '点击"添加分组"创建自定义类别，如"工作项目"、"个人学习"或"兴趣爱好"',
        tutorialDragTopics: '拖动主题到分组',
        tutorialDragTopicsDesc: '只需拖放主题卡片到任何分组即可整理它们',
        tutorialReorderWithinGroups: '组内重新排序',
        tutorialReorderWithinGroupsDesc: '在组内上下拖动主题，按您的偏好排列',
        tutorialReorderGroups: '重新排序整个分组',
        tutorialReorderGroupsDesc: '拖动组标题重新排序整个分组，优先安排您的学习领域',
        tutorialGroupingTip: '整理技巧：',
        tutorialGroupingTipDesc: '分组帮助您在学习过程中专注于特定领域，将相关主题保持在一起以获得更好的上下文。',
        tutorialGroupingNote: '重要说明：',
        tutorialGroupingNoteDesc: '分组纯粹是为了视觉组织，不会以任何方式影响AI生成的内容。',
        tutorialAITutorContent: '您的AI导师随时准备帮助！提出问题、获得解释，深入探讨任何主题。',
        tutorialReviewContent: '使用间隔重复定期复习帮助您长期保持知识。回答问题以强化您的学习。',
        tutorialCongrats: '恭喜！',
        tutorialCompleteContent: '您已完成教程！现在您已准备好开始学习之旅。',
        tutorialNextSteps: '下一步：',
        tutorialStep1: '添加您的第一个学习主题',
        tutorialStartLearningHint: '从添加您热衷于学习的主题开始！',
        tutorialStep2: '每日回顾知识点和概念',
        tutorialStep3: '有问题时与AI导师聊天',
        tutorialStep4: '完成复习以加强记忆',
        tutorialConcepts: '已学习概念',
        tutorialExplanation: '解释：',
        
        // Tutorial Dummy Content
        tutorialDummyTopicTitle: '学习JavaScript',
        tutorialDummyTopicDesc: '掌握JavaScript编程基础',
        tutorialDummyNextReview: '明天上午9:00',
        tutorialDummyTipTitle: '理解变量',
        tutorialDummyTipContent: '变量是存储数据值的容器。在JavaScript中，您可以使用let、const或var声明变量。',
        
        tutorialDummyReviewQuestion: 'let和const之间有什么区别？',
        tutorialDummyOption1: 'let可以重新赋值，const不能',
        tutorialDummyOption2: 'const可以重新赋值，let不能',
        tutorialDummyOption3: '两者都可以重新赋值',
        tutorialDummyOption4: '两者都不能重新赋值',
        tutorialDummyExplanation: '用let声明的变量可以重新赋值，而const创建常量引用。',
        
        // Tutorial Knowledge Level Modal
        tutorialNewConceptModalIntro: '当您选择未读的学习概念时，您会看到这个知识水平选择界面。这是个性化您学习体验的关键步骤！',
        tutorialTooEasy: '太简单',
        tutorialTooEasyDesc: '这个概念感觉很基础，对您当前的知识没有太多补充',
        tutorialAlreadyKnown: '已经掌握',
        tutorialAlreadyKnownDesc: '您很好地理解这个概念并对此感到自信',
        tutorialPartiallyKnow: '部分了解',
        tutorialPartiallyKnowDesc: '您有一些理解但需要加强知识',
        tutorialNewToMe: '全新内容',
        tutorialNewToMeDesc: '这是您第一次学习的全新信息',
        tutorialTooHard: '太难了',
        tutorialTooHardDesc: '这个概念目前对您来说太有挑战性，难以理解',
        tutorialKnowledgeTip: '为什么这很重要：',
        tutorialKnowledgeTipDesc: '您的选择帮助AI导师根据您的知识水平定制解释和推荐内容。',
        tutorialKnowledgeLevelPanelIntro: '您可以随时更改您的知识水平。',
        tutorialProgressTracking: '跟踪您的进度',
        tutorialProgressTrackingDesc: '随着学习进展从"全新内容"更新为"部分了解"，帮助AI适应您的进步',
        tutorialFlexibleLearning: '灵活学习',
        tutorialFlexibleLearningDesc: '搞错了？发现比预期容易？只需点击即可随时更新您的知识水平！',
        tutorialTipContentIntro: '这是您的学习内容显示的地方。每个概念都包含AI生成的精炼知识，旨在帮助您掌握所选主题。所有内容都经过搜索验证，确保准确性和相关性。',
        tutorialQuickActionsIntro: '这些快捷操作按钮帮助您发送预设提示的对话，节省您输入常见问题的时间。',
        tutorialViewModesIntro: '切换不同的视图模式，自定义您的学习体验：',
        tutorialTipOnlyMode: '仅概念',
        tutorialTipOnlyModeDesc: '专注于阅读和学习概念内容，没有任何干扰。非常适合初次阅读和理解。',
        tutorialFullViewMode: '完整视图',
        tutorialFullViewModeDesc: '并排查看知识点内容和AI对话。非常适合主动学习，同时阅读和提问。',
        tutorialChatOnlyMode: '仅聊天',
        tutorialChatOnlyModeDesc: '最大化对话空间，与AI导师进行深入讨论。非常适合扩展问答和详细解释。',
        tutorialViewModesProTip: '专业提示：',
        tutorialViewModesProTipDesc: '当您开始对话时，视图会自动切换到完整视图。您可以随时更改以匹配您的学习偏好！',
        tutorialTipActionsIntro: '使用这些强大的操作管理您的学习概念：',
        tutorialHideAction: '隐藏',
        tutorialHideActionDesc: '暂时隐藏您已掌握或想暂时跳过的概念',
        tutorialFavoriteAction: '收藏',
        tutorialFavoriteActionDesc: '标记重要概念以便快速访问和优先复习',
        tutorialScheduleReview: '安排复习',
        tutorialScheduleReviewDesc: '设置提醒使用间隔重复复习概念，以获得更好的记忆效果',
        tutorialProTip: '专业提示：',
        tutorialTipActionsProTip: '使用这些操作自定义您的学习流程。隐藏您熟悉的概念，收藏有挑战性的概念，并安排复习以获得最佳记忆效果。',
        tutorialReviewTrackingIntro: '基于艾宾浩斯曲线，生成复习题帮助您长期保持知识：',
        tutorialDateLearned: '学习日期',
        tutorialDateLearnedDesc: '这是您首次为概念选择知识水平的日期。',
        tutorialReviewButton: '复习题按钮',
        tutorialReviewButtonDesc: '点击测试您的知识与多选题。有新复习题时会发出橙色光芒。',
        tutorialDaysTillReview: '距下次复习天数',
        tutorialDaysTillReviewDesc: '显示到下次复习的倒计时。',
        tutorialReviewModalIntro: '通过复习题测试您的知识！这个强大的学习工具通过主动回忆帮助强化概念：',
        tutorialAnswerQuestions: '回答复习题',
        tutorialAnswerQuestionsDesc: '多选题测试您对关键概念的理解。选择您的答案和信心水平。',
        tutorialDeepDiveChat: '深入对话',
        tutorialDeepDiveChatDesc: '回答后，与AI导师开始对话，更深入地探索主题并澄清任何困惑。',
        tutorialActiveRecall: '主动回忆提示：',
        tutorialActiveRecallDesc: '在查看选项之前尝试回忆答案。这能加强记忆路径并改善长期记忆！',
        tutorialSpacedRepetition: '最佳实践：',
        tutorialSpacedRepetitionDesc: '为了获得最有效的学习成果，在概念到期复习时立即复习。这个时机优化了记忆保持和长期回忆。',
        tutorialScrollTip: '提示：',
        tutorialScrollTipDesc: '内容区域可滚动。如果包含更多信息，您可以向下滚动阅读整个概念。',
        tutorialDummyTopic1Name: '健康与养生',
        tutorialDummyTopic1Desc: '获得更健康、更有活力生活的必要知识',
        tutorialDummyTopic2Name: '烹饪',
        tutorialDummyTopic2Desc: '掌握基本烹饪技巧和食谱',
        tutorialDummyTopic3Name: '园艺',
        tutorialDummyTopic3Desc: '学习如何种植自己的植物和蔬菜',
        tutorialDummyTip1Title: '水分补充的力量',
        tutorialDummyTip1Content: '适当的水分补充对您身体的每个系统都至关重要。虽然"每天8杯水"的规则很流行，但您的实际需求取决于活动水平、气候和体型。\n\n关键事实：\n• 您的身体有60%是水\n• 即使轻微脱水（2%）也会影响注意力和能量\n• 最佳指标：淡黄色尿液意味着良好的水分补充\n• 在感到口渴之前喝水 - 口渴意味着您已经轻微脱水\n\n最佳时机：醒来后立即喝水，全天饮用，睡前2小时减少摄入。',
        tutorialDummyTip2Title: '理解您的能量水平',
        tutorialDummyTip2Content: '您的能量在一天中自然波动，遵循您的昼夜节律。了解这些模式帮助您优化生产力和福祉。\n\n自然能量高峰：\n• 上午（9-11点）：最适合复杂任务\n• 下午早期（13-15点）：自然低谷，适合常规工作\n• 下午晚期（15-18点）：第二个高峰，适合会议\n\n能量提升器：\n• 一致的睡眠时间表（7-9小时）\n• 营养均衡的定期餐食\n• 每90分钟短暂散步\n• 全天保持水分补充',
        tutorialDummyTip3Title: '刀工：基础',
        tutorialDummyTip3Content: '正确的刀工对高效安全的烹饪至关重要。掌握这些基本切法：\n\n• 细丝切：薄火柴棍切法\n• 切丁：均匀的立方体形状\n• 香草卷切：卷起并切片香草\n• 细末切：非常细的切丁\n\n始终保持刀具锋利并使用稳定的切菜板。',
        
        // Tutorial Conversations
        tutorialDummyBotInit1: '很高兴看到您在探索水分补充！这确实是我们身体功能的基础。\n\n您想深入了解哪个方面 - 每日摄入指南、时机还是识别脱水迹象？',
        tutorialDummyQuestion1: '我如何判断自己是真的脱水还是只是口渴？',
        tutorialDummyAnswer1: '好区别！口渴实际上是一个较晚的指标 - 当您感到口渴时，您已经轻微脱水了。\n\n更好的指标包括：\n• **尿液颜色**：淡黄色意味着水分充足；深琥珀色表示脱水\n• **能量下降**：无法解释的疲劳，特别是下午\n• **皮肤弹性**：捏一下手上的皮肤 - 应该立即弹回\n\n关键是通过全天持续喝水来预防脱水，而不是等待口渴。',
        
        tutorialDummyBotInit2: '很好，您在深入了解能量水平！理解您的自然节律是提高生产力的关键。\n\n您想探索什么 - 管理能量低谷、优化高峰时段还是提高整体耐力？',
        tutorialDummyQuestion2: '为什么即使睡眠充足，我总是在下午2-3点崩溃？',
        tutorialDummyAnswer2: '那个下午低谷实际上是自然的 - 这是您昼夜节律的一部分，不一定是出了问题的迹象。\n\n您的体温在下午1-3点之间自然下降，引发困倦。要最小化崩溃：\n• **吃较清淡的午餐** - 重餐会加重困倦\n• **短暂散步** - 即使5-10分钟也有帮助\n• **保持水分** - 脱水会加剧疲劳\n• **考虑20分钟的能量小睡** 如果可能\n\n通过在这个时间安排常规任务，并将复杂工作留给上午或下午晚期的高峰时段，来配合这个自然低谷。',
        
        // Tutorial Review Questions
        tutorialDummyReviewQ2: '什么时候喝一大杯水对新陈代谢最好？',
        tutorialDummyReviewChoice2A: '睡前',
        tutorialDummyReviewChoice2B: '用餐时',
        tutorialDummyReviewChoice2C: '早晨第一件事',
        tutorialDummyReviewChoice2D: '仅在运动时',
        tutorialDummyReviewExp2A: '睡前大量饮水会因上厕所而扰乱睡眠。',
        tutorialDummyReviewExp2B: '用餐时大量饮水会稀释消化酸。小口饮用更好。',
        tutorialDummyReviewExp2C: '正确！早晨补水启动新陈代谢并帮助冲洗夜间毒素。',
        tutorialDummyReviewExp2D: '虽然运动时很重要，但这不是促进新陈代谢的最佳时机。',
        tutorialChatStarter2A: '睡前补水很棘手！让我解释一下水分和睡眠之间的平衡...',
        tutorialChatStarter2B: '用餐时补水很重要但时机很重要。让我解释为什么...',
        tutorialChatStarter2C: '完美！想了解最大化效益的早晨补水程序吗？',
        tutorialChatStarter2D: '运动补水是关键但不同。让我解释时机...',
        
        // Tutorial Scheduled Generation Modal
        tutorialScheduledGenerationIntro: '使用定时概念生成自动化您的学习！设置后无需操心 - 新概念将在您偏好的时间自动生成。',
        tutorialSetSchedule: '设置您的计划',
        tutorialSetScheduleDesc: '选择新概念应该何时以及多久生成一次 - 每日、每周或每月在您偏好的时间。',
        tutorialTimezoneAware: '时区感知',
        tutorialTimezoneAwareDesc: '系统自动调整到您的时区，确保概念在您期望的时间准备好。',
        tutorialSmartSkipping: '智能跳过',
        tutorialSmartSkippingDesc: '启用"如果存在未读概念则跳过"以防止自己被太多未读概念淹没。',
        
        // Tutorial Filter Functions
        tutorialFilterFunctionsIntro: '使用智能筛选保持条理！通过强大的筛选选项快速找到对您最重要的主题和概念。',
        tutorialFavoriteFilter: '收藏筛选',
        tutorialFavoriteFilterDesc: '点击心形图标只显示您收藏的主题或概念。非常适合专注于您最关心的内容。',
        tutorialReviewDueFilter: '待复习筛选',
        tutorialReviewDueFilterDesc: '点击时钟图标只显示需要复习的项目。掌握您的间隔重复计划。',
        tutorialAdvancedFilter: '高级筛选',
        tutorialAdvancedFilterDesc: '点击漏斗图标访问高级筛选选项，如状态（未读、已标记为已知等）和定时生成设置。',
        tutorialFilterProTip: '专业提示',
        tutorialCombineFiltersTip: '您可以组合多个筛选器创建强大的视图。例如，只显示需要复习的收藏主题！',
        
        // Tutorial AI Learning Summary Modal
        tutorialAILearningSummaryIntro: '获得您学习旅程的个性化见解！AI分析您的互动以了解您的知识概况、学习模式和优化机会。',
        tutorialKnowledgeProfile: '知识概况',
        tutorialKnowledgeProfileDesc: '基于您与概念和对话的参与了解您的优势和知识差距。',
        tutorialLearningPatterns: '学习模式',
        tutorialLearningPatternsDesc: '发现您如何最好地学习 - 是通过例子、理论还是基于实践的方法。',
        tutorialOptimizationInsights: '优化见解',
        tutorialOptimizationInsightsDesc: '获得可行的建议以提高您的学习效果和记忆。',
        tutorialRequirementNote: '注意：',
        tutorialRequirementNoteDesc: '您需要至少阅读5个概念才能生成您的第一个AI学习总结。这确保有足够的数据产生有意义的见解。',
        tutorialRightClickTopicTip: '您可以随时右键点击任何主题卡片并选择"编辑"来访问这些设置。',
        
        // Tutorial Group Names
        tutorialDummyGroup1Name: '健康与生活方式',
        tutorialDummyGroup2Name: '兴趣爱好',
        addYourFirstTopic: '添加您的第一个主题',
        tutorialDummyUserQuestion: '您能解释一下函数是如何工作的吗？',
        tutorialDummyAIResponse: '函数是执行特定任务的可重用代码块。把它们想象成可以多次使用的食谱...',
        
        // Header
        appTitle: '知旅驿站', // or 日新堂? 学思阁?
        welcome: '欢迎',
        logout: '退出登录',
        
        // Profile
        name: '姓名',
        enterYourName: '请输入您的姓名',
        nameHelp: '您的姓名将用于个性化交互',
        
        // Topics
        topics: '主题',
        addTopicTooltip: '添加新的学习主题',
        filterFavoriteTopicsTooltip: '只显示收藏的主题',
        filterReviewDueTopicsTooltip: '显示有待复习概念的主题',
        filterTopicsTooltip: '筛选主题',
        noTopicsToFilter: '请先创建主题以使用筛选功能',
        editTopic: '编辑主题',
        topicName: '主题名称',
        startNewLearningTopic: '开始新的学习主题',
        learningContext: '学习背景和目标（可选）',
        learningContextHelp: '描述您当前的水平、已学内容、具体目标以及任何有助于生成更相关概念的背景信息。',
        learningContextExample: '示例："我是日语初学者，想学习东京旅行用的日常俚语。我已经学会了基本问候语，能读平假名。我想专注于年轻人日常对话中使用的短语。我特别对食物相关词汇和问路感兴趣。"',
        purpose: '学习目的',
        hobby: '兴趣爱好',
        professional: '技术/职业发展',
        topicLanguage: '主题语言',
        saveChanges: '保存更改',
        editTopicChangesNote: '注意：主题设置的更改只会影响未来生成的提示。现有提示将保持不变。',
        learningDetails: '学习详情',
        knowledgeContext: '知识与背景',
        learningSummary: 'AI学习总结',
        summaryTooltip: '基于您的活动生成的学习模式和兴趣的AI分析',
        generateSummary: '生成总结',
        generating: '生成中...',
        summaryPlaceholder: '点击"生成总结"来分析您的学习模式和兴趣...',
        includeSummaryInPrompt: '包含在AI上下文中以获得个性化内容',
        summaryHelp: '此AI生成的总结分析您的学习模式以个性化未来内容',
        insufficientReadTips: '您需要至少阅读5个提示才能生成AI学习总结。您目前已阅读{count}个提示。继续阅读和标记提示以解锁此功能。',
        noTipsForSummary: '暂无学习内容。请先添加一些提示以生成总结。',
        summaryCachedMessage: '自上次生成总结以来，您的学习活动未发生变化。请在阅读更多提示或进行更多对话后再试。',
        summaryNewMessage: '基于您最新的活动和交互生成新的学习总结。',
        summaryGenerationError: '生成学习总结失败。请重试。',
        knowledgeProfile: '知识画像',
        learningPatterns: '学习模式',
        optimizationInsights: '优化建议',
        edit: '编辑',
        view: '查看',
        learningContextGoals: '学习背景与目标（可选）',
        learningContextPlaceholder: '描述您的具体学习目标、兴趣或想要专注的内容...',
        cancel: '取消',
        startLearning: '开始学习',
        settings: '设置',
        feedbackBugReport: '反馈 / 错误报告',
        currentProfile: '当前资料',
        age: '年龄',
        education: '教育',
        notProvided: '未提供',
        yearsOld: '岁',
        saving: '保存中...',
        profile: '个人资料',
        aiModel: 'AI 模型',
        activeSessions: '活跃会话',
        usage: '使用情况',
        thankYouTestUser: '感谢您成为测试用户！',
        thankYouMessage: '我们感谢您帮助测试{appTitle}。您的反馈非常宝贵。',
        testUserBenefits: '您的测试用户权益',
        conversations: '对话',
        unlimited: '无限制',
        conceptGeneration: '概念生成',
        aiLearningSummary: 'AI学习总结',
        reviewScheduling: '复习安排',
        loadingSessions: '加载会话中...',
        activeSessionsInfo: '管理您的活跃会话',
        activeSessionsDescription: '查看和管理您当前登录的所有设备。您可以从特定设备或所有设备退出登录。',
        noActiveSessions: '未找到活跃会话',
        unknownDevice: '未知设备',
        currentSession: '当前会话',
        ipAddress: 'IP地址',
        unknown: '未知',
        lastActive: '最后活跃',
        logoutThisDevice: '从此设备退出',
        logoutAllDevices: '从所有设备退出',
        confirmLogoutSession: '您确定要从此设备退出吗？',
        confirmLogoutAllSessions: '您确定要从所有设备退出吗？您需要重新登录。',
        close: '关闭',
        
        // AI Model Settings
        conversationModel: '对话模型',
        selectConversationModel: '选择对话 AI 模型',
        selectAIProvider: '选择 AI 提供商',
        modelQualityVsSpeed: '质量与速度权衡',
        modelPro: '高级版',
        modelProDescription: '深度推理，最佳质量响应',
        modelFlash: '平衡版',
        modelFlashDescription: '良好质量，响应更快',
        modelLite: '快速版',
        modelLiteDescription: '简单查询的快速响应',
        recommended: '推荐',
        bestQuality: '最佳质量',
        balanced: '平衡',
        fastest: '最快',
        quality: '质量',
        speed: '速度',
        
        // Slow Chat Notification
        slowChatNotification: '等待响应时间过长？尝试切换到更快的模型以获得更快的回复',
        changeModel: '更换模型',
        dontShowAgain: '不再显示',
        
        sessionInvalidatedByAnotherLogin: '您已被登出，因为您从另一台设备登录。',
        favoriteTopic: '收藏主题',
        unfavoriteTopic: '取消收藏主题',
        favoritedTopic: '已收藏主题',
        showFavoriteTopicsOnly: '只显示收藏主题',
        showReviewDueTopicsOnly: '只显示待复习主题',
        showScheduledTopicsOnly: '只显示自动生成主题',
        showUnscheduledTopicsOnly: '只显示手动主题',
        showAllTopics: '显示所有主题',
        daily: '每日',
        weekly: '每周',
        monthly: '每月',
        everyNDays: '每{count}天',
        everyNWeeks: '每{count}周',
        everyNMonths: '每{count}月',
        topicSingular: '主题',
        topicPlural: '主题',
        ungrouped: '未分组',
        createGroup: '创建分组',
        groupName: '分组名称',
        rename: '重命名',
        deleteGroupTitle: '删除分组',
        deleteGroupMessage: '您确定要删除此分组吗？主题将被移至未分组。',
        
        // Add Topic Modal specific translations
        learningGoalQuestion: '您学习此的目的是什么？',
        contentLanguageQuestion: '内容应以哪种语言解释？',
        rateYourKnowledge: '评估您的当前知识水平',
        example: '示例',
        
        // Scheduling translations
        enableAutomaticTipGeneration: '启用自动提示生成',
        generateTipsAt: '在以下时间生成提示：',
        generateNewTipsEvery: '每隔以下时间生成新提示：',
        hour: '小时',
        hours: '小时',
        days: '天',
        weeks: '周',
        months: '月',
        timezone: '时区',
        schedulingNote: '将使用您选择的时区',
        scheduledSkipIfUnread: '如果该主题有未读的知识点，则不自动生成新知识点',
        
        // Placeholders
        topicNamePlaceholder: '量子计算',
        selectKnowledgeLevel: '拖动滑块来评估您的当前知识水平',
        selectLearningGoal: '请选择您的学习目标',
        completeAllFieldsToStart: '请完成主题名称、学习目标和知识水平设置以开始学习',
        
        // Topic Examples
        topicExamples: [
            {
                name: '量子计算',
                description: '我是一名物理系学生，在线性代数和经典力学方面有扎实的基础。我想为我的研究项目理解量子算法。我已经学习了量子力学基础知识，能够处理复数运算。我特别对量子密码学和量子机器学习应用感兴趣。'
            },
            {
                name: '日语会话',
                description: '我是日语初学者，想学习东京旅行用的日常俚语。我已经学会了基本问候语，能读平假名。我想专注于年轻人日常对话中使用的短语。我特别对食物相关词汇和问路感兴趣。'
            },
            {
                name: '机器学习',
                description: '我是一名软件开发者，有Python和统计学经验。我想为公司电商平台构建推荐系统。我已经参加了监督学习在线课程，理解基本算法。我特别对协同过滤和深度学习方法感兴趣。'
            },
            {
                name: '旅行西班牙语',
                description: '我计划在南美洲背包旅行，需要实用的西班牙语技能。我从高中就知道一些基本词汇，但多年没有练习了。我想专注于点餐、预订住宿和交朋友的会话技能。我特别对地区差异和文化背景感兴趣。'
            },
            {
                name: '区块链开发',
                description: '我是一名网页开发者，有JavaScript和智能合约经验。我想为创业项目构建去中心化应用。我已经学习了Solidity基础，理解以太坊的工作原理。我特别对DeFi协议和NFT市场感兴趣。'
            },
            {
                name: '烹饪技巧',
                description: '我是一名家庭厨师，想为晚宴提高烹饪技能。我能按照菜谱做菜，但想了解烹饪方法背后的科学原理。我已经掌握了基本刀工，能制作简单酱汁。我特别对法国技术和分子美食学感兴趣。'
            },
            {
                name: '数据科学',
                description: '我是一名商业分析师，想转向数据科学。我有很强的Excel技能和基本SQL知识。我想学习Python进行数据分析和可视化。我特别对预测建模和商业智能应用感兴趣。'
            },
            {
                name: '吉他理论',
                description: '我是一名中级吉他手，能弹和弦和简单歌曲。我想理解音乐理论来创作自己的歌曲和即兴演奏。我已经学习了基本音阶，能读吉他谱。我特别对爵士理论和指弹技巧感兴趣。'
            },
            {
                name: '摄影',
                description: '我是一名摄影爱好者，有一台单反相机。我想提高风景摄影的构图和用光技巧。我理解基本相机设置，但想掌握手动模式。我特别对长时间曝光技术和后期处理感兴趣。'
            },
            {
                name: '网页开发',
                description: '我是一名初学者程序员，想构建现代网页应用。我从在线教程中获得了基本的HTML和CSS知识。我想学习JavaScript框架和后端开发。我特别对React和Node.js全栈开发感兴趣。'
            },
            {
                name: '财务规划',
                description: '我是一名刚毕业的学生，想学习个人理财和投资策略。我对预算有基本了解，但想学习股票、债券和退休规划。我特别对指数基金和税收效率投资感兴趣。'
            },
            {
                name: '公众演讲',
                description: '我是一名专业人士，需要提高工作会议和演讲的演讲技巧。我能在小组中发言，但在大观众面前会紧张。我想学习吸引人的表达和处理问题的技巧。我特别对讲故事和说服性演讲感兴趣。'
            },
            {
                name: '瑜伽练习',
                description: '我是一名初学者，想为减压和灵活性发展持续的瑜伽练习。我试过几节课，但想了解正确的对齐和呼吸技巧。我特别对流瑜伽和冥想练习感兴趣。'
            },
            {
                name: '游戏开发',
                description: '我是一名程序员，想作为爱好项目创建2D游戏。我有C++和基本图形编程经验。我想学习游戏引擎和设计原则。我特别对Unity和移动游戏开发感兴趣。'
            },
            {
                name: '创意写作',
                description: '我是一名有抱负的作家，想提高短篇小说的讲故事技巧。我喜欢阅读，但在角色发展和情节结构方面有困难。我想学习创作引人入胜叙事的技巧。我特别对科幻和奇幻类型感兴趣。'
            },
            {
                name: '数字营销',
                description: '我是一名小企业主，想提高在线存在感和客户获取。我有基本的社交媒体知识，但想学习SEO和付费广告。我特别对内容营销和电子邮件活动感兴趣。'
            },
            {
                name: '冥想',
                description: '我是一名忙碌的专业人士，想为心理清晰和压力管理发展冥想练习。我试过正念应用，但想了解不同的技巧和传统。我特别对超验冥想和引导练习感兴趣。'
            },
            {
                name: '家庭酿酒',
                description: '我是一名啤酒爱好者，想开始在家酿造自己的精酿啤酒。我理解基本酿造过程，但想学习不同风格和技术。我特别对IPA和酸啤酒感兴趣。'
            },
            {
                name: '天文学',
                description: '我是一名科学爱好者，想了解宇宙和天体。我有一台基本望远镜，能识别主要星座。我想理解天体物理学概念和观测技术。我特别对系外行星和深空天体感兴趣。'
            },
            {
                name: '国际象棋策略',
                description: '我是一名中级国际象棋棋手，想提高战略思维和残局技能。我知道基本开局，能计算简单战术。我想学习位置棋和高级概念。我特别对中局规划和兵结构感兴趣。'
            }
        ],
        
        // Concepts
        concepts: '概念',
        addConceptTooltip: '为此主题生成新概念',
        filterFavoriteConceptsTooltip: '只显示收藏的概念',
        filterReviewDueConceptsTooltip: '显示待复习的概念',
        filterConceptsTooltip: '筛选概念',
        selectTopicToFilterConcepts: '请先选择一个主题以筛选概念',
        filterByStatus: '按状态筛选',
        showFavoritesOnly: '只显示收藏',
        showAllConcepts: '显示所有概念',
        noConceptsMatchFilter: '没有概念符合您的当前筛选设置。',
        tooEasy: '太简单',
        alreadyKnown: '已经知道',
        partiallyKnow: '部分知道',
        newToMe: '对我来说是新的',
        tooHard: '太难',
        notWhatLookingFor: '不是我要找的？',
        showHiddenTips: '显示隐藏提示',
        hideThisTip: '隐藏',
        hiddenTip: '隐藏提示',
        showThisTipAgain: '再次显示此提示',
        favoriteThisTip: '收藏',
        unfavoriteThisTip: '取消收藏',
        favoritedTip: '已收藏提示',
        scheduleReview: '安排复习',
        unscheduleReview: '取消复习安排',
        enableReviewSchedule: '为此概念启用复习计划',
        resetReviewProgress: '重置复习进度',
        nextReviewIn: '下次复习在',
        noReviewQuestionsYet: '暂无可用的复习问题',
        reviewDays: '天后',
        day: '天后',
        today: '今天',
        yesterday: '昨天',
        daysAgo: '{count}天前',
        weeksAgo: '{count}周前',
        monthsAgo: '{count}个月前',
        yearsAgo: '{count}年前',
        reviewDue: '复习到期',
        reviewOverdue: '复习逾期',
        reviewSequenceCompleted: '复习序列完成',
        reviewProgressReset: '复习进度重置',
        reviewProgressResetMessage: '这将：\n• 重置您的复习进度到开始\n• 关闭复习计划\n• 删除所有复习问题和答案\n• 保留您的学习日期不变\n\n您可以稍后重新启用复习。\n\n您确定吗？',
        
        // Main Content
        currentLearningConcept: '当前学习概念',
        selectTopicConcept: '选择一个主题和概念开始。',
        notLookingFor: '不是您要找的？',
        tryEditingTopic: '尝试编辑主题以添加更多关于您目标的描述。',
        selectConcept: '选择一个概念开始。',
        askQuestion: '提问...',
        
        // Three-State Feature
        tipOnly: '仅概念',
        fullView: '完整视图',
        chatOnly: '仅聊天',
        clickStartDeepDive: '点击"开始深度对话"开始',
        startConversationFirst: '先开始对话再使用此视图',
        botGeneratingMessage: '机器人正在生成回复，请稍候...',
        
        // Quick Response Buttons
        quickActions: '快速操作',
        stillDontUnderstand: '仍不明白',
        testMyKnowledge: '考考我',
        giveAnExample: '给个例子',
        explainDifferently: '换个说法',
        
        // Quick Response Messages (actual messages sent to conversation)
        stillDontUnderstandMessage: '我仍然不明白这个概念。你能用更简单的方式解释一下吗？',
        testMyKnowledgeMessage: '你能测试我对这个概念的理解吗？问我一些问题，看看我是否真的理解了。',
        giveAnExampleMessage: '你能给我一个具体的例子，说明这个概念在实际中的应用吗？',
        explainDifferentlyMessage: '你能用不同的方式解释这个概念吗？也许使用不同的类比或方法？',
        
        // Concept Details
        conceptDetails: '概念详情',
        myKnowledgeLevel: '我的知识水平',
        dateIntroduced: '引入日期',
        dateLearned: '学习日期',
        notLearnedYet: '尚未学习',
        unknownStatus: '未知',
        conversation: '对话',
        startNewDeepDive: '开始新的深度对话',
        reviewQuestions: '复习问题',
        tipActions: '操作',
        deleteConcept: '删除概念',
        selectConceptDetails: '选择一个概念查看详情。',
        
        // Review Questions Modal
        reviewQuestion: '复习问题',
        deepDiveChat: '深度对话',
        howConfident: '您有多自信？',
        totallyGuessing: '完全猜测',
        aBitUnsure: '有点不确定',
        confident: '有信心',
        submitAnswer: '提交答案',
        tryAgain: '重试',
        correctWellDone: '正确！做得好！',
        notQuiteRight: '不太对',
        reviewLocked: '复习已锁定',
        reviewWillUnlock: '此复习将在第{day}天解锁',
        startConversation: '开始对话',
        startNewConversation: '开始新对话',
        answerQuestionFirst: '请先回答复习问题再开始对话。',
        clearChatHistory: '清除聊天记录？',
        clearChatMessage: '这将清除当前复习的所有聊天消息。数据删除后不可恢复。',
        clearAndStartNew: '清除并开始新的',
        askQuestionsToDeepen: '针对此复习提问，加深您的理解！',
        reviewQuestionForDay: '第{day}天复习问题',
        dayNumber: '第{day}天',
        reviewDay: '复习{day}',
        
        // Modals
        confirm: '确认',
        delete: '删除',
        overwriteConversation: '覆盖对话？',
        overwriteMessage: '开始新的深度对话将删除此概念的当前对话。您要继续吗？',
        eraseAndStartNew: '删除并开始新的',
        continueLearning: '继续学习？',
        knownMessage: '您知道这个概念！您想请求一个新的吗？',
        easyMessage: '这个概念太简单了。您想请求一个更高级的吗？',
        requestNewConcept: '请求新概念',
        notNow: '暂时不要',
        hideForNow: '隐藏',
        skipConversation: '跳过对话',
        continueExploring: '继续探索',
        conceptTooHard: '概念太难',
        hardMessage: '这个概念似乎很有挑战性。您想请求一个更简单的，还是暂时跳过？',
        requestEasier: '请求更简单的',
        skipForNow: '暂时跳过',
        letsLearnMore: '让我们学习更多！',
        learnMoreMessage: '您想开始关于这个概念的深度对话吗？',
        deleteConfirmTitle: '删除主题',
        deleteConfirmMessage: '您确定要删除"{title}"吗？数据删除后不可恢复。',
        notLookingForTitle: '不是您要找的？',
        notLookingForMessage: '这个概念不符合您要学习的。您想请求一个不同的概念吗？',
        requestDifferentConcept: '请求不同的概念',
        selectKnowledgeLevelTitle: '选择知识水平',
        selectKnowledgeLevelMessage: '在开始对话之前，请为此概念选择知识水平。',
        selectLevelToContinue: '选择一个级别继续',
        
        // User Profile
        ageRange: '年龄范围',
        selectAgeRange: '选择您的年龄范围',
        educationLevel: '最高教育水平',
        selectEducation: '选择您的最高教育水平',
        selectUILanguage: '选择您偏好的界面语言',
        saveProfile: '保存资料',
        optional: '可选',
        preferNotToSay: '我选择不说',
        ageRangeHelp: '这有助于我们根据您的年龄段调整内容。如果您愿意，可以跳过此项。',
        educationLevelHelp: '这有助于我们调整解释的复杂度。如果您仍在学习，请包含您当前的水平。',
        
        // Age ranges
        ageRanges: {
            'under20': '20岁以下',
            '20-30': '20-30岁',
            '31-40': '31-40岁',
            '41-50': '41-50岁',
            '51-60': '51-60岁',
            'over60': '60岁以上'
        },
        
        // Education levels
        educationLevels: {
            'high_school': '高中或在读',
            'some_college': '大学或在读',
            'associates': '副学士学位或在读',
            'bachelors': '学士学位或在读',
            'masters': '硕士学位或在读',
            'doctorate': '博士学位或在读',
            'professional': '专业学位或在读',
            'other': '其他'
        },
        
        // Loading
        loadingData: '加载数据...',
        loadingJourney: '加载您的学习之旅...',
        generatingConcept: '生成您的第一个概念...',
        updatingTopic: '更新主题...',
        deletingTopic: '删除主题...',
        requestsPending: '个请求待处理',
        
        // Errors
        errorAddingTopic: '添加主题时出错：',
        errorUpdatingTopic: '更新主题时出错：',
        errorDeletingTopic: '删除主题时出错：',
        errorGeneratingTip: '生成新提示时出错：',
        errorDeletingTip: '删除提示时出错：',
        failedToUpdateStatus: '更新状态失败。',
        failedToStartConversation: '开始对话失败',
        failedToSendMessage: '发送消息失败：',
        rateLimitError: '请求太频繁。请稍等片刻后再试。',
        rateLimitExceeded: '超出速率限制。请稍后再试。',
        retryingRequest: '将在 {seconds} 秒后重试请求...',
        networkError: '抱歉，我无法连接到AI。',
        requestTimeout: '请求时间过长。AI服务可能繁忙。请重试。',
        serverError: '服务器错误。请稍后重试。',
        pleaseSelectKnowledgeLevel: '在开始对话之前，请为此概念选择知识水平。',
        retry: '重试',
        retrying: '正在重试...',
        previousMessageFailed: '未能获取您上一条消息的回复。您可以重试或发送新消息。',
        
        // Session Takeover Modal
        activeSessionDetected: '检测到活动会话',
        sessionTakeoverWarning: '您已经在另一个标签页或窗口中打开了此应用程序。打开多个会话可能会导致数据冲突。',
        sessionTakeoverContinueWarning: '如果您继续，其他会话将自动登出。',
        continueAnyway: '仍然继续',
        
        // Connection status
        connected: '已连接到服务器',
        connectionLost: '连接断开。正在尝试重新连接...',
        reconnecting: '重新连接中... ({attempt}/{maxAttempts})',
        serverUpdating: '服务器正在更新。请稍候...',
        connectionTimeout: '连接超时。重新连接中...',
        unableToConnect: '无法连接到服务器。请刷新页面。',
        serverUpdateMessage: '服务器正在更新。您的会话将很快恢复。',
        
        // Group management
        dropTopicHere: '将主题拖放到这里',
        noTopicsInGroup: '该组中没有主题',
        failedToCreateGroup: '创建组失败',
        failedToUpdateGroup: '更新组失败',
        failedToDeleteGroup: '删除组失败',
        reorderingWhileFilteringNotSupported: '筛选时暂不支持重新排序',
        
        // Review and testing
        noTopicsHaveReviewDue: '没有需要复习的主题',
        testMe: '测试我',
        different: '不同',
        confused: '困惑',
        devGenerateReview: '仅限开发：为特定日期生成复习问题',
        
        // Responsive warning modal
        responsiveWarningTitle: '最佳桌面浏览体验',
        responsiveWarningMessage: '此应用程序专为在计算机上最大化的浏览器窗口中使用而设计。在手机或平板电脑上可能无法最佳显示。',
        understood: '明白了',
        dontShowResponsiveWarning: '不再显示',
        
        // Rate concept modal
        pleaseRateThisConcept: '请评价这个概念',
        beforeStartingDeepDive: '在开始深入学习前，请评价您对"{conceptTitle}"的了解程度。',
        howDoesConceptFeel: '这个概念对您来说感觉如何？',
        
        loading: '加载中...',
        
        // Error messages
        userNotAuthenticated: '用户未通过身份验证。',
        failedToDeleteTip: '删除提示失败',
        failedToDeleteTopic: '删除主题失败',
        networkResponseNotOk: '网络响应异常',
        noTopicSelected: '未选择主题。',
        authenticationRequired: '生成总结需要身份验证。',
        failedToLoadReviewQuestions: '加载复习问题失败',
        failedToGenerateAIResponse: '生成AI响应失败',
        tokenCountsInvalid: 'Token数必须是数字',
        tokenCountsNegative: 'Token数不能为负数',
        tokenCountsNotFinite: 'Token数必须是有限数字',
        tokenCountsExceedLimit: 'Token数超过合理限制（1亿tokens）',
        noJsonInAIResponse: 'AI响应中未找到JSON块。',
        geminiApiKeyNotSet: '未设置GEMINI_API_KEY',
        useTimezoneError: '必须在TimezoneProvider中使用useTimezone',
        
        // Placeholders
        askAboutReviewQuestion: '询问有关此复习问题的内容...',
        
        // Knowledge levels
        knowledgeLevels: {
            '0': { 
                label: '绝对初学者', 
                description: '我听说过这个名字，但仅此而已。',
                hobbyDescription: '我听说过这个名字，但仅此而已。',
                professionalDescription: '我听说过这个名字，但仅此而已。'
            },
            '1': { 
                label: '新手', 
                description: '我可以定义它，但没有实际经验。',
                hobbyDescription: '我可以简单描述，但没有实际经验。',
                professionalDescription: '我可以定义它，但没有实际经验。'
            },
            '2': { 
                label: '新手', 
                description: '我可以按照教程执行简单任务。',
                hobbyDescription: '我能跟着教程学习基本概念。',
                professionalDescription: '我能跟着教程完成基础任务。'
            },
            '3': { 
                label: '进阶初学者', 
                description: '我可以执行简单任务，但经常需要查找资料。',
                hobbyDescription: '我能做一些基础操作，但经常需要查资料。',
                professionalDescription: '我能做基础工作，但还需查阅资料。'
            },
            '4': { 
                label: '进阶初学者', 
                description: '我可以可靠地执行基本任务，无需指导。',
                hobbyDescription: '我能独立完成大部分兴趣相关的任务。',
                professionalDescription: '我能独立完成本职工作中的常规任务。'
            },
            '5': { 
                label: '熟练', 
                description: '我可以自己解决小的常见问题。',
                hobbyDescription: '我能解决常见问题，也能帮助其他人。',
                professionalDescription: '我能独立排查和解决常见问题，具备一定的专业能力。'
            },
            '6': { 
                label: '熟练', 
                description: '我可以独立高效地执行大多数任务。',
                hobbyDescription: '我能高效独立地完成复杂的活动，偶尔指导他人。',
                professionalDescription: '我能高效独立地完成复杂工作任务，并能指导同事。'
            },
            '7': { 
                label: '精通', 
                description: '我可以有效处理复杂或不熟悉的问题。',
                hobbyDescription: '我能挑战高难度概念，钻研和创新。',
                professionalDescription: '我能应对复杂或新颖的工作难题，推动团队进步。'
            },
            '8': { 
                label: '精通', 
                description: '我有深入的理解，可以教授他人。',
                hobbyDescription: '我在圈子里有一定影响力，能分享和带动更多人参与。',
                professionalDescription: '我有深厚的专业知识，能培训同事或为团队提供专业建议。'
            },
            '9': { 
                label: '专家', 
                description: '我是这个领域的公认权威或先驱。',
                hobbyDescription: '我是该兴趣领域的权威人物，积极推动社区发展。',
                professionalDescription: '我是该领域的行业领袖或创新者，推动行业标准和发展。'
            },
            '10': { 
                label: '资深教授级', 
                description: '我是世界知名的专家，对这个领域做出了重大贡献。',
                hobbyDescription: '我是世界知名的专家，对这个兴趣领域做出了重大贡献。',
                professionalDescription: '我是世界知名的专家，对这个行业做出了重大贡献。'
            }
        },
        
        // Feedback Modal
        feedbackModalTitle: '反馈 / 错误报告',
        feedbackDescription: '您的反馈帮助我们改进知旅驿站。请分享您的想法！',
        feedbackInstructions: '如何有效地描述错误或反馈：',
        feedbackTip1: '• 具体说明您正在尝试做什么',
        feedbackTip2: '• 描述发生了什么与您期望的不同',
        feedbackTip3: '• 包含您看到的任何错误消息',
        feedbackTip4: '• 说明重现问题的步骤',
        feedbackTip5: '• 欢迎提出改进建议！',
        feedbackPlaceholder: '在此输入您的反馈或错误报告...',
        submitFeedback: '提交反馈',
        submittingFeedback: '提交中...',
        feedbackSuccess: '感谢您的反馈！我们会尽快查看。',
        feedbackError: '提交反馈失败。请重试。'
    },
    
    'zh-Hant': {
        // Login
        username: '使用者名稱',
        password: '密碼',
        enterUsername: '請輸入使用者名稱',
        enterPassword: '請輸入密碼',
        rememberMe: '記住我 30 天',
        logIn: '登入',
        loginFailed: '登入失敗。',
        connectionError: '無法連接到伺服器。',
        signingIn: '正在登入...',
        or: '或',
        googleLoginFailed: 'Google登入失敗。',
        loginTitle: '知識驛站',
        loginSubtitle: '積跬步而行千里',
        secureConnection: '您的連接是安全且加密的',
        
        // Registration
        createAccount: '建立帳戶',
        registerDescription: '註冊以開始您的學習之旅',
        email: '電子郵件',
        confirmPassword: '確認密碼',
        enterEmail: '請輸入您的電子郵件',
        confirmPasswordPlaceholder: '請再次輸入密碼',
        passwordRequirement: '至少6個字符',
        passwordsDoNotMatch: '兩次密碼不一致',
        passwordTooShort: '密碼至少需要6個字符',
        invalidEmail: '請輸入有效的電子郵件地址',
        emailAlreadyRegistered: '該電子郵件已被註冊',
        emailAlreadyRegisteredVerified: '電子郵件已註冊並驗證。請登入。',
        accountExistsUnverified: '該電子郵件的帳戶已存在。我們已發送驗證電子郵件，請查看您的收件箱。',
        registrationFailed: '註冊失敗',
        notWhitelisted: '抱歉，目前只有受邀的測試使用者可以建立帳戶。',
        creatingAccount: '正在建立帳戶...',
        alreadyHaveAccount: '已有帳戶？',
        signIn: '登入',
        noAccount: '還沒有帳戶？',
        allFieldsRequired: '所有欄位都是必填的',
        
        // Password Reset
        forgotPassword: '忘記密碼？',
        resetPassword: '重置密碼',
        resetPasswordDescription: '輸入您的電子郵件地址，我們將向您發送重置密碼的連結。',
        sendResetLink: '發送重置連結',
        sending: '發送中...',
        resetLinkSent: '重置連結已發送！請查看您的電子郵件。',
        resetLinkError: '發送重置連結失敗。請重試。',
        newPassword: '新密碼',
        enterNewPassword: '輸入您的新密碼',
        confirmNewPassword: '確認新密碼',
        resetPasswordButton: '重置密碼',
        resettingPassword: '正在重置密碼...',
        passwordResetSuccess: '密碼重置成功！您現在可以使用新密碼登入。',
        passwordResetError: '密碼重置失敗。請重試或申請新的重置連結。',
        invalidResetToken: '無效的重置代碼。請申請新的重置連結。',
        expiredResetToken: '重置代碼已過期。請申請新的重置連結。',
        
        // Email Verification
        emailVerification: '電子郵件驗證',
        checkYourEmail: '請查看您的電子郵件',
        verificationSent: '我們已向 {email} 發送驗證連結。請點擊連結以驗證您的帳戶。',
        didntReceiveEmail: '沒有收到電子郵件？',
        resendVerification: '重新發送驗證',
        resending: '重新發送中...',
        verificationResent: '驗證電子郵件已重新發送！',
        verificationError: '發送驗證電子郵件失敗。請重試。',
        emailVerified: '電子郵件已驗證！',
        verificationSuccess: '您的電子郵件已成功驗證。您現在可以登入了。',
        verificationFailed: '電子郵件驗證失敗。連結可能無效或已過期。',
        alreadyVerified: '此電子郵件已經過驗證。',
        backToLogin: '返回登入',
        
        // User onboarding
        welcomeToApp: '歡迎來到 {appTitle}',
        selectPreferredLanguage: '請選擇您的偏好語言以繼續。',
        uiLanguage: '介面語言',
        youCanChangeLater: '您稍後可以在設定中更改。',
        next: '下一步',
        back: '上一步',
        previous: '上一步',
        step: '步驟',
        quickStartGuide: '快速入門指南',
        userProfileSetup: '告訴我們關於您',
        profileSetup: '此選填資訊有助於我們為您量身打造學習體驗。',
        
        // Guided Tutorial
        tutorialWelcomeTitle: '讓我們開始吧！',
        tutorialButtonSpotlightTitle: '互動教學',
        tutorialButtonSpotlightMessage: '想要完整的功能演示嗎？點擊教學按鈕，透過互動指南探索所有功能。如果您想直接開始，點擊下一步添加您的第一個主題！',
        tutorialStepIndicator: '步驟 1 / 1',
        tutorialWelcomeMessage: '準備好開始您的學習之旅了嗎？點擊上方藍色的「+」按鈕添加您的第一個主題，今天就開始學習新東西！',
        tutorialActionHint: '點擊這裡添加主題',
        tutorialActionDescription: '選擇您想學習的任何主題',
        tutorialSkip: '跳過教學',
        tutorialGetStarted: '知道了！',
        
        // Comprehensive Tutorial Mode
        tutorial: '教學',
        startTutorial: '開始教學',
        tutorialNext: '下一步',
        tutorialPrevious: '上一步',
        tutorialComplete: '完成',
        tutorialWelcomeContent: '歡迎來到您的個人化學習助手！本教學將引導您瞭解所有功能，幫助您充分利用系統的學習優勢。',
        tutorialWelcomeTip: '• 本教學大約需要1-2分鐘完成。',
        tutorialNavigationTip: '• 使用鍵盤上的 ← 和 → 箭頭鍵可以更方便地導航。',
        
        tutorialAddTopicContent: '點擊上方的「+」按鈕添加您的第一個學習主題。您可以添加任何感興趣的科目！',
        tutorialTip: '提示：',
        tutorialAddTopicTip: '使用通用主題，如機器學習、烹飪、動物或園藝。創建主題時還可以添加學習目標和詳細信息，以獲得更個人化的內容。',
        tutorialTopicCardContent: '主題卡片整理您的學習科目。注意狀態指示器：',
        tutorialUnreadIndicator: '藍點 - 未讀內容',
        tutorialUnreadDesc: '顯示您有新的知識點需要閱讀',
        tutorialReviewIndicator: '橙點 - 待複習',
        tutorialReviewDesc: '是時候複習概念以加強記憶了',
        tutorialFavoriteIndicator: '紅心 - 收藏主題',
        tutorialFavoriteDesc: '您收藏的主題，方便快速訪問',
        tutorialAddTipContent: '點擊知識點面板中的「+」按鈕，生成新的AI驅動學習概念。AI創建個人化內容可能需要約1分鐘時間。',
        tutorialManualTipBenefit: '專業提示：',
        tutorialManualTipDesc: '不想等待？您可以在主題設置中安排概念生成，這樣系統會自動提前為您準備好內容！',
        tutorialTipCardContent: '這些是您的學習概念。點擊任何知識點查看完整內容、開始AI對話或標記為複習。',
        tutorialInteractionHint: '快捷操作：',
        tutorialInteractionDesc: '右鍵點擊任何知識點，可以收藏、編輯或刪除。',
        tutorialTopicInteractionDesc: '右鍵點擊任何主題，可以收藏、編輯或刪除。',
        tutorialTopicGroupingContent: '使用自定義分組保持主題井然有序！將相關主題歸類，優化學習流程。',
        tutorialCreateGroup: '創建主題分組',
        tutorialCreateGroupDesc: '點擊「添加分組」創建自定義類別，如「工作項目」、「個人學習」或「興趣愛好」',
        tutorialDragTopics: '拖動主題到分組',
        tutorialDragTopicsDesc: '只需拖放主題卡片到任何分組即可整理它們',
        tutorialReorderWithinGroups: '組內重新排序',
        tutorialReorderWithinGroupsDesc: '在組內上下拖動主題，按您的偏好排列',
        tutorialReorderGroups: '重新排序整個分組',
        tutorialReorderGroupsDesc: '拖動組標題重新排序整個分組，優先安排您的學習領域',
        tutorialGroupingTip: '整理技巧：',
        tutorialGroupingTipDesc: '分組幫助您在學習過程中專注於特定領域，將相關主題保持在一起以獲得更好的上下文。',
        tutorialGroupingNote: '重要說明：',
        tutorialGroupingNoteDesc: '分組純粹是為了視覺組織，不會以任何方式影響AI生成的內容。',
        tutorialAITutorContent: '您的AI導師隨時準備幫助！提出問題、獲得解釋，深入探討任何主題。',
        tutorialReviewContent: '使用間隔重複定期複習幫助您長期保持知識。回答問題以強化您的學習。',
        tutorialCongrats: '恭喜！',
        tutorialCompleteContent: '您已完成教學！現在您已準備好開始學習之旅。',
        tutorialNextSteps: '下一步：',
        tutorialStep1: '添加您的第一個學習主題',
        tutorialStartLearningHint: '從添加您熱衷於學習的主題開始！',
        tutorialStep2: '每日回顧知識點和概念',
        tutorialStep3: '有問題時與AI導師聊天',
        tutorialStep4: '完成複習以加強記憶',
        tutorialConcepts: '已學習概念',
        tutorialExplanation: '解釋：',
        
        // Tutorial Dummy Content
        tutorialDummyTopicTitle: '學習JavaScript',
        tutorialDummyTopicDesc: '掌握JavaScript程式設計基礎',
        tutorialDummyNextReview: '明天上午9:00',
        tutorialDummyTipTitle: '理解變數',
        tutorialDummyTipContent: '變數是存儲資料值的容器。在JavaScript中，您可以使用let、const或var宣告變數。',
        
        tutorialDummyReviewQuestion: 'let和const之間有什麼區別？',
        tutorialDummyOption1: 'let可以重新賦值，const不能',
        tutorialDummyOption2: 'const可以重新賦值，let不能',
        tutorialDummyOption3: '兩者都可以重新賦值',
        tutorialDummyOption4: '兩者都不能重新賦值',
        tutorialDummyExplanation: '用let宣告的變數可以重新賦值，而const創建常量參照。',
        
        // Tutorial Knowledge Level Modal
        tutorialNewConceptModalIntro: '當您選擇未讀的學習概念時，您會看到這個知識水平選擇介面。這是個人化您學習體驗的關鍵步驟！',
        tutorialTooEasy: '太簡單',
        tutorialTooEasyDesc: '這個概念感覺很基礎，對您當前的知識沒有太多補充',
        tutorialAlreadyKnown: '已經掌握',
        tutorialAlreadyKnownDesc: '您很好地理解這個概念並對此感到自信',
        tutorialPartiallyKnow: '部分了解',
        tutorialPartiallyKnowDesc: '您有一些理解但需要加強知識',
        tutorialNewToMe: '全新內容',
        tutorialNewToMeDesc: '這是您第一次學習的全新資訊',
        tutorialTooHard: '太難了',
        tutorialTooHardDesc: '這個概念目前對您來說太有挑戰性，難以理解',
        tutorialKnowledgeTip: '為什麼這很重要：',
        tutorialKnowledgeTipDesc: '您的選擇幫助AI導師根據您的知識水平定制解釋和推薦內容。',
        tutorialKnowledgeLevelPanelIntro: '您可以隨時更改您的知識水平。',
        tutorialProgressTracking: '追蹤您的進度',
        tutorialProgressTrackingDesc: '隨著學習進展從「全新內容」更新為「部分了解」，幫助AI適應您的進步',
        tutorialFlexibleLearning: '靈活學習',
        tutorialFlexibleLearningDesc: '搞錯了？發現比預期容易？只需點擊即可隨時更新您的知識水平！',
        tutorialTipContentIntro: '這是您的學習內容顯示的地方。每個概念都包含AI生成的精煉知識，旨在幫助您掌握所選主題。所有內容都經過搜尋驗證，確保準確性和相關性。',
        tutorialQuickActionsIntro: '這些快捷操作按鈕幫助您發送預設提示的對話，節省您輸入常見問題的時間。',
        tutorialViewModesIntro: '切換不同的檢視模式，自定義您的學習體驗：',
        tutorialTipOnlyMode: '僅概念',
        tutorialTipOnlyModeDesc: '專注於閱讀和學習概念內容，沒有任何干擾。非常適合初次閱讀和理解。',
        tutorialFullViewMode: '完整檢視',
        tutorialFullViewModeDesc: '並排查看知識點內容和AI對話。非常適合主動學習，同時閱讀和提問。',
        tutorialChatOnlyMode: '僅聊天',
        tutorialChatOnlyModeDesc: '最大化對話空間，與AI導師進行深入討論。非常適合擴展問答和詳細解釋。',
        tutorialViewModesProTip: '專業提示：',
        tutorialViewModesProTipDesc: '當您開始對話時，檢視會自動切換到完整檢視。您可以隨時更改以匹配您的學習偏好！',
        tutorialTipActionsIntro: '使用這些強大的操作管理您的學習概念：',
        tutorialHideAction: '隱藏',
        tutorialHideActionDesc: '暫時隱藏您已掌握或想暫時跳過的概念',
        tutorialFavoriteAction: '收藏',
        tutorialFavoriteActionDesc: '標記重要概念以便快速訪問和優先複習',
        tutorialScheduleReview: '安排複習',
        tutorialScheduleReviewDesc: '設置提醒使用間隔重複複習概念，以獲得更好的記憶效果',
        tutorialProTip: '專業提示：',
        tutorialTipActionsProTip: '使用這些操作自定義您的學習流程。隱藏您熟悉的概念，收藏有挑戰性的概念，並安排複習以獲得最佳記憶效果。',
        tutorialReviewTrackingIntro: '基於艾賓浩斯曲線，生成複習題幫助您長期保持知識：',
        tutorialDateLearned: '學習日期',
        tutorialDateLearnedDesc: '這是您首次為概念選擇知識水平的日期。',
        tutorialReviewButton: '複習題按鈕',
        tutorialReviewButtonDesc: '點擊測試您的知識與多選題。有新複習題時會發出橙色光芒。',
        tutorialDaysTillReview: '距下次複習天數',
        tutorialDaysTillReviewDesc: '顯示到下次複習的倒計時。',
        tutorialReviewModalIntro: '通過複習題測試您的知識！這個強大的學習工具通過主動回憶幫助強化概念：',
        tutorialAnswerQuestions: '回答複習題',
        tutorialAnswerQuestionsDesc: '多選題測試您對關鍵概念的理解。選擇您的答案和信心水平。',
        tutorialDeepDiveChat: '深入對話',
        tutorialDeepDiveChatDesc: '回答後，與AI導師開始對話，更深入地探索主題並澄清任何困惑。',
        tutorialActiveRecall: '主動回憶提示：',
        tutorialActiveRecallDesc: '在查看選項之前嘗試回憶答案。這能加強記憶路徑並改善長期記憶！',
        tutorialSpacedRepetition: '最佳實踐：',
        tutorialSpacedRepetitionDesc: '為了獲得最有效的學習成果，在概念到期複習時立即複習。這個時機優化了記憶保持和長期回憶。',
        tutorialScrollTip: '提示：',
        tutorialScrollTipDesc: '內容區域可滾動。如果包含更多資訊，您可以向下滾動閱讀整個概念。',
        tutorialDummyTopic1Name: '健康與養生',
        tutorialDummyTopic1Desc: '獲得更健康、更有活力生活的必要知識',
        tutorialDummyTopic2Name: '烹飪',
        tutorialDummyTopic2Desc: '掌握基本烹飪技巧和食譜',
        tutorialDummyTopic3Name: '園藝',
        tutorialDummyTopic3Desc: '學習如何種植自己的植物和蔬菜',
        tutorialDummyTip1Title: '水分補充的力量',
        tutorialDummyTip1Content: '適當的水分補充對您身體的每個系統都至關重要。雖然「每天8杯水」的規則很流行，但您的實際需求取決於活動水平、氣候和體型。\n\n關鍵事實：\n• 您的身體有60%是水\n• 即使輕微脫水（2%）也會影響注意力和能量\n• 最佳指標：淡黃色尿液意味著良好的水分補充\n• 在感到口渴之前喝水 - 口渴意味著您已經輕微脫水\n\n最佳時機：醒來後立即喝水，全天飲用，睡前2小時減少攝入。',
        tutorialDummyTip2Title: '理解您的能量水平',
        tutorialDummyTip2Content: '您的能量在一天中自然波動，遵循您的晝夜節律。了解這些模式幫助您優化生產力和福祉。\n\n自然能量高峰：\n• 上午（9-11點）：最適合複雜任務\n• 下午早期（13-15點）：自然低谷，適合常規工作\n• 下午晚期（15-18點）：第二個高峰，適合會議\n\n能量提升器：\n• 一致的睡眠時間表（7-9小時）\n• 營養均衡的定期餐食\n• 每90分鐘短暫散步\n• 全天保持水分補充',
        tutorialDummyTip3Title: '刀工：基礎',
        tutorialDummyTip3Content: '正確的刀工對高效安全的烹飪至關重要。掌握這些基本切法：\n\n• 細絲切：薄火柴棍切法\n• 切丁：均勻的立方體形狀\n• 香草卷切：卷起並切片香草\n• 細末切：非常細的切丁\n\n始終保持刀具鋒利並使用穩定的切菜板。',
        
        // Tutorial Conversations
        tutorialDummyBotInit1: '很高興看到您在探索水分補充！這確實是我們身體功能的基礎。\n\n您想深入了解哪個方面 - 每日攝入指南、時機還是識別脫水跡象？',
        tutorialDummyQuestion1: '我如何判斷自己是真的脫水還是只是口渴？',
        tutorialDummyAnswer1: '好區別！口渴實際上是一個較晚的指標 - 當您感到口渴時，您已經輕微脫水了。\n\n更好的指標包括：\n• **尿液顏色**：淡黃色意味著水分充足；深琥珀色表示脫水\n• **能量下降**：無法解釋的疲勞，特別是下午\n• **皮膚彈性**：捏一下手上的皮膚 - 應該立即彈回\n\n關鍵是通過全天持續喝水來預防脫水，而不是等待口渴。',
        
        tutorialDummyBotInit2: '很好，您在深入了解能量水平！理解您的自然節律是提高生產力的關鍵。\n\n您想探索什麼 - 管理能量低谷、優化高峰時段還是提高整體耐力？',
        tutorialDummyQuestion2: '為什麼即使睡眠充足，我總是在下午2-3點崩潰？',
        tutorialDummyAnswer2: '那個下午低谷實際上是自然的 - 這是您晝夜節律的一部分，不一定是出了問題的跡象。\n\n您的體溫在下午1-3點之間自然下降，引發睏倦。要最小化崩潰：\n• **吃較清淡的午餐** - 重餐會加重睏倦\n• **短暫散步** - 即使5-10分鐘也有幫助\n• **保持水分** - 脫水會加劇疲勞\n• **考慮20分鐘的能量小睡** 如果可能\n\n通過在這個時間安排常規任務，並將複雜工作留給上午或下午晚期的高峰時段，來配合這個自然低谷。',
        
        // Tutorial Review Questions
        tutorialDummyReviewQ2: '什麼時候喝一大杯水對新陳代謝最好？',
        tutorialDummyReviewChoice2A: '睡前',
        tutorialDummyReviewChoice2B: '用餐時',
        tutorialDummyReviewChoice2C: '早晨第一件事',
        tutorialDummyReviewChoice2D: '僅在運動時',
        tutorialDummyReviewExp2A: '睡前大量飲水會因上廁所而擾亂睡眠。',
        tutorialDummyReviewExp2B: '用餐時大量飲水會稀釋消化酸。小口飲用更好。',
        tutorialDummyReviewExp2C: '正確！早晨補水啟動新陳代謝並幫助沖洗夜間毒素。',
        tutorialDummyReviewExp2D: '雖然運動時很重要，但這不是促進新陳代謝的最佳時機。',
        tutorialChatStarter2A: '睡前補水很棘手！讓我解釋一下水分和睡眠之間的平衡...',
        tutorialChatStarter2B: '用餐時補水很重要但時機很重要。讓我解釋為什麼...',
        tutorialChatStarter2C: '完美！想了解最大化效益的早晨補水程序嗎？',
        tutorialChatStarter2D: '運動補水是關鍵但不同。讓我解釋時機...',
        
        // Tutorial Scheduled Generation Modal
        tutorialScheduledGenerationIntro: '使用定時概念生成自動化您的學習！設置後無需操心 - 新概念將在您偏好的時間自動生成。',
        tutorialSetSchedule: '設置您的計劃',
        tutorialSetScheduleDesc: '選擇新概念應該何時以及多久生成一次 - 每日、每週或每月在您偏好的時間。',
        tutorialTimezoneAware: '時區感知',
        tutorialTimezoneAwareDesc: '系統自動調整到您的時區，確保概念在您期望的時間準備好。',
        tutorialSmartSkipping: '智慧跳過',
        tutorialSmartSkippingDesc: '啟用「如果存在未讀概念則跳過」以防止自己被太多未讀概念淹沒。',
        
        // Tutorial Filter Functions
        tutorialFilterFunctionsIntro: '使用智能篩選保持條理！通過強大的篩選選項快速找到對您最重要的主題和概念。',
        tutorialFavoriteFilter: '收藏篩選',
        tutorialFavoriteFilterDesc: '點擊心形圖標只顯示您收藏的主題或概念。非常適合專注於您最關心的內容。',
        tutorialReviewDueFilter: '待複習篩選',
        tutorialReviewDueFilterDesc: '點擊時鐘圖標只顯示需要複習的項目。掌握您的間隔重複計劃。',
        tutorialAdvancedFilter: '高級篩選',
        tutorialAdvancedFilterDesc: '點擊漏斗圖標訪問高級篩選選項，如狀態（未讀、已標記為已知等）和定時生成設置。',
        tutorialFilterProTip: '專業提示',
        tutorialCombineFiltersTip: '您可以組合多個篩選器創建強大的視圖。例如，只顯示需要複習的收藏主題！',
        
        // Tutorial AI Learning Summary Modal
        tutorialAILearningSummaryIntro: '獲得您學習旅程的個人化見解！AI分析您的互動以了解您的知識概況、學習模式和優化機會。',
        tutorialKnowledgeProfile: '知識概況',
        tutorialKnowledgeProfileDesc: '基於您與概念和對話的參與了解您的優勢和知識差距。',
        tutorialLearningPatterns: '學習模式',
        tutorialLearningPatternsDesc: '發現您如何最好地學習 - 是通過例子、理論還是基於實踐的方法。',
        tutorialOptimizationInsights: '優化見解',
        tutorialOptimizationInsightsDesc: '獲得可行的建議以提高您的學習效果和記憶。',
        tutorialRequirementNote: '注意：',
        tutorialRequirementNoteDesc: '您需要至少閱讀5個概念才能生成您的第一個AI學習總結。這確保有足夠的資料產生有意義的見解。',
        tutorialRightClickTopicTip: '您可以隨時右鍵點擊任何主題卡片並選擇「編輯」來訪問這些設置。',
        
        // Tutorial Group Names
        tutorialDummyGroup1Name: '健康與生活方式',
        tutorialDummyGroup2Name: '興趣愛好',
        
        tutorialFeatureAI: 'AI驅動學習',
        tutorialFeatureSpaced: '間隔重複',
        tutorialFeatureTutor: '個人AI導師',
        tutorialFeatureProgress: '進度追蹤',
        tutorialDailyTipContent: '每天，您都會收到根據您的學習節奏定製的小型課程。這些提示旨在快速、有效地學習。',
        addYourFirstTopic: '添加您的第一個主題',
        tutorialDummyUserQuestion: '您能解釋一下函式是如何運作的嗎？',
        tutorialDummyAIResponse: '函式是執行特定任務的可重用程式碼塊。把它們想像成可以多次使用的食譜...',
        
        // Header
        appTitle: '知識驛站',
        welcome: '歡迎',
        
        // Profile
        name: '姓名',
        enterYourName: '輸入您的姓名',
        nameHelp: '您的姓名將用於個人化互動',
        
        // Navigation
        topics: '主題',
        settings: '設定',
        profile: '個人檔案',
        logout: '登出',
        feedback: '意見回饋',
        
        // Add Topic Modal
        filterFavoriteTopicsTooltip: '只顯示收藏的主題',
        filterReviewDueTopicsTooltip: '顯示有待複習概念的主題',
        filterTopicsTooltip: '篩選主題',
        noTopicsToFilter: '請先建立主題以使用篩選功能',
        editTopic: '編輯主題',
        cancel: '取消',
        topicName: '主題名稱',
        confirmChanges: '確認變更',
        discardChanges: '放棄變更',
        unsavedChangesTitle: '未儲存的變更',
        unsavedChangesMessage: '您有未儲存的變更。您想儲存它們嗎？',
        saveChanges: '儲存變更',
        continue: '繼續',
        customSchedule: '自訂排程',
        generateConceptsAt: '在以下時間產生概念：',
        hour: '小時',
        minute: '分鐘',
        am: '上午',
        pm: '下午',
        
        // Pagination  
        showingTopics: '顯示 {start}-{end} 個主題，共 {total} 個',
        noTopicsFound: '未找到主題',
        
        // Topic List
        noTopicsMessage: '您還沒有任何主題。點擊右上角的「+」按鈕來新增您的第一個主題！',
        showingFilteredTopics: '顯示 {count} 個符合篩選條件的主題',
        clearFilters: '清除篩選器',
        
        // Concept generation
        conceptGenerationInProgress: '概念產生進行中...',
        conceptGenerationQueued: '概念產生已排隊...',
        conceptGenerationFailed: '概念產生失敗',
        conceptGenerationCompleted: '概念產生完成',
        retryConceptGeneration: '重試概念產生',
        
        // Settings
        settingsTitle: '設定',
        general: '一般',
        aiModel: 'AI 模型',
        notifications: '通知',
        privacy: '隱私',
        darkMode: '深色模式',
        language: '語言',
        timeFormat: '時間格式',
        contentLanguage: '內容語言',
        english: '英語',
        chinese: '中文',
        japanese: '日語',
        clearData: '清除資料',
        clearDataWarning: '這將刪除您的所有主題和進度。資料刪除後無法復原。',
        clearDataConfirm: '是的，刪除所有資料',
        soundNotifications: '聲音通知',
        newConceptSound: '新概念聲音',
        enableSounds: '啟用聲音',
        testSound: '測試聲音',
        volume: '音量',
        playSoundWhenNewConceptAdded: '新增新概念時播放聲音',
        requestNotificationPermission: '請求通知權限',
        notificationPermissionGranted: '已授予通知權限',
        notificationPermissionDenied: '通知權限被拒絕',
        notificationPermissionDefault: '通知權限預設',
        usageAnalytics: '使用分析',
        showUsageAnalytics: '顯示使用分析',
        hideUsageAnalytics: '隱藏使用分析',
        totalTokensUsed: '已使用的總代幣數',
        totalCost: '總成本',
        costBreakdown: '成本明細',
        inputTokens: '輸入代幣',
        outputTokens: '輸出代幣',
        usageByModel: '按模型使用情況',
        usageByDay: '按日使用情況',
        usageByMonth: '按月使用情況',
        refreshUsage: '重新整理使用情況',
        costCalculationNote: '成本計算基於官方API定價',
        
        // Settings additional keys
        currentProfile: '當前檔案',
        age: '年齡',
        education: '教育',
        notProvided: '未提供',
        yearsOld: '歲',
        saving: '儲存中...',
        activeSessions: '活躍連線',
        usage: '使用情況',
        thankYouTestUser: '感謝您成為測試使用者！',
        thankYouMessage: '我們感謝您幫助測試 {appTitle}。您的意見非常寶貴。',
        testUserBenefits: '您的測試使用者優惠',
        conversations: '對話',
        unlimited: '無限制',
        conceptGeneration: '概念產生',
        aiLearningSummary: 'AI學習總結',
        reviewScheduling: '複習排程',
        loadingSessions: '載入連線中...',
        activeSessionsInfo: '管理您的活躍連線',
        activeSessionsDescription: '檢視和管理您當前登入的所有裝置。您可以從特定裝置或所有裝置登出。',
        noActiveSessions: '未找到活躍連線',
        unknownDevice: '未知裝置',
        currentSession: '當前連線',
        ipAddress: 'IP位址',
        unknown: '未知',
        lastActive: '最後活躍',
        logoutThisDevice: '從此裝置登出',
        logoutAllDevices: '從所有裝置登出',
        confirmLogoutSession: '您確定要從此裝置登出嗎？',
        confirmLogoutAllSessions: '您確定要從所有裝置登出嗎？您需要重新登入。',
        close: '關閉',
        
        // AI Model Settings
        conversationModel: '對話模型',
        selectConversationModel: '選擇對話的AI模型',
        selectAIProvider: '選擇 AI 提供商',
        modelQualityVsSpeed: '品質與速度權衡',
        modelPro: '高品質',
        modelProDescription: '最佳品質的深度推理回應',
        modelFlash: '平衡版',
        modelFlashDescription: '良好品質與更快回應',
        modelLite: '快速版',
        modelLiteDescription: '簡單查詢的快速回應',
        recommended: '推薦',
        bestQuality: '最佳品質',
        balanced: '平衡',
        fastest: '最快',
        quality: '品質',
        speed: '速度',
        
        // AI Learning Summary
        knowledgeProfile: '知識概況',
        learningPatterns: '學習模式',
        optimizationInsights: '優化建議',
        
        // Slow Chat Notification
        slowChatNotification: '等待回應時間過長？嘗試切換到更快的模型以獲得更快的回覆',
        changeModel: '更換模型',
        dontShowAgain: '不再顯示',
        
        sessionInvalidatedByAnotherLogin: '您已被登出，因為您從另一台裝置登入。',
        favoriteTopic: '收藏主題',
        unfavoriteTopic: '取消收藏主題',
        favoritedTopic: '已收藏主題',
        showFavoriteTopicsOnly: '只顯示收藏主題',
        showReviewDueTopicsOnly: '只顯示待複習主題',
        showScheduledTopicsOnly: '只顯示自動產生主題',
        showUnscheduledTopicsOnly: '只顯示手動主題',
        showAllTopics: '顯示所有主題',
        daily: '每日',
        weekly: '每週',
        monthly: '每月',
        everyNDays: '每{count}天',
        everyNWeeks: '每{count}週',
        everyNMonths: '每{count}月',
        topicSingular: '主題',
        topicPlural: '主題',
        ungrouped: '未分組',
        createGroup: '建立分組',
        groupName: '分組名稱',
        rename: '重新命名',
        deleteGroupTitle: '刪除分組',
        deleteGroupMessage: '您確定要刪除此分組嗎？主題將被移至未分組。',
        
        // Add Topic Modal specific translations
        learningGoalQuestion: '您學習此項目的目的是什麼？',
        contentLanguageQuestion: '內容應以哪種語言解釋？',
        rateYourKnowledge: '評估您的當前知識水平',
        example: '範例',
        
        // Scheduling translations
        enableAutomaticTipGeneration: '啟用自動提示產生',
        generateTipsAt: '在以下時間產生提示：',
        generateNewTipsEvery: '每隔以下時間產生新提示：',
        hours: '小時',
        days: '天',
        weeks: '週',
        months: '月',
        timezone: '時區',
        schedulingNote: '將使用您選擇的時區',
        scheduledSkipIfUnread: '如果該主題有未讀的知識點，則不自動產生新知識點',
        
        // Placeholders
        topicNamePlaceholder: '量子計算',
        selectKnowledgeLevel: '拖動滑桿來評估您的當前知識水平',
        selectLearningGoal: '請選擇您的學習目標',
        completeAllFieldsToStart: '請完成主題名稱、學習目標和知識水平設定以開始學習',
        
        // Topic Examples
        topicExamples: [
            {
                name: '量子計算',
                description: '我是一名物理系學生，在線性代數和經典力學方面有扎實的基礎。我想為我的研究專案理解量子演算法。我已經學習了量子力學基礎知識，能夠處理複數運算。我特別對量子密碼學和量子機器學習應用感興趣。'
            },
            {
                name: '日語會話',
                description: '我是日語初學者，想學習東京旅行用的日常俚語。我已經學會了基本問候語，能讀平假名。我想專注於年輕人日常對話中使用的短語。我特別對食物相關詞彙和問路感興趣。'
            },
            {
                name: '機器學習',
                description: '我是一名軟體開發者，有Python和統計學經驗。我想為公司電商平台建置推薦系統。我已經參加了監督學習線上課程，理解基本演算法。我特別對協同過濾和深度學習方法感興趣。'
            },
            {
                name: '旅行西班牙語',
                description: '我計劃在南美洲背包旅行，需要實用的西班牙語技能。我從高中就知道一些基本詞彙，但多年沒有練習了。我想專注於點餐、預訂住宿和交朋友的會話技能。我特別對地區差異和文化背景感興趣。'
            },
            {
                name: '區塊鏈開發',
                description: '我是一名網頁開發者，有JavaScript和智慧合約經驗。我想為創業專案建置去中心化應用程式。我已經學習了Solidity基礎，理解以太坊的工作原理。我特別對DeFi協議和NFT市場感興趣。'
            },
            {
                name: '烹飪技巧',
                description: '我是一名家庭廚師，想為晚宴提高烹飪技能。我能按照食譜做菜，但想了解烹飪方法背後的科學原理。我已經掌握了基本刀工，能製作簡單醬汁。我特別對法國技術和分子美食學感興趣。'
            },
            {
                name: '資料科學',
                description: '我是一名商業分析師，想轉向資料科學。我有很強的Excel技能和基本SQL知識。我想學習Python進行資料分析和視覺化。我特別對預測建模和商業智慧應用感興趣。'
            },
            {
                name: '吉他理論',
                description: '我是一名中級吉他手，能彈和弦和簡單歌曲。我想理解音樂理論來創作自己的歌曲和即興演奏。我已經學習了基本音階，能讀吉他譜。我特別對爵士理論和指彈技巧感興趣。'
            },
            {
                name: '攝影',
                description: '我是一名攝影愛好者，有一台單眼相機。我想提高風景攝影的構圖和用光技巧。我理解基本相機設定，但想掌握手動模式。我特別對長時間曝光技術和後製處理感興趣。'
            },
            {
                name: '網頁開發',
                description: '我是一名初學者程式設計師，想建置現代網頁應用程式。我從線上教學中獲得了基本的HTML和CSS知識。我想學習JavaScript框架和後端開發。我特別對React和Node.js全端開發感興趣。'
            },
            {
                name: '財務規劃',
                description: '我是一名剛畢業的學生，想學習個人理財和投資策略。我對預算有基本了解，但想學習股票、債券和退休規劃。我特別對指數基金和稅收效率投資感興趣。'
            },
            {
                name: '公眾演講',
                description: '我是一名專業人士，需要提高工作會議和演講的演講技巧。我能在小組中發言，但在大觀眾面前會緊張。我想學習吸引人的表達和處理問題的技巧。我特別對講故事和說服性演講感興趣。'
            },
            {
                name: '瑜伽練習',
                description: '我是一名初學者，想為減壓和靈活性發展持續的瑜伽練習。我試過幾節課，但想了解正確的對齊和呼吸技巧。我特別對流瑜伽和冥想練習感興趣。'
            },
            {
                name: '遊戲開發',
                description: '我是一名程式設計師，想作為愛好專案建立2D遊戲。我有C++和基本圖形程式設計經驗。我想學習遊戲引擎和設計原則。我特別對Unity和行動遊戲開發感興趣。'
            },
            {
                name: '創意寫作',
                description: '我是一名有抱負的作家，想提高短篇小說的講故事技巧。我喜歡閱讀，但在角色發展和情節結構方面有困難。我想學習創作引人入勝敘事的技巧。我特別對科幻和奇幻類型感興趣。'
            },
            {
                name: '數位行銷',
                description: '我是一名小企業主，想提高線上存在感和客戶獲取。我有基本的社群媒體知識，但想學習SEO和付費廣告。我特別對內容行銷和電子郵件活動感興趣。'
            },
            {
                name: '冥想',
                description: '我是一名忙碌的專業人士，想為心理清晰和壓力管理發展冥想練習。我試過正念應用程式，但想了解不同的技巧和傳統。我特別對超驗冥想和引導練習感興趣。'
            },
            {
                name: '家庭釀酒',
                description: '我是一名啤酒愛好者，想開始在家釀造自己的精釀啤酒。我理解基本釀造過程，但想學習不同風格和技術。我特別對IPA和酸啤酒感興趣。'
            },
            {
                name: '天文學',
                description: '我是一名科學愛好者，想了解宇宙和天體。我有一台基本望遠鏡，能識別主要星座。我想理解天體物理學概念和觀測技術。我特別對系外行星和深空天體感興趣。'
            },
            {
                name: '國際象棋策略',
                description: '我是一名中級國際象棋棋手，想提高戰略思維和殘局技能。我知道基本開局，能計算簡單戰術。我想學習位置棋和進階概念。我特別對中局規劃和兵結構感興趣。'
            }
        ],
        
        // Concepts
        concepts: '概念',
        addConceptTooltip: '為此主題產生新概念',
        filterFavoriteConceptsTooltip: '只顯示收藏的概念',
        filterReviewDueConceptsTooltip: '顯示待複習的概念',
        filterConceptsTooltip: '篩選概念',
        selectTopicToFilterConcepts: '請先選擇一個主題以篩選概念',
        filterByStatus: '按狀態篩選',
        showFavoritesOnly: '只顯示收藏',
        showAllConcepts: '顯示所有概念',
        noConceptsMatchFilter: '沒有概念符合您的當前篩選設定。',
        tooEasy: '太簡單',
        alreadyKnown: '已經知道',
        partiallyKnow: '部分知道',
        newToMe: '對我來說是新的',
        tooHard: '太難',
        notWhatLookingFor: '不是我要找的？',
        showHiddenTips: '顯示隱藏提示',
        hideThisTip: '隱藏',
        hiddenTip: '隱藏提示',
        showThisTipAgain: '再次顯示此提示',
        favoriteThisTip: '收藏',
        unfavoriteThisTip: '取消收藏',
        favoritedTip: '已收藏提示',
        scheduleReview: '安排複習',
        unscheduleReview: '取消複習安排',
        enableReviewSchedule: '為此概念啟用複習計劃',
        resetReviewProgress: '重置複習進度',
        nextReviewIn: '下次複習在',
        noReviewQuestionsYet: '暫無可用的複習問題',
        reviewDays: '天後',
        day: '天後',
        today: '今天',
        yesterday: '昨天',
        daysAgo: '{count}天前',
        weeksAgo: '{count}週前',
        monthsAgo: '{count}個月前',
        yearsAgo: '{count}年前',
        reviewDue: '複習到期',
        reviewOverdue: '複習逾期',
        reviewSequenceCompleted: '複習序列完成',
        reviewProgressReset: '複習進度重置',
        reviewProgressResetMessage: '這將：\n• 重置您的複習進度到開始\n• 關閉複習計劃\n• 刪除所有複習問題和答案\n• 保留您的學習日期不變\n\n您可以稍後重新啟用複習。\n\n您確定嗎？',
        
        // Main Content
        currentLearningConcept: '當前學習概念',
        selectTopicConcept: '選擇一個主題和概念開始。',
        startDeepDive: '開始深度對話',
        notLookingFor: '不是您要找的？',
        tryEditingTopic: '嘗試編輯主題以新增更多關於您目標的描述。',
        selectConcept: '選擇一個概念開始。',
        askQuestion: '提問...',
        
        // Three-State Feature
        tipOnly: '僅概念',
        fullView: '完整檢視',
        chatOnly: '僅聊天',
        clickStartDeepDive: '點擊「開始深度對話」開始',
        startConversationFirst: '先開始對話再使用此檢視',
        botGeneratingMessage: '機器人正在產生回覆，請稍候...',
        
        // Quick Response Buttons
        quickActions: '快速操作',
        stillDontUnderstand: '仍不明白',
        testMyKnowledge: '考考我',
        giveAnExample: '給個例子',
        explainDifferently: '換個說法',
        
        // Quick Response Messages (actual messages sent to conversation)
        stillDontUnderstandMessage: '我仍然不明白這個概念。你能用更簡單的方式解釋一下嗎？',
        testMyKnowledgeMessage: '你能測試我對這個概念的理解嗎？問我一些問題，看看我是否真的理解了。',
        giveAnExampleMessage: '你能給我一個具體的例子，說明這個概念在實際中的應用嗎？',
        explainDifferentlyMessage: '你能用不同的方式解釋這個概念嗎？也許使用不同的類比或方法？',
        
        // Concept Details
        conceptDetails: '概念詳情',
        myKnowledgeLevel: '我的知識水平',
        dateIntroduced: '引入日期',
        dateLearned: '學習日期',
        notLearnedYet: '尚未學習',
        unknownStatus: '未知',
        conversation: '對話',
        startNewDeepDive: '開始新的深度對話',
        reviewQuestions: '複習問題',
        tipActions: '操作',
        deleteConcept: '刪除概念',
        selectConceptDetails: '選擇一個概念檢視詳情。',
        
        // Review Questions Modal
        reviewQuestion: '複習問題',
        deepDiveChat: '深度對話',
        howConfident: '您有多自信？',
        totallyGuessing: '完全猜測',
        aBitUnsure: '有點不確定',
        confident: '有信心',
        submitAnswer: '提交答案',
        tryAgain: '重試',
        correctWellDone: '正確！做得好！',
        notQuiteRight: '不太對',
        reviewLocked: '複習已鎖定',
        reviewWillUnlock: '此複習將在第{day}天解鎖',
        startConversation: '開始對話',
        startNewConversation: '開始新對話',
        answerQuestionFirst: '請先回答複習問題再開始對話。',
        clearChatHistory: '清除聊天記錄？',
        clearChatMessage: '這將清除當前複習的所有聊天訊息。資料刪除後無法復原。',
        clearAndStartNew: '清除並開始新的',
        askQuestionsToDeepen: '針對此複習提問，加深您的理解！',
        reviewQuestionForDay: '第{day}天複習問題',
        dayNumber: '第{day}天',
        reviewDay: '複習{day}',
        
        // Modals
        confirm: '確認',
        delete: '刪除',
        overwriteConversation: '覆蓋對話？',
        overwriteMessage: '開始新的深度對話將刪除此概念的當前對話。您要繼續嗎？',
        eraseAndStartNew: '刪除並開始新的',
        continueLearning: '繼續學習？',
        knownMessage: '您知道這個概念！您想請求一個新的嗎？',
        easyMessage: '這個概念太簡單了。您想請求一個更進階的嗎？',
        requestNewConcept: '請求新概念',
        notNow: '暫時不要',
        hideForNow: '隱藏',
        skipConversation: '跳過對話',
        continueExploring: '繼續探索',
        conceptTooHard: '概念太難',
        hardMessage: '這個概念似乎很有挑戰性。您想請求一個更簡單的，還是暫時跳過？',
        requestEasier: '請求更簡單的',
        skipForNow: '暫時跳過',
        letsLearnMore: '讓我們學習更多！',
        learnMoreMessage: '您想開始關於這個概念的深度對話嗎？',
        deleteConfirmTitle: '刪除主題',
        deleteConfirmMessage: '您確定要刪除「{title}」嗎？資料刪除後無法復原。',
        deleteConfirmStart: '您確定要刪除主題',
        deleteConfirmEnd: '嗎？資料刪除後無法復原。',
        notLookingForTitle: '不是您要找的？',
        notLookingForMessage: '這個概念不符合您要學習的。您想請求一個不同的概念嗎？',
        requestDifferentConcept: '請求不同的概念',
        selectKnowledgeLevelTitle: '選擇知識水平',
        selectKnowledgeLevelMessage: '在開始對話之前，請為此概念選擇知識水平。',
        selectLevelToContinue: '選擇一個級別繼續',
        
        // User Profile
        userProfile: '使用者檔案',
        ageRange: '年齡範圍',
        selectAgeRange: '選擇您的年齡範圍',
        educationLevel: '最高教育水平',
        selectEducation: '選擇您的最高教育水平',
        selectUILanguage: '選擇您偏好的介面語言',
        saveProfile: '儲存檔案',
        optional: '可選',
        preferNotToSay: '我選擇不說',
        ageRangeHelp: '這有助於我們根據您的年齡段調整內容。如果您願意，可以跳過此項。',
        educationLevelHelp: '這有助於我們調整解釋的複雜度。如果您仍在學習，請包含您當前的水平。',
        
        // Age ranges
        ageRanges: {
            'under20': '20歲以下',
            '20-30': '20-30歲',
            '31-40': '31-40歲',
            '41-50': '41-50歲',
            '51-60': '51-60歲',
            'over60': '60歲以上'
        },
        
        // Education levels
        educationLevels: {
            'high_school': '高中或在讀',
            'some_college': '大學或在讀',
            'associates': '副學士學位或在讀',
            'bachelors': '學士學位或在讀',
            'masters': '碩士學位或在讀',
            'doctorate': '博士學位或在讀',
            'professional': '專業學位或在讀',
            'other': '其他'
        },
        
        // Loading
        loadingData: '載入資料...',
        loadingJourney: '載入您的學習之旅...',
        generatingConcept: '產生您的第一個概念...',
        updatingTopic: '更新主題...',
        deletingTopic: '刪除主題...',
        requestsPending: '個請求待處理',
        
        // Errors
        errorAddingTopic: '新增主題時出錯：',
        errorUpdatingTopic: '更新主題時出錯：',
        errorDeletingTopic: '刪除主題時出錯：',
        errorGeneratingTip: '產生新提示時出錯：',
        errorDeletingTip: '刪除提示時出錯：',
        failedToUpdateStatus: '更新狀態失敗。',
        failedToStartConversation: '開始對話失敗',
        failedToSendMessage: '發送訊息失敗：',
        rateLimitError: '請求太頻繁。請稍等片刻後再試。',
        rateLimitExceeded: '超出速率限制。請稍後再試。',
        retryingRequest: '將在 {seconds} 秒後重試請求...',
        networkError: '抱歉，我無法連接到AI。',
        requestTimeout: '請求時間過長。AI服務可能繁忙。請重試。',
        serverError: '伺服器錯誤。請稍後重試。',
        pleaseSelectKnowledgeLevel: '在開始對話之前，請為此概念選擇知識水平。',
        retry: '重試',
        retrying: '正在重試...',
        previousMessageFailed: '未能獲取您上一條訊息的回覆。您可以重試或發送新訊息。',
        
        // Session Takeover Modal
        activeSessionDetected: '檢測到活動工作階段',
        sessionTakeoverWarning: '您已經在另一個分頁或視窗中打開了此應用程式。開啟多個工作階段可能會導致資料衝突。',
        sessionTakeoverContinueWarning: '如果您繼續，其他工作階段將自動登出。',
        continueAnyway: '仍然繼續',
        
        // Connection status
        connected: '已連接到伺服器',
        connectionLost: '連線中斷。正在嘗試重新連線...',
        reconnecting: '重新連線中... ({attempt}/{maxAttempts})',
        serverUpdating: '伺服器正在更新。請稍候...',
        connectionTimeout: '連線逾時。重新連線中...',
        unableToConnect: '無法連接到伺服器。請重新整理頁面。',
        serverUpdateMessage: '伺服器正在更新。您的工作階段將很快恢復。',
        
        // Group management
        dropTopicHere: '將主題拖放到這裡',
        noTopicsInGroup: '該組中沒有主題',
        failedToCreateGroup: '建立組失敗',
        failedToUpdateGroup: '更新組失敗',
        failedToDeleteGroup: '刪除組失敗',
        reorderingWhileFilteringNotSupported: '篩選時暫不支援重新排序',
        
        // Review and testing
        noTopicsHaveReviewDue: '沒有需要複習的主題',
        testMe: '測試我',
        different: '不同',
        confused: '困惑',
        devGenerateReview: '僅限開發：為特定日期產生複習問題',
        
        // Responsive warning modal
        responsiveWarningTitle: '最佳桌面瀏覽體驗',
        responsiveWarningMessage: '此應用程式專為在電腦上最大化的瀏覽器視窗中使用而設計。在手機或平板電腦上可能無法最佳顯示。',
        understood: '明白了',
        dontShowResponsiveWarning: '不再顯示',
        
        // Rate concept modal
        pleaseRateThisConcept: '請評價這個概念',
        beforeStartingDeepDive: '在開始深入學習前，請評價您對「{conceptTitle}」的了解程度。',
        howDoesConceptFeel: '這個概念對您來說感覺如何？',
        
        loading: '載入中...',
        
        // Error messages
        userNotAuthenticated: '使用者未通過身分驗證。',
        failedToDeleteTip: '刪除提示失敗',
        failedToDeleteTopic: '刪除主題失敗',
        networkResponseNotOk: '網路回應異常',
        noTopicSelected: '未選擇主題。',
        authenticationRequired: '產生總結需要身分驗證。',
        failedToLoadReviewQuestions: '載入複習問題失敗',
        failedToGenerateAIResponse: '產生AI回應失敗',
        tokenCountsInvalid: 'Token數必須是數字',
        tokenCountsNegative: 'Token數不能為負數',
        tokenCountsNotFinite: 'Token數必須是有限數字',
        tokenCountsExceedLimit: 'Token數超過合理限制（1億tokens）',
        noJsonInAIResponse: 'AI回應中未找到JSON區塊。',
        geminiApiKeyNotSet: '未設定GEMINI_API_KEY',
        useTimezoneError: '必須在TimezoneProvider中使用useTimezone',
        
        // Placeholders
        askAboutReviewQuestion: '詢問有關此複習問題的內容...',
        
        // Knowledge levels
        knowledgeLevels: {
            '0': { 
                label: '絕對初學者', 
                description: '我聽說過這個名字，但僅此而已。',
                hobbyDescription: '我聽說過這個名字，但僅此而已。',
                professionalDescription: '我聽說過這個名字，但僅此而已。'
            },
            '1': { 
                label: '新手', 
                description: '我可以定義它，但沒有實際經驗。',
                hobbyDescription: '我可以簡單描述，但沒有實際經驗。',
                professionalDescription: '我可以定義它，但沒有實際經驗。'
            },
            '2': { 
                label: '新手', 
                description: '我可以按照教學執行簡單任務。',
                hobbyDescription: '我能跟著教學學習基本概念。',
                professionalDescription: '我能跟著教學完成基礎任務。'
            },
            '3': { 
                label: '進階初學者', 
                description: '我可以執行簡單任務，但經常需要查找資料。',
                hobbyDescription: '我能做一些基礎操作，但經常需要查資料。',
                professionalDescription: '我能做基礎工作，但還需查閱資料。'
            },
            '4': { 
                label: '進階初學者', 
                description: '我可以可靠地執行基本任務，無需指導。',
                hobbyDescription: '我能獨立完成大部分興趣相關的任務。',
                professionalDescription: '我能獨立完成本職工作中的常規任務。'
            },
            '5': { 
                label: '熟練', 
                description: '我可以自己解決小的常見問題。',
                hobbyDescription: '我能解決常見問題，也能幫助其他人。',
                professionalDescription: '我能獨立排查和解決常見問題，具備一定的專業能力。'
            },
            '6': { 
                label: '熟練', 
                description: '我可以獨立高效地執行大多數任務。',
                hobbyDescription: '我能高效獨立地完成複雜的活動，偶爾指導他人。',
                professionalDescription: '我能高效獨立地完成複雜工作任務，並能指導同事。'
            },
            '7': { 
                label: '精通', 
                description: '我可以有效處理複雜或不熟悉的問題。',
                hobbyDescription: '我能挑戰高難度概念，鑽研和創新。',
                professionalDescription: '我能應對複雜或新穎的工作難題，推動團隊進步。'
            },
            '8': { 
                label: '精通', 
                description: '我有深入的理解，可以教授他人。',
                hobbyDescription: '我在圈子裡有一定影響力，能分享和帶動更多人參與。',
                professionalDescription: '我有深厚的專業知識，能培訓同事或為團隊提供專業建議。'
            },
            '9': { 
                label: '專家', 
                description: '我是這個領域的公認權威或先驅。',
                hobbyDescription: '我是該興趣領域的權威人物，積極推動社群發展。',
                professionalDescription: '我是該領域的行業領袖或創新者，推動行業標準和發展。'
            },
            '10': { 
                label: '資深教授級', 
                description: '我是世界知名的專家，對這個領域做出了重大貢獻。',
                hobbyDescription: '我是世界知名的專家，對這個興趣領域做出了重大貢獻。',
                professionalDescription: '我是世界知名的專家，對這個行業做出了重大貢獻。'
            }
        },
        
        // Feedback Modal
        feedbackModalTitle: '意見回饋 / 錯誤報告',
        feedbackDescription: '您的意見回饋幫助我們改進知識驛站。請分享您的想法！',
        feedbackInstructions: '如何有效地描述錯誤或意見回饋：',
        feedbackTip1: '• 具體說明您正在嘗試做什麼',
        feedbackTip2: '• 描述發生了什麼與您期望的不同',
        feedbackTip3: '• 包含您看到的任何錯誤訊息',
        feedbackTip4: '• 說明重現問題的步驟',
        feedbackTip5: '• 歡迎提出改進建議！',
        feedbackPlaceholder: '在此輸入您的意見回饋或錯誤報告...',
        submitFeedback: '提交意見回饋',
        submittingFeedback: '提交中...',
        feedbackSuccess: '感謝您的意見回饋！我們會儘快查看。',
        feedbackError: '提交意見回饋失敗。請重試。'
    },
    
    'ja': {
        // Login
        username: 'ユーザー名',
        password: 'パスワード',
        enterUsername: 'ユーザー名を入力してください',
        enterPassword: 'パスワードを入力してください',
        rememberMe: '30日間ログイン状態を保持',
        logIn: 'ログイン',
        loginFailed: 'ログインに失敗しました。',
        connectionError: 'サーバーに接続できません。',
        signingIn: 'ログイン中...',
        or: 'または',
        googleLoginFailed: 'Googleログインに失敗しました。',
        loginTitle: '日進月歩',
        loginSubtitle: '千里の道も一歩から',
        secureConnection: '接続は安全で暗号化されています',
        
        // Registration
        createAccount: 'アカウント作成',
        registerDescription: '登録して学習の旅を始めましょう',
        email: 'メールアドレス',
        confirmPassword: 'パスワード確認',
        enterEmail: 'メールアドレスを入力してください',
        confirmPasswordPlaceholder: 'パスワードを再入力してください',
        passwordRequirement: '6文字以上',
        passwordsDoNotMatch: 'パスワードが一致しません',
        passwordTooShort: 'パスワードは6文字以上必要です',
        invalidEmail: '有効なメールアドレスを入力してください',
        emailAlreadyRegistered: 'このメールアドレスは既に登録されています',
        emailAlreadyRegisteredVerified: 'メールアドレスは既に登録・確認済みです。ログインしてください。',
        accountExistsUnverified: 'このメールアドレスのアカウントは既に存在します。確認メールを送信しました。受信トレイをご確認ください。',
        registrationFailed: '登録に失敗しました',
        notWhitelisted: '申し訳ございません。現在、招待されたテストユーザーのみアカウントを作成できます。',
        creatingAccount: 'アカウントを作成中...',
        alreadyHaveAccount: 'アカウントをお持ちですか？',
        signIn: 'ログイン',
        noAccount: 'アカウントをお持ちでない方',
        allFieldsRequired: 'すべての項目は必須です',
        
        // Password Reset
        forgotPassword: 'パスワードをお忘れですか？',
        resetPassword: 'パスワードをリセット',
        resetPasswordDescription: 'メールアドレスを入力してください。パスワードリセット用のリンクをお送りします。',
        sendResetLink: 'リセットリンクを送信',
        sending: '送信中...',
        checkYourEmail: 'メールをご確認ください',
        resetLinkSent: 'このメールアドレスのアカウントが存在する場合、パスワードリセットリンクが送信されました。',
        backToLogin: 'ログインに戻る',
        setNewPassword: '新しいパスワードを設定',
        enterNewPassword: '以下に新しいパスワードを入力してください',
        newPassword: '新しいパスワード',
        confirmNewPassword: '新しいパスワードを確認',
        resettingPassword: 'パスワードをリセット中...',
        passwordResetSuccess: 'パスワードリセット完了！',
        passwordResetSuccessMessage: 'パスワードが正常にリセットされました。新しいパスワードでログインできます。',
        redirectingToLogin: 'ログインページにリダイレクト中...',
        resetPasswordFailed: 'パスワードのリセットに失敗しました',
        
        // Email Verification
        verificationEmailSent: 'メールをご確認ください！',
        verificationEmailDescription: '以下のアドレスに確認メールを送信しました：',
        nextSteps: '次のステップ：',
        checkInbox: 'メールの受信トレイを確認',
        clickVerifyLink: 'メール内の確認リンクをクリック',
        loginAfterVerification: '認証情報でログイン',
        cantFindEmail: 'メールが見つかりませんか？',
        checkSpamFolder: '迷惑メールフォルダを確認するか、ログインページから新しい確認メールをリクエストしてください。',
        goToLogin: 'ログインへ',
        emailNotVerified: 'メールアドレス未確認',
        verifyEmailToLogin: 'ログインするにはメールアドレスを確認してください。受信トレイの確認メールをご確認ください。',
        resendVerificationEmail: '確認メールを再送信',
        sendingEmail: '送信中...',
        emailSent: 'メール送信完了！',
        checkYourInbox: '確認メールを送信しました！受信トレイをご確認ください。',
        
        // User onboarding
        welcomeToApp: 'ようこそ {appTitle} へ',
        selectPreferredLanguage: '続行するには、ご希望の言語を選択してください。',
        uiLanguage: 'UI言語',
        youCanChangeLater: 'これは後で設定で変更できます。',
        next: '次へ',
        back: '戻る',
        previous: '前へ',
        step: 'ステップ',
        quickStartGuide: 'クイックスタートガイド',
        userProfileSetup: 'あなたについて教えてください',
        profileSetup: 'このオプション情報は、学習体験をあなたに合わせるのに役立ちます。',
        
        // Guided Tutorial
        tutorialWelcomeTitle: 'さあ、始めましょう！',
        tutorialButtonSpotlightTitle: 'インタラクティブチュートリアル',
        tutorialButtonSpotlightMessage: '完全な機能ガイドをご希望ですか？チュートリアルボタンをクリックして、インタラクティブガイドですべての機能を探索してください。すぐに始めたい場合は、次へをクリックして最初のトピックを追加してください！',
        tutorialStepIndicator: 'ステップ 1 / 1',
        tutorialWelcomeMessage: '学習の旅を始める準備はできましたか？上の青い「+」ボタンをクリックして、最初のトピックを追加し、今日から新しいことを学び始めましょう！',
        tutorialActionHint: 'ここをクリックしてトピックを追加',
        tutorialActionDescription: '学びたい科目を選んでください',
        tutorialSkip: 'チュートリアルをスキップ',
        tutorialGetStarted: 'わかりました！',
        
        // Comprehensive Tutorial Mode
        tutorial: 'チュートリアル',
        startTutorial: 'チュートリアルを開始',
        tutorialNext: '次へ',
        tutorialPrevious: '前へ',
        tutorialComplete: '完了',
        tutorialWelcomeContent: 'パーソナライズされた学習アシスタントへようこそ！このチュートリアルでは、システムの学習機能を最大限に活用するためのすべての機能をご紹介します。',
        tutorialWelcomeTip: '• このチュートリアルは約1～2分で完了します。',
        tutorialNavigationTip: '• キーボードの ← と → 矢印キーを使うと、より簡単にナビゲートできます。',
        
        tutorialAddTopicContent: '上部の「+」ボタンをクリックして、最初の学習トピックを追加してください。興味のある科目を何でも追加できます！',
        tutorialTip: 'ヒント：',
        tutorialAddTopicTip: '機械学習、料理、動物、ガーデニングなどの一般的なトピックを使用してください。トピック作成時に学習目標や詳細を追加すると、よりパーソナライズされたコンテンツが得られます。',
        tutorialTopicCardContent: 'トピックカードは学習科目を整理します。ステータスインジケーターに注目してください：',
        tutorialUnreadIndicator: '青い点 - 未読コンテンツ',
        tutorialUnreadDesc: '新しいヒントを読む必要があることを示します',
        tutorialReviewIndicator: 'オレンジの点 - レビュー期限',
        tutorialReviewDesc: '記憶を強化するために概念をレビューする時期です',
        tutorialFavoriteIndicator: 'ハート - お気に入りトピック',
        tutorialFavoriteDesc: 'お気に入りのトピックで、素早くアクセスできます',
        tutorialAddTipContent: 'ヒントパネルの「+」ボタンをクリックして、AI駆動の新しい学習概念を生成します。AIがパーソナライズされたコンテンツを作成するのに約1分かかる場合があります。',
        tutorialManualTipBenefit: 'プロのヒント：',
        tutorialManualTipDesc: '待ちたくないですか？トピック設定で概念生成をスケジュールすれば、システムが自動的に事前にコンテンツを準備してくれます！',
        tutorialTipCardContent: 'これらはあなたの学習概念です。任意のヒントをクリックして、完全なコンテンツを表示したり、AI会話を開始したり、レビュー用にマークしたりできます。',
        tutorialInteractionHint: 'クイックアクション：',
        tutorialInteractionDesc: '任意のヒントを右クリックすると、お気に入り登録、編集、削除などのオプションが表示されます。',
        tutorialTopicInteractionDesc: '任意のトピックを右クリックすると、お気に入り登録、編集、削除などのオプションが表示されます。',
        tutorialTopicGroupingContent: 'カスタムグループでトピックを整理しましょう！関連するトピックをグループ化して、学習ワークフローを最適化します。',
        tutorialCreateGroup: 'トピックグループを作成',
        tutorialCreateGroupDesc: '「グループを追加」をクリックして、「仕事のプロジェクト」、「個人学習」、「趣味」などのカスタムカテゴリを作成します',
        tutorialDragTopics: 'トピックをグループにドラッグ',
        tutorialDragTopicsDesc: 'トピックカードを任意のグループにドラッグ＆ドロップするだけで整理できます',
        tutorialReorderWithinGroups: 'グループ内で並べ替え',
        tutorialReorderWithinGroupsDesc: 'グループ内でトピックを上下にドラッグして、お好みの順序に並べます',
        tutorialReorderGroups: 'グループ全体を並べ替え',
        tutorialReorderGroupsDesc: 'グループヘッダーをドラッグして、グループ全体を並べ替え、学習分野の優先順位を決めます',
        tutorialGroupingTip: '整理のコツ：',
        tutorialGroupingTipDesc: 'グループは学習セッション中に特定の分野に集中するのに役立ち、関連するトピックをまとめて、より良いコンテキストを提供します。',
        tutorialGroupingNote: '重要な注意：',
        tutorialGroupingNoteDesc: 'グループは純粋に視覚的な整理のためのもので、AI生成コンテンツには一切影響しません。',
        tutorialAITutorContent: 'あなたのAIチューターはいつでもお手伝いする準備ができています！質問をしたり、説明を受けたり、どのトピックでも深く掘り下げたりできます。',
        tutorialReviewContent: '間隔反復を使用した定期的なレビューは、知識を長期的に保持するのに役立ちます。質問に答えて学習を強化しましょう。',
        tutorialCongrats: 'おめでとうございます！',
        tutorialCompleteContent: 'チュートリアルを完了しました！これで学習の旅を始める準備ができました。',
        tutorialNextSteps: '次のステップ：',
        tutorialStep1: '最初の学習トピックを追加',
        tutorialStartLearningHint: '学習に情熱を注いでいるトピックを追加することから始めましょう！',
        tutorialStep2: '毎日のヒントと概念をレビュー',
        tutorialStep3: '質問があるときはAIチューターとチャット',
        tutorialStep4: 'レビューを完了して記憶を強化',
        tutorialConcepts: '学習済みの概念',
        tutorialExplanation: '説明：',
        
        // Tutorial Dummy Content
        tutorialDummyTopicTitle: 'JavaScriptを学ぶ',
        tutorialDummyTopicDesc: 'JavaScriptプログラミングの基礎をマスター',
        tutorialDummyNextReview: '明日の午前9:00',
        tutorialDummyTipTitle: '変数を理解する',
        tutorialDummyTipContent: '変数はデータ値を格納するコンテナです。JavaScriptでは、let、const、またはvarを使用して変数を宣言できます。',
        
        tutorialDummyReviewQuestion: 'letとconstの違いは何ですか？',
        tutorialDummyOption1: 'letは再代入可能、constは不可',
        tutorialDummyOption2: 'constは再代入可能、letは不可',
        tutorialDummyOption3: '両方とも再代入可能',
        tutorialDummyOption4: '両方とも再代入不可',
        tutorialDummyExplanation: 'letで宣言された変数は再代入できますが、constは定数参照を作成します。',
        
        // Tutorial Knowledge Level Modal
        tutorialNewConceptModalIntro: '未読の学習概念を選択すると、この知識レベル選択画面が表示されます。これは学習体験をパーソナライズするための重要なステップです！',
        tutorialTooEasy: '簡単すぎる',
        tutorialTooEasyDesc: 'この概念は基本的で、現在の知識にあまり追加されません',
        tutorialAlreadyKnown: 'すでに理解',
        tutorialAlreadyKnownDesc: 'この概念をよく理解しており、自信があります',
        tutorialPartiallyKnow: '部分的に理解',
        tutorialPartiallyKnowDesc: 'ある程度理解していますが、知識を強化する必要があります',
        tutorialNewToMe: '初めての内容',
        tutorialNewToMeDesc: '初めて学ぶまったく新しい情報です',
        tutorialTooHard: '難しすぎる',
        tutorialTooHardDesc: 'この概念は今のところ理解するには難しすぎます',
        tutorialKnowledgeTip: 'なぜ重要なのか：',
        tutorialKnowledgeTipDesc: 'あなたの選択により、AIチューターはあなたの知識レベルに合わせて説明や推奨を調整します。',
        tutorialKnowledgeLevelPanelIntro: 'いつでも知識レベルを変更できます。',
        tutorialProgressTracking: '進捗を追跡',
        tutorialProgressTrackingDesc: '学習が進むにつれて「初めての内容」から「部分的に理解」に更新し、AIがあなたの進歩に適応するのを助けます',
        tutorialFlexibleLearning: '柔軟な学習',
        tutorialFlexibleLearningDesc: '間違えた？予想より簡単だった？クリックするだけでいつでも知識レベルを更新できます！',
        tutorialTipContentIntro: 'ここにあなたの学習コンテンツが表示されます。各概念には、選択したトピックをマスターするのに役立つAI生成の洗練された知識が含まれています。すべてのコンテンツは検索で検証され、正確性と関連性が保証されています。',
        tutorialQuickActionsIntro: 'これらのクイックアクションボタンは、事前設定されたプロンプトで会話を送信するのに役立ち、一般的な質問を入力する時間を節約します。',
        tutorialViewModesIntro: '異なる表示モードを切り替えて、学習体験をカスタマイズします：',
        tutorialTipOnlyMode: '概念のみ',
        tutorialTipOnlyModeDesc: '邪魔されることなく、概念コンテンツの読書と学習に集中します。初回の読書と理解に最適です。',
        tutorialFullViewMode: 'フルビュー',
        tutorialFullViewModeDesc: 'ヒントコンテンツとAI会話を並べて表示します。読みながら質問もできる能動的な学習に理想的です。',
        tutorialChatOnlyMode: 'チャットのみ',
        tutorialChatOnlyModeDesc: 'AIチューターとの詳細な議論のために会話スペースを最大化します。拡張Q&Aセッションや詳細な説明に最適です。',
        tutorialViewModesProTip: 'プロのヒント：',
        tutorialViewModesProTipDesc: '会話を開始すると、ビューは自動的にフルビューに切り替わります。いつでも学習の好みに合わせて変更できます！',
        tutorialTipActionsIntro: 'これらの強力なアクションで学習概念を管理します：',
        tutorialHideAction: '非表示',
        tutorialHideActionDesc: 'マスターした概念や一時的にスキップしたい概念を一時的に非表示にします',
        tutorialFavoriteAction: 'お気に入り',
        tutorialFavoriteActionDesc: '重要な概念をマークして、素早くアクセスし、優先的にレビューします',
        tutorialScheduleReview: 'レビューをスケジュール',
        tutorialScheduleReviewDesc: '間隔反復を使用して概念をレビューするリマインダーを設定し、より良い記憶効果を得ます',
        tutorialProTip: 'プロのヒント：',
        tutorialTipActionsProTip: 'これらのアクションを使用して学習フローをカスタマイズします。よく知っている概念を非表示にし、挑戦的な概念をお気に入りに登録し、最適な記憶効果のためにレビューをスケジュールします。',
        tutorialReviewTrackingIntro: 'エビングハウスの忘却曲線に基づいて、レビュー問題を生成し、長期的に知識を保持するのに役立ちます：',
        tutorialDateLearned: '学習日',
        tutorialDateLearnedDesc: '概念の知識レベルを初めて選択した日付です。',
        tutorialReviewButton: 'レビュー問題ボタン',
        tutorialReviewButtonDesc: 'クリックして多肢選択問題で知識をテストします。新しいレビュー問題がある場合はオレンジ色に光ります。',
        tutorialDaysTillReview: '次回レビューまでの日数',
        tutorialDaysTillReviewDesc: '次回のレビューまでのカウントダウンを表示します。',
        tutorialReviewModalIntro: 'レビュー問題で知識をテストしましょう！この強力な学習ツールは、能動的想起を通じて概念を強化するのに役立ちます：',
        tutorialAnswerQuestions: 'レビュー問題に答える',
        tutorialAnswerQuestionsDesc: '多肢選択問題で重要な概念の理解度をテストします。答えと確信度を選択してください。',
        tutorialDeepDiveChat: '深掘り会話',
        tutorialDeepDiveChatDesc: '回答後、AIチューターと会話を始めて、トピックをより深く探求し、混乱を解消します。',
        tutorialActiveRecall: '能動的想起のヒント：',
        tutorialActiveRecallDesc: '選択肢を見る前に答えを思い出してみてください。これにより記憶経路が強化され、長期記憶が改善されます！',
        tutorialSpacedRepetition: 'ベストプラクティス：',
        tutorialSpacedRepetitionDesc: '最も効果的な学習成果を得るために、概念のレビュー期限が来たらすぐにレビューしてください。このタイミングが記憶保持と長期的な想起を最適化します。',
        tutorialScrollTip: 'ヒント：',
        tutorialScrollTipDesc: 'コンテンツエリアはスクロール可能です。より多くの情報が含まれている場合は、下にスクロールして概念全体を読むことができます。',
        tutorialDummyTopic1Name: '健康とウェルネス',
        tutorialDummyTopic1Desc: 'より健康的で活力のある生活のための必須知識',
        tutorialDummyTopic2Name: '料理',
        tutorialDummyTopic2Desc: '基本的な調理技術とレシピをマスター',
        tutorialDummyTopic3Name: 'ガーデニング',
        tutorialDummyTopic3Desc: '自分の植物や野菜を育てる方法を学ぶ',
        tutorialDummyTip1Title: '水分補給の力',
        tutorialDummyTip1Content: '適切な水分補給は体のあらゆるシステムにとって基本的です。「1日8杯」のルールは人気がありますが、実際の必要量は活動レベル、気候、体格によって異なります。\n\n重要な事実：\n• あなたの体の60%は水分です\n• 軽度の脱水（2%）でも集中力とエネルギーに影響します\n• 最良の指標：薄い黄色の尿は良好な水分補給を意味します\n• 喉が渇く前に水を飲む - 喉の渇きはすでに軽度の脱水を意味します\n\n最適なタイミング：起床時すぐに水を飲み、一日中飲み、就寝2時間前は摂取を減らす。',
        tutorialDummyTip2Title: 'エネルギーレベルを理解する',
        tutorialDummyTip2Content: 'あなたのエネルギーは、サーカディアンリズムに従って一日中自然に変動します。これらのパターンを理解することで、生産性と幸福を最適化できます。\n\n自然なエネルギーピーク：\n• 午前（9-11時）：複雑なタスクに最適\n• 午後早め（13-15時）：自然な低下、ルーチンワークに理想的\n• 午後遅め（15-18時）：第二のピーク、会議に適している\n\nエネルギーブースター：\n• 一貫した睡眠スケジュール（7-9時間）\n• バランスの取れた栄養を含む定期的な食事\n• 90分ごとの短い散歩\n• 一日中水分補給を維持',
        tutorialDummyTip3Title: '包丁技術：基礎',
        tutorialDummyTip3Content: '適切な包丁技術は、効率的で安全な調理に不可欠です。これらの基本的なカットをマスターしましょう：\n\n• 千切り：細いマッチ棒切り\n• さいの目切り：均一な立方体の形\n• シフォナード：ハーブを巻いて切る\n• ブリュノワーズ：非常に細かいさいの目切り\n\n常に包丁を鋭く保ち、安定したまな板を使用してください。',
        
        // Tutorial Conversations
        tutorialDummyBotInit1: '水分補給について探求しているのを見て嬉しいです！これは本当に私たちの体の機能の基礎です。\n\nどの側面をより深く掘り下げたいですか - 毎日の摂取ガイドライン、タイミング、それとも脱水症状の認識？',
        tutorialDummyQuestion1: '実際に脱水しているのか、ただ喉が渇いているだけなのか、どうやって見分けられますか？',
        tutorialDummyAnswer1: '良い区別です！喉の渇きは実際には遅い指標です - 喉が渇いたと感じる時には、すでに軽度の脱水状態です。\n\nより良い指標には以下があります：\n• **尿の色**：薄い黄色は水分補給が良好、濃い琥珀色は脱水を示します\n• **エネルギーの低下**：説明のつかない疲労、特に午後\n• **肌の弾力性**：手の皮膚をつまむ - すぐに戻るはずです\n\n重要なのは、喉の渇きを待つのではなく、一日中一貫して水を飲むことで脱水を防ぐことです。',
        
        tutorialDummyBotInit2: '素晴らしい、エネルギーレベルについて深く掘り下げていますね！自然なリズムを理解することは生産性の鍵です。\n\n何を探求したいですか - エネルギーの低下を管理する、ピーク時間を最適化する、それとも全体的なスタミナを改善する？',
        tutorialDummyQuestion2: '良い睡眠を取っても、なぜいつも午後2-3時に疲れ切ってしまうのですか？',
        tutorialDummyAnswer2: 'その午後の低下は実際には自然なものです - それはあなたのサーカディアンリズムの一部であり、必ずしも何か問題があるわけではありません。\n\nあなたの体温は午後1-3時の間に自然に下がり、眠気を引き起こします。クラッシュを最小限に抑えるには：\n• **軽い昼食を食べる** - 重い食事は眠気を悪化させます\n• **短い散歩をする** - 5-10分でも役立ちます\n• **水分補給を維持** - 脱水は疲労を増幅させます\n• **可能なら20分のパワーナップを検討** \n\nこの時間帯にルーチンタスクをスケジュールし、複雑な作業を午前または午後遅めのピーク時に残すことで、この自然な低下に対処しましょう。',
        
        // Tutorial Review Questions
        tutorialDummyReviewQ2: '代謝に最適な大きなコップ一杯の水を飲むタイミングはいつですか？',
        tutorialDummyReviewChoice2A: '就寝前',
        tutorialDummyReviewChoice2B: '食事中',
        tutorialDummyReviewChoice2C: '朝一番',
        tutorialDummyReviewChoice2D: '運動中のみ',
        tutorialDummyReviewExp2A: '就寝前に大量の水を飲むとトイレで睡眠が中断されます。',
        tutorialDummyReviewExp2B: '食事中に大量の水を飲むと消化酸が薄まります。少しずつ飲む方が良いです。',
        tutorialDummyReviewExp2C: '正解！朝の水分補給は代謝を始動させ、夜間の毒素を洗い流すのに役立ちます。',
        tutorialDummyReviewExp2D: '運動中は重要ですが、代謝促進には最適なタイミングではありません。',
        tutorialChatStarter2A: '就寝前の水分補給は難しいです！水分と睡眠のバランスについて説明しましょう...',
        tutorialChatStarter2B: '食事時の水分補給は重要ですが、タイミングが大切です。その理由を説明しましょう...',
        tutorialChatStarter2C: '完璧です！効果を最大化する朝の水分補給ルーティンについて知りたいですか？',
        tutorialChatStarter2D: '運動時の水分補給は重要ですが異なります。タイミングについて説明しましょう...',
        
        // Tutorial Scheduled Generation Modal
        tutorialScheduledGenerationIntro: 'スケジュールされた概念生成で学習を自動化しましょう！設定して忘れる - 新しい概念があなたの好みの時間に自動的に生成されます。',
        tutorialSetSchedule: 'スケジュールを設定',
        tutorialSetScheduleDesc: '新しい概念をいつ、どのくらいの頻度で生成するかを選択 - 毎日、毎週、または毎月、あなたの好みの時間に。',
        tutorialTimezoneAware: 'タイムゾーン対応',
        tutorialTimezoneAwareDesc: 'システムは自動的にあなたのタイムゾーンに調整し、期待する時間に概念が準備されることを保証します。',
        tutorialSmartSkipping: 'スマートスキップ',
        tutorialSmartSkippingDesc: '「未読の概念が存在する場合はスキップ」を有効にして、多すぎる未読の概念に圧倒されるのを防ぎます。',
        
        // Tutorial Filter Functions
        tutorialFilterFunctionsIntro: 'スマートフィルタリングで整理整頓！強力なフィルタオプションで最も重要なトピックと概念をすばやく見つけます。',
        tutorialFavoriteFilter: 'お気に入りフィルタ',
        tutorialFavoriteFilterDesc: 'ハートアイコンをクリックして、お気に入りのトピックまたは概念のみを表示します。最も気になるコンテンツに集中するのに最適です。',
        tutorialReviewDueFilter: 'レビュー期限フィルタ',
        tutorialReviewDueFilterDesc: '時計アイコンをクリックして、レビューが必要な項目のみを表示します。間隔反復スケジュールを把握しましょう。',
        tutorialAdvancedFilter: '高度なフィルタ',
        tutorialAdvancedFilterDesc: '漏斗アイコンをクリックして、ステータス（未読、既知としてマークなど）やスケジュール生成設定などの高度なフィルタリングオプションにアクセスします。',
        tutorialFilterProTip: 'プロのヒント',
        tutorialCombineFiltersTip: '複数のフィルタを組み合わせて強力なビューを作成できます。たとえば、レビューが必要なお気に入りのトピックのみを表示できます！',
        
        // Tutorial AI Learning Summary Modal
        tutorialAILearningSummaryIntro: 'あなたの学習の旅についてパーソナライズされた洞察を得ましょう！AIはあなたの相互作用を分析して、知識プロファイル、学習パターン、最適化の機会を理解します。',
        tutorialKnowledgeProfile: '知識プロファイル',
        tutorialKnowledgeProfileDesc: '概念や会話との関わりに基づいて、あなたの強みと知識のギャップを理解します。',
        tutorialLearningPatterns: '学習パターン',
        tutorialLearningPatternsDesc: 'あなたが最も効果的に学ぶ方法を発見 - 例、理論、または実践ベースのアプローチのどれか。',
        tutorialOptimizationInsights: '最適化の洞察',
        tutorialOptimizationInsightsDesc: '学習効果と記憶を向上させるための実行可能な推奨事項を取得します。',
        tutorialRequirementNote: '注意：',
        tutorialRequirementNoteDesc: '最初のAI学習サマリーを生成する前に、少なくとも5つの概念を読む必要があります。これにより、意味のある洞察を得るのに十分なデータが確保されます。',
        tutorialRightClickTopicTip: 'トピックカードを右クリックして「編集」を選択すれば、いつでもこれらの設定にアクセスできます。',
        
        // Tutorial Group Names
        tutorialDummyGroup1Name: '健康とライフスタイル',
        tutorialDummyGroup2Name: '趣味',
        tutorialFeatureAI: 'AI駆動学習',
        tutorialFeatureSpaced: '間隔反復',
        tutorialFeatureTutor: 'パーソナルAIチューター',
        tutorialFeatureProgress: '進捗追跡',
        tutorialDailyTipContent: '毎日、あなたの学習ペースに合わせたバイトサイズのレッスンを受け取ります。これらのヒントは、迅速かつ効果的な学習のために設計されています。',
        addYourFirstTopic: '最初のトピックを追加',
        tutorialDummyUserQuestion: '関数がどのように機能するか説明していただけますか？',
        tutorialDummyAIResponse: '関数は特定のタスクを実行する再利用可能なコードブロックです。何度も使えるレシピのようなものだと考えてください...',
        
        // Header
        appTitle: '日進月歩', // or 知恵の泉?
        welcome: 'ようこそ',
        logout: 'ログアウト',
        
        // Profile
        name: '名前',
        enterYourName: '名前を入力してください',
        nameHelp: '名前は個人用のやりとりに使用されます',
        
        // Topics
        topics: 'トピック',
        addTopicTooltip: '新しい学習トピックを追加',
        filterFavoriteTopicsTooltip: 'お気に入りのトピックのみ表示',
        filterReviewDueTopicsTooltip: 'レビューが必要な概念があるトピックを表示',
        filterTopicsTooltip: 'トピックをフィルター',
        noTopicsToFilter: 'フィルターを使用するには、まずトピックを作成してください',
        editTopic: 'トピックを編集',
        topicName: 'トピック名',
        startNewLearningTopic: '新しい学習トピックを開始',
        learningContext: '学習コンテキストと目標（任意）',
        learningContextHelp: '現在のレベル、既に学習した内容、具体的な目標、より関連性の高い概念を生成するのに役立つコンテキストを説明してください。',
        learningContextExample: '例：「私は日本語の初心者で、東京旅行用のカジュアルなスラングを学びたいです。基本的な挨拶は既に学んでおり、ひらがなを読むことができます。若者の日常会話で使われるフレーズに焦点を当てたいです。特に食べ物関連の語彙と道案内に興味があります。」',
        purpose: '学習目的',
        hobby: '趣味・関心',
        professional: '技術・専門的成長',
        topicLanguage: 'トピック言語',
        saveChanges: '変更を保存',
        editTopicChangesNote: '注意：トピック設定の変更は、将来生成されるヒントにのみ影響します。既存のヒントは変更されません。',
        learningDetails: '学習詳細',
        knowledgeContext: '知識とコンテキスト',
        learningSummary: 'AI学習サマリー',
        summaryTooltip: 'あなたの活動に基づいて生成された学習パターンと興味のAI分析',
        generateSummary: 'サマリーを生成',
        generating: '生成中...',
        summaryPlaceholder: '「サマリーを生成」をクリックして学習パターンと興味を分析...',
        includeSummaryInPrompt: 'パーソナライズされたコンテンツのためにAIコンテキストに含める',
        summaryHelp: 'このAI生成サマリーは、将来のコンテンツをパーソナライズするためにあなたの学習パターンを分析します',
        insufficientReadTips: 'AI学習サマリーを生成するには、少なくとも5つの既読ヒントが必要です。現在{count}個のヒントを読んでいます。この機能を解除するため、ヒントを読み続けてマークしてください。',
        noTipsForSummary: 'まだ学習コンテンツがありません。まずヒントを追加してからサマリーを生成してください。',
        summaryCachedMessage: '前回のサマリー生成以降、学習活動に変化はありません。より多くのヒントを読んだり、会話を行った後に再度お試しください。',
        summaryNewMessage: '最新の活動とやり取りに基づいて新しい学習サマリーが生成されました。',
        summaryGenerationError: '学習サマリーの生成に失敗しました。もう一度お試しください。',
        knowledgeProfile: '知識プロフィール',
        learningPatterns: '学習パターン',
        optimizationInsights: '最適化のヒント',
        edit: '編集',
        view: '表示',
        learningContextGoals: '学習コンテキストと目標（オプション）',
        learningContextPlaceholder: '具体的な学習目標、興味、または焦点を当てたい内容を説明してください...',
        cancel: 'キャンセル',
        startLearning: '学習を開始',
        settings: '設定',
        feedbackBugReport: 'フィードバック / バグレポート',
        currentProfile: '現在のプロフィール',
        age: '年齢',
        education: '教育',
        notProvided: '未提供',
        yearsOld: '歳',
        saving: '保存中...',
        profile: 'プロフィール',
        aiModel: 'AIモデル',
        activeSessions: 'アクティブセッション',
        usage: '使用状況',
        thankYouTestUser: 'テストユーザーになっていただきありがとうございます！',
        thankYouMessage: '{appTitle}のテストにご協力いただき、ありがとうございます。皆様のフィードバックは非常に貴重です。',
        testUserBenefits: 'テストユーザー特典',
        conversations: '会話',
        unlimited: '無制限',
        conceptGeneration: 'コンセプト生成',
        aiLearningSummary: 'AI学習サマリー',
        reviewScheduling: 'レビュースケジューリング',
        loadingSessions: 'セッションを読み込み中...',
        activeSessionsInfo: 'アクティブセッションの管理',
        activeSessionsDescription: '現在ログインしているすべてのデバイスを表示および管理します。特定のデバイスまたはすべてのデバイスからログアウトできます。',
        noActiveSessions: 'アクティブセッションが見つかりません',
        unknownDevice: '不明なデバイス',
        currentSession: '現在のセッション',
        ipAddress: 'IPアドレス',
        unknown: '不明',
        lastActive: '最終アクティブ',
        logoutThisDevice: 'このデバイスからログアウト',
        logoutAllDevices: 'すべてのデバイスからログアウト',
        confirmLogoutSession: 'このデバイスからログアウトしてもよろしいですか？',
        confirmLogoutAllSessions: 'すべてのデバイスからログアウトしてもよろしいですか？再度ログインする必要があります。',
        close: '閉じる',
        
        // AI Model Settings
        conversationModel: '会話モデル',
        selectConversationModel: '会話用AIモデルを選択',
        selectAIProvider: 'AIプロバイダーを選択',
        modelQualityVsSpeed: '品質と速度のトレードオフ',
        modelPro: 'プレミアム',
        modelProDescription: '深い推論による最高品質の応答',
        modelFlash: 'バランス',
        modelFlashDescription: '良い品質とより速い応答',
        modelLite: 'スピード',
        modelLiteDescription: 'シンプルな質問への迅速な応答',
        recommended: '推奨',
        bestQuality: '最高品質',
        balanced: 'バランス',
        fastest: '最速',
        quality: '品質',
        speed: '速度',
        
        // Slow Chat Notification
        slowChatNotification: '応答を待ちすぎていますか？より速い返信のために高速モデルに切り替えてみてください',
        changeModel: 'モデルを変更',
        dontShowAgain: '今後表示しない',
        
        sessionInvalidatedByAnotherLogin: '別のデバイスからログインしたため、ログアウトされました。',
        favoriteTopic: 'お気に入りのトピック',
        unfavoriteTopic: 'お気に入りから削除',
        favoritedTopic: 'お気に入りに追加済み',
        showFavoriteTopicsOnly: 'お気に入りのトピックのみ表示',
        showReviewDueTopicsOnly: '復習の必要なトピックのみ表示',
        showScheduledTopicsOnly: '自動生成トピックのみ表示',
        showUnscheduledTopicsOnly: '手動トピックのみ表示',
        showAllTopics: 'すべてのトピックを表示',
        daily: '毎日',
        weekly: '毎週',
        monthly: '毎月',
        everyNDays: '{count}日ごと',
        everyNWeeks: '{count}週間ごと',
        everyNMonths: '{count}ヶ月ごと',
        topicSingular: 'トピック',
        topicPlural: 'トピック',
        ungrouped: '未分類',
        createGroup: 'グループを作成',
        groupName: 'グループ名',
        rename: '名前を変更',
        deleteGroupTitle: 'グループを削除',
        deleteGroupMessage: 'このグループを削除してもよろしいですか？トピックは未分類に移動されます。',
        
        // Add Topic Modal specific translations
        learningGoalQuestion: 'この学習の目的は何ですか？',
        contentLanguageQuestion: '内容はどの言語で説明するべきですか？',
        rateYourKnowledge: '現在の知識レベルを評価',
        example: '例',
        
        // Scheduling translations
        enableAutomaticTipGeneration: '自動ヒント生成を有効にする',
        generateTipsAt: '以下の時間にヒントを生成：',
        generateNewTipsEvery: '以下の間隔で新しいヒントを生成：',
        hour: '時間',
        hours: '時間',
        days: '日',
        weeks: '週',
        months: '月',
        timezone: 'タイムゾーン',
        schedulingNote: '選択したタイムゾーンがスケジューリングに使用されます',
        scheduledSkipIfUnread: 'このトピックに未読のヒントがある場合は新しいヒントをリクエストしない',
        
        // Placeholders
        topicNamePlaceholder: '例：量子コンピューティング',
        selectKnowledgeLevel: 'スライダーを動かして現在の知識レベルを評価してください',
        selectLearningGoal: '学習目標を選択してください',
        completeAllFieldsToStart: '学習を開始するには、トピック名、学習目標、知識レベルを完了してください',
        
        // Topic Examples
        topicExamples: [
            {
                name: '量子計算',
                description: '私は物理系学生で、線形代数と古典力学についての基礎があります。私は研究プロジェクトのために量子アルゴリズムを理解したいと思っています。私は既に量子力学の基礎を学び、複素数を扱うことができます。私は特に量子暗号学と量子機械学習アプリケーションに興味があります。'
            },
            {
                name: '日本語会話',
                description: '私は日本語の初心者で、東京旅行用の日常のスラングを学びたいと思っています。私は既に基本挨拶を学び、ひらがなを読むことができます。私は若者の日常会話で使われるフレーズに焦点を当てたいと思っています。私は特に食べ物関連の語彙と道案内に興味があります。'
            },
            {
                name: '機械学習',
                description: '私はPythonと統計学の経験を持つソフトウェア開発者です。私は会社のeコマースプラットフォームのために推奨システムを構築したいと思っています。私は既に監督学習のオンラインコースに参加し、基本的なアルゴリズムを理解しています。私は特に協調フィルタリングとディープラーニングアプローチに興味があります。'
            },
            {
                name: '旅行スペイン語',
                description: '私は南アメリカをバックパッキング旅行する計画を持っています。実用的なスペイン語スキルが必要です。私は高校で基本的な言葉を知っていますが、長い間練習していません。私は注文、宿泊、友人と話す会話スキルに焦点を当てたいと思っています。私は特に地域の違いと文化の背景に興味があります。'
            },
            {
                name: 'ブロックチェーン開発',
                description: '私はJavaScriptとスマートコントラクトの経験を持つウェブ開発者です。私はスタートアッププロジェクトのためにディセントライズドアプリケーションを構築したいと思っています。私は既にSolidityの基礎を学び、Ethereumの仕組みを理解しています。私は特にDeFiプロトコルとNFTマーケットに興味があります。'
            },
            {
                name: 'コックリングテクニック',
                description: '私は家庭料理人で、ダイニングパーティーのために料理スキルを向上させたいと思っています。私はレシピに従って料理を作ることができますが、料理方法の科学の背景を理解したいと思っています。私は既に基本的なナイフスキルを習得し、シンプルなソースを作ることができます。私は特にフランスのテクニックと分子ガストロニミーに興味があります。'
            },
            {
                name: 'データサイエンス',
                description: '私はビジネスアナリストで、データサイエンスに向けて移行したいと思っています。私は強力なExcelスキルと基本的なSQL知識を持っています。私はデータ分析と可視化のためにPythonを学びたいと思っています。私は特に予測モデリングとビジネスインテリジェンスアプリケーションに興味があります。'
            },
            {
                name: 'ギター理論',
                description: '私は中級のギタリストで、コードとシンプルな曲を弾くことができます。私は音楽理論を理解して自分の曲と即興を作ることを望んでいます。私は既に基本的なスケールを学び、タブラチャを読むことができます。私は特にジャズ理論とフィンガーピッキングテクニックに興味があります。'
            },
            {
                name: '写真',
                description: '私は写真愛好家で、デジタル一眼レフを持っています。私は風景写真の構図とライトニングスキルを向上させたいと思っています。私は基本的なカメラ設定を理解していますが、手動モードをマスターしたいと思っています。私は特に長時間露光テクニックとポストプロセスに興味があります。'
            },
            {
                name: 'ウェブ開発',
                description: '私は初心者のプログラマーで、現代のウェブアプリケーションを構築したいと思っています。私はオンラインチュートリアルから基本的なHTMLとCSSの知識を得ました。私はJavaScriptフレームワークとバックエンド開発を学びたいと思っています。私は特にReactとNode.jsを使用したフルスタック開発に興味があります。'
            },
            {
                name: '財務計画',
                description: '私は最近卒業した学生で、個人の財務と投資戦略を学びたいと思っています。私は予算に基本的な理解を持っていますが、株式、債券、そして退職計画について学びたいと思っています。私は特にインデックスファンドと税効率投資に興味があります。'
            },
            {
                name: '公衆演説',
                description: '私は専門家で、仕事の会議と演説のプレゼンテーションスキルを向上させる必要があります。私は小さなグループで話すことができますが、大規模な観客の前では緊張します。私は吸引的な表現と問題処理のテクニックを学びたいと思っています。私は特にストーリーテリングと説得性のあるスピーチに興味があります。'
            },
            {
                name: 'ヨガの練習',
                description: '私は初心者で、ストレス緩和と柔軟性を発展させるために継続的なヨガの練習をしたいと思っています。私はいくつかのクラスを試しましたが、正しいアライメントと呼吸のテクニックを理解したいと思っています。私は特にビニャーサフローとメディテーションの練習に興味があります。'
            },
            {
                name: 'ゲーム開発',
                description: '私はプログラマーで、2Dゲームを趣味のプロジェクトとして作成したいと思っています。私はC++と基本的なグラフィックスプログラミングの経験を持っています。私はゲームエンジンとデザインの原則を学びたいと思っています。私は特にUnityとモバイルゲーム開発に興味があります。'
            },
            {
                name: 'クリエイティブライティング',
                description: '私は抱負のある作家で、短編小説のストーリーテリングスキルを向上させたいと思っています。私は読書が好きですが、キャラクター開発とプロット構造に苦労しています。私は創造的な物語を作成するためのテクニックを学びたいと思っています。私は特にサイエンスフィクションとファンタジータイプに興味があります。'
            },
            {
                name: 'デジタルマーケティング',
                description: '私は小規模なビジネスオーナーで、オンラインの存在と顧客の取得を向上させたいと思っています。私は基本的なソーシャルメディアの知識を持っていますが、SEOと課金広告を学びたいと思っています。私は特にコンテンツマーケティングとメールキャンペーンに興味があります。'
            },
            {
                name: 'メディテーション',
                description: '私は忙しい専門家で、心理的な明確さとストレス管理のためにメディテーションの練習を発展させたいと思っています。私は正念アプリケーションを試しましたが、異なるテクニックと伝統を理解したいと思っています。私は特に超経験的メディテーションとガイド付きの練習に興味があります。'
            },
            {
                name: 'ホームビンテージ',
                description: '私はビール愛好家で、自分のクラフトビールを家で作りたいと思っています。私は基本的な発酵プロセスを理解していますが、異なるスタイルとテクニックを学びたいと思っています。私は特にIPAとサワーのビールに興味があります。'
            },
            {
                name: '天文学',
                description: '私は科学愛好家で、宇宙と天体について学びたいと思っています。私は基本的な望遠鏡を持っていますが、主要な星座を認識することができます。私は天体物理学の概念と観測技術を理解したいと思っています。私は特に系外惑星と深空天体に興味があります。'
            },
            {
                name: 'チェス戦略',
                description: '私は中級のチェスプレイヤーで、戦略的思考とエンドゲームスキルを向上させたいと思っています。私は基本的なオープニングを知っていて、単純な戦術を計算することができます。私は位置のプレイと高度な概念を学びたいと思っています。私は特に中盤の計画と兵の構造に興味があります。'
            }
        ],
        
        // Concepts
        concepts: '概念',
        addConceptTooltip: 'このトピックの新しい概念を生成',
        filterFavoriteConceptsTooltip: 'お気に入りの概念のみ表示',
        filterReviewDueConceptsTooltip: 'レビューが必要な概念を表示',
        filterConceptsTooltip: '概念をフィルター',
        selectTopicToFilterConcepts: '概念をフィルターするには、まずトピックを選択してください',
        filterByStatus: 'ステータスでフィルター',
        showFavoritesOnly: 'お気に入りのみ表示',
        showAllConcepts: 'すべての概念を表示',
        noConceptsMatchFilter: '現在のフィルター設定に一致する概念がありません。',
        tooEasy: '簡単すぎる',
        alreadyKnown: '既に知っている',
        partiallyKnow: '部分的に知っている',
        newToMe: '新しい',
        tooHard: '難しすぎる',
        notWhatLookingFor: '探しているものではない',
        showHiddenTips: '隠れたヒントを表示',
        hideThisTip: '非表示',
        hiddenTip: '隠れたヒント',
        showThisTipAgain: 'このヒントを再表示',
        favoriteThisTip: 'お気に入りに追加',
        unfavoriteThisTip: 'お気に入りから削除',
        favoritedTip: 'お気に入りに追加済み',
        scheduleReview: '復習をスケジュール',
        unscheduleReview: '復習スケジュールをキャンセル',
        enableReviewSchedule: 'このコンセプトのレビュースケジュールを有効にする',
        resetReviewProgress: '復習進捗をリセット',
        nextReviewIn: '次の復習まで',
        noReviewQuestionsYet: 'まだ復習問題はありません',
        reviewDays: '日',
        day: '日',
        today: '今日',
        yesterday: '昨日',
        daysAgo: '{count}日前',
        weeksAgo: '{count}週間前',
        monthsAgo: '{count}ヶ月前',
        yearsAgo: '{count}年前',
        reviewDue: '復習期限',
        reviewOverdue: '復習延長',
        reviewSequenceCompleted: '復習シーケンス完了',
        reviewProgressReset: '復習進捗リセット',
        reviewProgressResetMessage: 'これにより：\n• 復習進捗を最初にリセット\n• 復習スケジュールをオフ\n• すべての復習問題と回答を削除\n• 学習日は変更されません\n\n後で復習を再度有効にできます。\n\nよろしいですか？',
        
        // Main Content
        currentLearningConcept: '現在の学習概念',
        selectTopicConcept: 'トピックと概念を選択して開始してください。',
        notLookingFor: 'お探しのものではありませんか？',
        tryEditingTopic: 'トピックを編集して、目標についてより詳細な説明を追加してみてください。',
        selectConcept: '概念を選択して開始してください。',
        askQuestion: '質問してください...',
        
        // Three-State Feature
        tipOnly: 'コンセプトのみ',
        fullView: '全体表示',
        chatOnly: 'チャットのみ',
        clickStartDeepDive: '「ディープダイブ開始」をクリックして開始',
        startConversationFirst: 'このビューを使用するには、まず会話を開始してください',
        botGeneratingMessage: 'ボットが応答を生成しています、しばらくお待ちください...',
        
        // Quick Response Buttons
        quickActions: 'クイックアクション',
        stillDontUnderstand: 'まだ理解できません',
        testMyKnowledge: '私の知識をテスト',
        giveAnExample: '例を示してください',
        explainDifferently: '別の方法で説明してください',
        
        // Quick Response Messages (actual messages sent to conversation)
        stillDontUnderstandMessage: '私はまだこの概念を理解できません。もっと簡単な説明にしてください。',
        testMyKnowledgeMessage: 'この概念について私の理解をテストしてください。私が本当に理解しているかどうか確認してください。',
        giveAnExampleMessage: 'この概念の実際の例を教えてください。',
        explainDifferentlyMessage: 'この概念を別の方法で説明してください。おそらく異なる類推やアプローチを使用してください。',
        
        // Concept Details
        conceptDetails: '概念詳細',
        myKnowledgeLevel: '私の知識レベル',
        dateIntroduced: '導入日',
        dateLearned: '学習日',
        notLearnedYet: 'まだ学習していません',
        unknownStatus: '未知',
        conversation: '会話',
        startNewDeepDive: '新しいディープダイブを開始',
        reviewQuestions: '復習問題',
        tipActions: '操作',
        deleteConcept: '概念を削除',
        selectConceptDetails: '概念を選択して詳細を表示してください。',
        
        // Review Questions Modal
        reviewQuestion: '復習問題',
        deepDiveChat: 'ディープダイブチャット',
        howConfident: 'どの程度自信がありますか？',
        totallyGuessing: '完全に推測',
        aBitUnsure: '少し不安',
        confident: '自信あり',
        submitAnswer: '答えを提出',
        tryAgain: 'もう一度',
        correctWellDone: '正解！よくできました！',
        notQuiteRight: 'あまり正しくありません',
        reviewLocked: '復習がロックされています',
        reviewWillUnlock: 'この復習は{day}日目にアンロックされます',
        startConversation: '会話を開始',
        startNewConversation: '新しい会話を開始',
        answerQuestionFirst: 'まず復習問題に答えてから会話を開始してください。',
        clearChatHistory: 'チャット履歴をクリア？',
        clearChatMessage: '現在の復習のすべてのチャットメッセージがクリアされます。この操作は元に戻せません。',
        clearAndStartNew: 'クリアして新しく開始',
        askQuestionsToDeepen: 'この復習について質問して理解を深めましょう！',
        reviewQuestionForDay: '{day}日目の復習問題',
        dayNumber: '{day}日目',
        reviewDay: '復習{day}',
        
        // Modals
        confirm: '確認',
        delete: '削除',
        overwriteConversation: '会話を上書きしますか？',
        overwriteMessage: '新しいディープダイブを開始すると、この概念の現在の会話が削除されます。続行しますか？',
        eraseAndStartNew: '削除して新しいものを開始',
        continueLearning: '学習を続けますか？',
        knownMessage: 'この概念を知っています！新しいものをリクエストしますか？',
        easyMessage: 'この概念は簡単すぎました。より高度なものをリクエストしますか？',
        requestNewConcept: '新しい概念をリクエスト',
        notNow: '今はしない',
        hideForNow: '非表示',
        skipConversation: '会話をスキップ',
        continueExploring: '探索を続ける',
        conceptTooHard: '概念が難しすぎる',
        hardMessage: 'この概念は挑戦的です。より簡単なものをリクエストするか、今はスキップしますか？',
        requestEasier: 'より簡単なものをリクエスト',
        skipForNow: '今はスキップ',
        letsLearnMore: 'もっと学びましょう！',
        learnMoreMessage: 'この概念についてディープダイブ会話を開始しますか？',
        deleteConfirmTitle: 'トピックを削除',
        deleteConfirmMessage: '"{title}"を削除してもよろしいですか？この操作は元に戻せません。',
        notLookingForTitle: 'お探しのものではありませんか？',
        notLookingForMessage: 'この概念はあなたが学びたいものではありません。異なる概念をリクエストしませんか？',
        requestDifferentConcept: '異なる概念をリクエスト',
        selectKnowledgeLevelTitle: '選択知識レベル',
        selectKnowledgeLevelMessage: '会話を開始する前に、この概念の知識レベルを選択してください。',
        selectLevelToContinue: 'レベルを選択して続ける',
        
        // User Profile
        ageRange: '年齢層',
        selectAgeRange: '年齢層を選択してください',
        educationLevel: '最高学歴',
        selectEducation: '最高学歴を選択してください',
        selectUILanguage: 'お好みのUI言語を選択してください',
        saveProfile: 'プロフィールを保存',
        optional: '任意',
        preferNotToSay: '言いたくない',
        ageRangeHelp: 'これにより、年齢層に合わせてコンテンツを調整できます。希望する場合はスキップできます。',
        educationLevelHelp: 'これにより、説明の複雑さを調整できます。現在勉強中の場合は、現在のレベルを含めてください。',
        
        // Age ranges
        ageRanges: {
            'under20': '20歳未満',
            '20-30': '20-30歳',
            '31-40': '31-40歳',
            '41-50': '41-50歳',
            '51-60': '51-60歳',
            'over60': '60歳以上'
        },
        
        // Education levels
        educationLevels: {
            'high_school': '高校（または在学中）',
            'some_college': '大学（または在学中）',
            'associates': '短期大学（または在学中）',
            'bachelors': '学士号（または在学中）',
            'masters': '修士号（または在学中）',
            'doctorate': '博士号（または在学中）',
            'professional': '専門職学位（または在学中）',
            'other': 'その他'
        },
        
        // Loading
        loadingData: 'データを読み込み中...',
        loadingJourney: '学習の旅を読み込み中...',
        generatingConcept: '最初の概念を生成中...',
        updatingTopic: 'トピックを更新中...',
        deletingTopic: 'トピックを削除中...',
        requestsPending: '件のリクエスト待機中',
        
        // Errors
        errorAddingTopic: 'トピックの追加中にエラーが発生しました：',
        errorUpdatingTopic: 'トピックの更新中にエラーが発生しました：',
        errorDeletingTopic: 'トピックの削除中にエラーが発生しました：',
        errorGeneratingTip: '新しいヒントの生成中にエラーが発生しました：',
        errorDeletingTip: 'ヒントの削除中にエラーが発生しました：',
        failedToUpdateStatus: 'ステータスの更新に失敗しました。',
        failedToStartConversation: '会話の開始に失敗しました',
        failedToSendMessage: 'メッセージの送信に失敗しました：',
        rateLimitError: 'リクエストが多すぎます。しばらくお待ちください。',
        rateLimitExceeded: 'レート制限を超えました。後でもう一度お試しください。',
        retryingRequest: '{seconds} 秒後に再試行します...',
        networkError: '申し訳ございませんが、AIに接続できませんでした。',
        requestTimeout: 'リクエストに時間がかかっています。AIサービスが混雑している可能性があります。再度お試しください。',
        serverError: 'サーバーエラーが発生しました。しばらくしてから再度お試しください。',
        retry: '再試行',
        retrying: '再試行中...',
        previousMessageFailed: '前のメッセージへの応答を取得できませんでした。再試行するか、新しいメッセージを送信してください。',
        
        // Feedback Modal
        feedbackModalTitle: 'フィードバック / バグレポート',
        feedbackDescription: 'あなたのフィードバックは日進月歩の改善に役立ちます。ご意見をお聞かせください！',
        feedbackInstructions: 'バグやフィードバックを効果的に説明する方法：',
        feedbackTip1: '• 何をしようとしていたか具体的に説明してください',
        feedbackTip2: '• 期待した結果と実際に起きたことの違いを説明してください',
        feedbackTip3: '• 表示されたエラーメッセージを含めてください',
        feedbackTip4: '• 問題を再現する手順を記載してください',
        feedbackTip5: '• 改善提案も歓迎します！',
        feedbackPlaceholder: 'フィードバックやバグレポートをここに入力してください...',
        submitFeedback: 'フィードバックを送信',
        submittingFeedback: '送信中...',
        feedbackSuccess: 'フィードバックありがとうございます！すぐに確認いたします。',
        feedbackError: 'フィードバックの送信に失敗しました。もう一度お試しください。',
        pleaseSelectKnowledgeLevel: '会話を開始する前に、この概念の知識レベルを選択してください。',
        
        // Session Takeover Modal
        activeSessionDetected: 'アクティブセッションが検出されました',
        sessionTakeoverWarning: '別のタブまたはウィンドウで既にこのアプリケーションを開いています。複数のセッションを開くとデータ競合の原因となる可能性があります。',
        sessionTakeoverContinueWarning: '続行すると、他のセッションは自動的にログアウトされます。',
        continueAnyway: 'とにかく続行',
        
        // Connection status
        connected: 'サーバーに接続されました',
        connectionLost: '接続が失われました。再接続を試みています...',
        reconnecting: '再接続中... ({attempt}/{maxAttempts})',
        serverUpdating: 'サーバーが更新中です。お待ちください...',
        connectionTimeout: '接続タイムアウト。再接続中...',
        unableToConnect: 'サーバーに接続できません。ページを更新してください。',
        serverUpdateMessage: 'サーバーが更新中です。セッションはまもなく復元されます。',
        
        // Group management
        dropTopicHere: 'ここにトピックをドロップ',
        noTopicsInGroup: 'このグループにトピックはありません',
        failedToCreateGroup: 'グループの作成に失敗しました',
        failedToUpdateGroup: 'グループの更新に失敗しました',
        failedToDeleteGroup: 'グループの削除に失敗しました',
        reorderingWhileFilteringNotSupported: 'フィルタリング中の並べ替えはまだサポートされていません',
        
        // Review and testing
        noTopicsHaveReviewDue: '復習が必要なトピックはありません',
        testMe: 'テストする',
        different: '別の',
        confused: '混乱',
        devGenerateReview: '開発者専用：特定の日付の復習問題を生成',
        
        // Responsive warning modal
        responsiveWarningTitle: 'デスクトップでの最適な表示',
        responsiveWarningMessage: 'このアプリケーションは、コンピューターの最大化されたブラウザウィンドウで使用するように設計されています。スマートフォンやタブレットでは最適に表示されない場合があります。',
        understood: '了解しました',
        dontShowResponsiveWarning: '今後表示しない',
        
        // Rate concept modal
        pleaseRateThisConcept: 'この概念を評価してください',
        beforeStartingDeepDive: 'ディープダイブを始める前に、「{conceptTitle}」についての知識を評価してください。',
        howDoesConceptFeel: 'この概念はどのように感じますか？',
        
        loading: '読み込み中...',
        
        // Error messages
        userNotAuthenticated: 'ユーザーが認証されていません。',
        failedToDeleteTip: 'ヒントの削除に失敗しました',
        failedToDeleteTopic: 'トピックの削除に失敗しました',
        networkResponseNotOk: 'ネットワーク応答が正常ではありません',
        noTopicSelected: 'トピックが選択されていません。',
        authenticationRequired: '要約を生成するには認証が必要です。',
        failedToLoadReviewQuestions: '復習問題の読み込みに失敗しました',
        failedToGenerateAIResponse: 'AI応答の生成に失敗しました',
        tokenCountsInvalid: 'トークン数は数値でなければなりません',
        tokenCountsNegative: 'トークン数は負の値にできません',
        tokenCountsNotFinite: 'トークン数は有限の数値でなければなりません',
        tokenCountsExceedLimit: 'トークン数が妥当な制限を超えています（1億トークン）',
        noJsonInAIResponse: 'AI応答にJSONブロックが見つかりません。',
        geminiApiKeyNotSet: 'GEMINI_API_KEYが設定されていません',
        useTimezoneError: 'useTimezoneはTimezoneProvider内で使用する必要があります',
        
        // Placeholders
        askAboutReviewQuestion: 'この復習問題について質問する...',
        
        // Knowledge levels
        knowledgeLevels: {
            '0': { 
                label: '完全初心者', 
                description: '名前は聞いたことがありますが、それだけです。',
                hobbyDescription: '名前は聞いたことがありますが、それだけです。',
                professionalDescription: '名前は聞いたことがありますが、それだけです。'
            },
            '1': { 
                label: '初心者', 
                description: '定義はできますが、実践的な経験がありません。',
                hobbyDescription: '簡単に説明できますが、実践的な経験がありません。',
                professionalDescription: '定義はできますが、実践的な経験がありません。'
            },
            '2': { 
                label: '初心者', 
                description: 'チュートリアルに従って簡単なタスクを実行できます。',
                hobbyDescription: 'チュートリアルを見ながら基本的な概念を学べます。',
                professionalDescription: 'チュートリアルで基本的な作業ができます。'
            },
            '3': { 
                label: '上級初心者', 
                description: '簡単なタスクを実行できますが、頻繁に調べる必要があります。',
                hobbyDescription: '基本的なことはできますが、よく調べる必要があります。',
                professionalDescription: '基本作業はできますが、よく調べる必要があります。'
            },
            '4': { 
                label: '上級初心者', 
                description: '指導なしで基本的なタスクを確実に実行できます。',
                hobbyDescription: '趣味の範囲でほとんどのことを自分でできます。',
                professionalDescription: '職場で日常的な業務を自立してこなせます。'
            },
            '5': { 
                label: '熟練者', 
                description: '小さな一般的な問題を自分で解決できます。',
                hobbyDescription: 'よくあるトラブルも自分で解決でき、他の趣味仲間をサポートすることもあります。',
                professionalDescription: '職場で一般的な問題を自力で解決でき、専門性が高まっています。'
            },
            '6': { 
                label: '熟練者', 
                description: 'ほとんどのタスクを独立して効率的に実行できます。',
                hobbyDescription: '複雑な活動も効率よくこなせ、時々他の人にアドバイスもします。',
                professionalDescription: '複雑な業務も効率的にこなせ、同僚の指導やリーダーも担えます。'
            },
            '7': { 
                label: '専門家', 
                description: '複雑または不慣れな問題を効果的に処理できます。',
                hobbyDescription: '難しい概念にも挑戦し、新しい工夫や発見をすることができます。',
                professionalDescription: '職場で複雑・新規の課題にも対応し、チームの成長をリードできます。'
            },
            '8': { 
                label: '専門家', 
                description: '深い理解があり、他の人に教えることができます。',
                hobbyDescription: '趣味コミュニティで影響力があり、知識や経験を共有することができます。',
                professionalDescription: '深い専門知識を持ち、同僚の育成や組織への専門的な助言ができます。'
            },
            '9': { 
                label: 'エキスパート', 
                description: 'この分野の認められた権威または先駆者です。',
                hobbyDescription: 'この趣味分野のリーダーとして、コミュニティの発展に貢献しています。',
                professionalDescription: '業界のリーダーやイノベーターとして、標準や発展を牽引しています。'
            },
            '10': { 
                label: 'シニア教授レベル', 
                description: '世界で認められたエキスパートで、この分野に重要な貢献をしています。',
                hobbyDescription: '世界で認められたエキスパートで、この趣味分野に重要な貢献をしています。',
                professionalDescription: '世界で認められたエキスパートで、この業界に重要な貢献をしています。'
            }
        }
    }
};

export const getTranslation = (key, language = 'en', params = {}) => {
    // Normalize language codes
    let normalizedLanguage = language;
    if (language === 'zh' || language === 'zh-TW' || language === 'zh-HK') {
        normalizedLanguage = 'zh-CN';
    }
    
    const lang = translations[normalizedLanguage] || translations['en'];
    let text = lang[key] || translations['en'][key] || key;
    
    // If the value is an object (like educationLevels or ageRanges), return it as is
    if (typeof text === 'object' && text !== null) {
        return text;
    }
    
    // Ensure text is a string for non-object values
    if (typeof text !== 'string') {
        console.warn(`Translation key "${key}" returned unexpected type:`, typeof text);
        return String(text);
    }
    
    // Handle ICU MessageFormat plural rules
    const pluralRegex = /{(\w+),\s*plural,\s*one\s*{([^}]+)}\s*other\s*{([^}]+)}}/g;
    text = text.replace(pluralRegex, (match, param, singular, plural) => {
        const count = params[param];
        return count === 1 ? singular : plural;
    });
    
    // Replace parameters in the text
    Object.keys(params).forEach(param => {
        const regex = new RegExp(`{${param}}`, 'g');
        const replacement = params[param];
        text = text.replace(regex, replacement);
    });
    
    return text;
};

export const getRandomTopicExample = (language = 'en') => {
    const lang = translations[language] || translations['en'];
    const examples = lang.topicExamples || translations['en'].topicExamples;
    
    if (!examples || examples.length === 0) {
        return {
            name: 'Example Topic',
            description: 'Describe your current level, what you\'ve already learned, specific goals, and any context that would help generate more relevant concepts.'
        };
    }
    
    const randomIndex = Math.floor(Math.random() * examples.length);
    return examples[randomIndex];
};

export const getKnowledgeLevels = (language = 'en') => {
    // Normalize language codes
    let normalizedLanguage = language;
    if (language === 'zh' || language === 'zh-TW' || language === 'zh-HK') {
        normalizedLanguage = 'zh-CN';
    }
    
    const lang = translations[normalizedLanguage] || translations['en'];
    return lang.knowledgeLevels || translations['en'].knowledgeLevels;
};

export const t = getTranslation; 